<!-- app-subdomain-creators/src/Components/PageRenderer.vue -->

<template>
  <!-- Empty state -->
  <div
    v-if="!props.doc?.sections?.length"
    class="rounded-xl border border-dashed p-8 bg-white text-center"
  >
    <div class="text-sm text-zinc-500 mb-3">
      This page has no content yet.
    </div>
    <button
      type="button"
      class="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#2D8CFF] text-white hover:bg-[#1b6ad1]"
      @click="openAddEndPicker($event)"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="w-5 h-5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M12 4v16m8-8H4"
        />
      </svg>
      Add first Section
    </button>
  </div>

  <!-- Non-empty: sections list -->
  <div
    v-else
    class="space-y-0 [overflow:clip] [overflow-clip-margin:128px]"
    @click="onCanvasBackgroundClick"
    @dragenter.prevent="onCanvasDragEnter"
    @dragover.prevent="onCanvasDragOver"
    @dragleave="onCanvasDragLeave"
    @drop.prevent="onCanvasDrop"
  >
    <section
      v-for="({ block, index: i }) in getVisibleSectionEntries()"
      :key="i"
      class="lesson-content-block group/item relative transition-colors duration-150"
      :class="[
        String(block?.type || '') === 'section'
          ? 'rounded-none p-0'
          : 'rounded-xl p-1 sm:p-2',
        getBlockLayerClass(i, block),
      ]"
      :style="String(block?.type || '') === 'section' ? getSectionBlockStyle(block) : null"
      :data-page-block-index="i"
      @dragover.prevent="onSectionDragOver(i, $event)"
      @drop.prevent="onSectionDrop(i, $event)"
      @mouseenter="onSectionHoverEnter(i)"
      @mouseleave="onSectionHoverLeave(i)"
      @click.stop="$emit('select', i)"
    >
      <div
        v-if="shouldShowDropIndicator(i)"
        class="absolute -top-[7px] left-0 right-0 h-[3px] rounded-full bg-[#ff009dce]"
      />
      <!-- selection ring -->
      <div
        class="absolute inset-0 z-20 ring-2 transition pointer-events-none"
        :class="[
          block.type === 'section' ? 'rounded-none' : 'rounded-xl',
          block.type === 'section'
            ? `z-40 ${getSectionRingClass(i)}`
            : (props.selectedIndex === i ? 'ring-[#ff009dce]' : 'ring-transparent group-hover/item:ring-[#ff009d85]')
        ]"
        aria-hidden="true"
      />

      <div
        class="absolute z-[120] flex items-center gap-1 transition-opacity duration-150"
        :class="[getBlockToolbarClasses(block), getBlockChromeVisibilityClasses(i, block)]"
      >
        <div class="relative group/element-action">
          <button
            type="button"
            class="inline-flex h-8 w-8 items-center justify-center rounded-md text-white shadow-md"
            :class="getBlockActionButtonClasses(block)"
            @click.stop="openBlockSettingsMenu(i, block, $event)"
          >
            <Cog6ToothIcon class="w-4 h-4" />
          </button>
          <div
            class="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 bg-black/60 text-white text-xs px-3 py-1 rounded-lg shadow-xl whitespace-nowrap opacity-0 group-hover/element-action:opacity-100 transition-opacity duration-200 delay-150 pointer-events-none"
          >
            {{ getBlockSettingsButtonLabel(block) }}
          </div>
        </div>
        <div class="relative group/element-action">
          <button
            type="button"
            class="inline-flex h-8 w-8 items-center justify-center rounded-md text-white shadow-md"
            :class="getBlockActionButtonClasses(block)"
            @click.stop="$emit('duplicate', i)"
          >
            <DocumentDuplicateIcon class="w-3.5 h-3.5" />
          </button>
          <div
            class="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 bg-black/60 text-white text-xs px-3 py-1 rounded-lg shadow-xl whitespace-nowrap opacity-0 group-hover/element-action:opacity-100 transition-opacity duration-200 delay-150 pointer-events-none"
          >
            Duplicate block
          </div>
        </div>
        <div class="relative group/element-action">
          <button
            type="button"
            class="inline-flex h-8 w-8 items-center justify-center rounded-md text-white shadow-md cursor-grab active:cursor-grabbing"
            :class="getBlockActionButtonClasses(block)"
            draggable="true"
            @mousedown.stop
            @dragstart="onBlockDragStart(i, $event)"
            @dragend="onBlockDragEnd"
          >
            <Bars3Icon class="w-3.5 h-3.5" />
          </button>
          <div
            class="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 bg-black/60 text-white text-xs px-3 py-1 rounded-lg shadow-xl whitespace-nowrap opacity-0 group-hover/element-action:opacity-100 transition-opacity duration-200 delay-150 pointer-events-none"
          >
            Move block
          </div>
        </div>
        <div class="relative group/element-action">
          <button
            type="button"
            class="inline-flex h-8 w-8 items-center justify-center rounded-md text-white shadow-md"
            :class="getBlockActionButtonClasses(block)"
            @click.stop="$emit('delete', i)"
          >
            <TrashIcon class="w-4 h-4" />
          </button>
          <div
            class="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 bg-black/60 text-white text-xs px-3 py-1 rounded-lg shadow-xl whitespace-nowrap opacity-0 group-hover/element-action:opacity-100 transition-opacity duration-200 delay-150 pointer-events-none"
          >
            Delete block
          </div>
        </div>
      </div>

      <div
        v-if="block.type === 'section'"
        class="absolute left-0 top-0 z-50 rounded-br-md bg-[#14b8a6] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white transition-opacity duration-150 pointer-events-none"
        :class="getBlockChromeVisibilityClasses(i, block)"
      >
        Section
      </div>
      <div
        v-else
        class="absolute bottom-full left-0 z-30 rounded-br-md bg-[#ff009dce] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white transition-opacity duration-150 pointer-events-none"
        :class="props.selectedIndex === i ? 'opacity-100' : 'opacity-0 group-hover/item:opacity-100'"
      >
        {{ getBlockBadgeLabel(block) }}
      </div>

      <!-- block render -->
      <div
        v-if="block.type === 'hero'"
        class="rounded-md bg-zinc-50 px-8 py-16"
      >
        <h1 class="text-3xl font-bold mb-2">
          {{ block.props?.headline || 'Headline' }}
        </h1>
        <p class="text-zinc-600">
          {{ block.props?.sub || 'Sub-headline' }}
        </p>
      </div>

      <div
        v-else-if="block.type === 'features'"
        class="rounded-md bg-white p-8 "
      >
        <h2 class="text-xl font-semibold mb-4">
          Features
        </h2>
        <ul class="grid sm:grid-cols-2 gap-3">
          <li
            v-for="(it, j) in (block.props?.items || [])"
            :key="j"
            class="rounded-lg p-3"
          >
            <div class="font-medium">
              {{ it.title || 'Feature' }}
            </div>
          </li>
        </ul>
      </div>

      <div
        v-else-if="block.type === 'section'"
        class="w-full h-full bg-transparent"
      >
        <div
          v-if="!getSectionRows(block).length"
          class="w-full h-full flex items-center justify-center"
        >
          <div
            v-if="String(block?.props?.sectionPlaceholderText || '').trim()"
            class="px-4 text-sm text-zinc-500 text-center select-none"
          >
            {{ String(block?.props?.sectionPlaceholderText || '').trim() }}
          </div>
        </div>
        <div
          v-else
          class="w-full h-full space-y-0"
        >
          <div
            v-for="({ row, rowIndex }) in getVisibleRowEntries(block)"
            :key="`row-${i}-${rowIndex}`"
            class="group/row relative transition-colors duration-150"
            :class="getRowLayerClass(i, rowIndex)"
            :style="getRowBlockStyle(row)"
            @click.stop="onSelectRow(i, rowIndex)"
            @mouseenter="onRowHoverEnter(i, rowIndex)"
            @mouseleave="onRowHoverLeave(i, rowIndex)"
            @dragover.stop.prevent="onRowDragOver(i, rowIndex, $event)"
            @drop.stop.prevent="onRowDrop(i, rowIndex, $event)"
          >
            <div
              v-if="shouldShowRowDropIndicator(i, rowIndex)"
              class="absolute -top-[7px] left-0 right-0 h-[3px] rounded-full bg-[#2D8CFF]"
            />
            <div
              class="absolute inset-0 z-20 ring-2 pointer-events-none"
              :class="getRowRingClass(i, rowIndex)"
            />
            <div
              class="absolute bottom-full left-0 z-30 rounded-br-md bg-[#2D8CFF] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white transition-opacity duration-150 pointer-events-none"
              :class="getRowChromeVisibilityClasses(i, rowIndex)"
            >
              {{ getRowBadgeLabel(row) }}
            </div>
            <div
              class="absolute z-[120] flex items-center gap-1 transition-opacity duration-150"
              :class="getRowToolbarClasses(i, rowIndex)"
            >
              <div class="relative group/element-action">
                <button
                  type="button"
                  class="inline-flex h-8 w-8 items-center justify-center rounded-md text-white shadow-md bg-[#2D8CFF] hover:bg-[#1D4ED8]"
                  @click.stop="openRowSettingsMenu(i, rowIndex, $event)"
                >
                  <Cog6ToothIcon class="w-4 h-4" />
                </button>
                <div class="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 bg-black/60 text-white text-xs px-3 py-1 rounded-lg shadow-xl whitespace-nowrap opacity-0 group-hover/element-action:opacity-100 transition-opacity duration-200 delay-150 pointer-events-none">
                  Row settings
                </div>
              </div>
              <div class="relative group/element-action">
                <button
                  type="button"
                  class="inline-flex h-8 w-8 items-center justify-center rounded-md text-white shadow-md bg-[#2D8CFF] hover:bg-[#1D4ED8]"
                  @click.stop="$emit('duplicate-row', { sectionIndex: i, rowIndex })"
                >
                  <DocumentDuplicateIcon class="w-3.5 h-3.5" />
                </button>
                <div class="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 bg-black/60 text-white text-xs px-3 py-1 rounded-lg shadow-xl whitespace-nowrap opacity-0 group-hover/element-action:opacity-100 transition-opacity duration-200 delay-150 pointer-events-none">
                  Duplicate row
                </div>
              </div>
              <div class="relative group/element-action">
                <button
                  type="button"
                  class="inline-flex h-8 w-8 items-center justify-center rounded-md text-white shadow-md bg-[#2D8CFF] hover:bg-[#1D4ED8] cursor-grab active:cursor-grabbing"
                  draggable="true"
                  @mousedown.stop
                  @dragstart="onRowDragStart(i, rowIndex, $event)"
                  @dragend="onRowDragEnd"
                >
                  <Bars3Icon class="w-3.5 h-3.5" />
                </button>
                <div class="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 bg-black/60 text-white text-xs px-3 py-1 rounded-lg shadow-xl whitespace-nowrap opacity-0 group-hover/element-action:opacity-100 transition-opacity duration-200 delay-150 pointer-events-none">
                  Move row
                </div>
              </div>
              <div class="relative group/element-action">
                <button
                  type="button"
                  class="inline-flex h-8 w-8 items-center justify-center rounded-md text-white shadow-md bg-[#2D8CFF] hover:bg-[#1D4ED8]"
                  @click.stop="$emit('delete-row', { sectionIndex: i, rowIndex })"
                >
                  <TrashIcon class="w-4 h-4" />
                </button>
                <div class="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 bg-black/60 text-white text-xs px-3 py-1 rounded-lg shadow-xl whitespace-nowrap opacity-0 group-hover/element-action:opacity-100 transition-opacity duration-200 delay-150 pointer-events-none">
                  Delete row
                </div>
              </div>
            </div>

            <div
              class="grid min-h-full"
              :style="getRowColumnsGridStyle(row)"
            >
              <div
                v-for="c in getRowColumnCount(row)"
                :key="`row-col-${i}-${rowIndex}-${c}`"
                class="group/row-column relative space-y-2 transition-colors duration-150"
                :class="[hasRowElementsInColumn(row, c - 1) ? 'min-h-0' : 'min-h-[64px]', getRowColumnClasses(i, rowIndex, c - 1), getRowColumnLayerClass(i, rowIndex, c - 1)]"
                :style="getRowColumnStyle(row, c - 1)"
                @click.stop="onSelectRowColumn(i, rowIndex, c - 1)"
                @mouseenter="onRowColumnHoverEnter(i, rowIndex, c - 1)"
                @mouseleave="onRowColumnHoverLeave(i, rowIndex, c - 1)"
                @dragover.stop.prevent="onRowColumnDragOver(i, rowIndex, row, c - 1, $event)"
                @drop.stop.prevent="onRowElementDrop(i, rowIndex, getRowElements(row).length, c - 1, $event)"
              >
                <div
                  class="absolute inset-0 z-20 ring-2 pointer-events-none"
                  :class="getRowColumnRingClass(i, rowIndex, c - 1)"
                />
                <div
                  class="absolute bottom-full left-0 z-30 rounded-br-md bg-[#2D8CFF] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white transition-opacity duration-150 pointer-events-none"
                  :class="getRowColumnChromeVisibilityClasses(i, rowIndex, c - 1)"
                >
                  Column {{ c }}
                </div>
                <div
                  class="absolute bottom-full right-0 z-[120] flex items-center gap-1 transition-opacity duration-150"
                  :class="getRowColumnChromeVisibilityClasses(i, rowIndex, c - 1)"
                >
                  <div class="relative group/element-action">
                    <button
                      type="button"
                      class="inline-flex h-8 w-8 items-center justify-center rounded-md bg-[#2D8CFF] text-white shadow-md hover:bg-[#1D4ED8]"
                      @click.stop="onSelectRowColumn(i, rowIndex, c - 1)"
                    >
                      <Cog6ToothIcon class="h-4 w-4" />
                    </button>
                    <div class="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 bg-black/60 text-white text-xs px-3 py-1 rounded-lg shadow-xl whitespace-nowrap opacity-0 group-hover/element-action:opacity-100 transition-opacity duration-200 delay-150 pointer-events-none">
                      Column settings
                    </div>
                  </div>
                  <div v-if="c > 1" class="relative group/element-action">
                    <button
                      type="button"
                      class="inline-flex h-8 w-8 items-center justify-center rounded-md bg-[#2D8CFF] text-white shadow-md hover:bg-[#1D4ED8]"
                      aria-label="Move column left"
                      @click.stop="$emit('move-row-column', { sectionIndex: i, rowIndex, columnIndex: c - 1, direction: -1 })"
                    >
                      <ChevronLeftIcon class="h-4 w-4" />
                    </button>
                    <div class="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 bg-black/60 text-white text-xs px-3 py-1 rounded-lg shadow-xl whitespace-nowrap opacity-0 group-hover/element-action:opacity-100 transition-opacity duration-200 delay-150 pointer-events-none">Move left</div>
                  </div>
                  <div v-if="c < getRowColumnCount(row)" class="relative group/element-action">
                    <button
                      type="button"
                      class="inline-flex h-8 w-8 items-center justify-center rounded-md bg-[#2D8CFF] text-white shadow-md hover:bg-[#1D4ED8]"
                      aria-label="Move column right"
                      @click.stop="$emit('move-row-column', { sectionIndex: i, rowIndex, columnIndex: c - 1, direction: 1 })"
                    >
                      <ChevronRightIcon class="h-4 w-4" />
                    </button>
                    <div class="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 bg-black/60 text-white text-xs px-3 py-1 rounded-lg shadow-xl whitespace-nowrap opacity-0 group-hover/element-action:opacity-100 transition-opacity duration-200 delay-150 pointer-events-none">Move right</div>
                  </div>
                  <div v-if="canDeleteRowColumn(row, c - 1)" class="relative group/element-action">
                    <button
                      type="button"
                      class="inline-flex h-8 w-8 items-center justify-center rounded-md bg-[#2D8CFF] text-white shadow-md hover:bg-red-600"
                      aria-label="Delete column"
                      @click.stop="$emit('delete-row-column', { sectionIndex: i, rowIndex, columnIndex: c - 1 })"
                    >
                      <TrashIcon class="h-4 w-4" />
                    </button>
                    <div class="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 bg-black/60 text-white text-xs px-3 py-1 rounded-lg shadow-xl whitespace-nowrap opacity-0 group-hover/element-action:opacity-100 transition-opacity duration-200 delay-150 pointer-events-none">Delete column</div>
                  </div>
                </div>
                <div
                  v-for="entry in getRowElementEntriesForColumn(row, c - 1)"
                  :key="`row-element-${i}-${rowIndex}-${entry.elementIndex}`"
                  class="group/row-element relative transition-colors duration-150"
                  :class="getRowElementLayerClass(i, rowIndex, entry.elementIndex)"
                  :style="getRowElementBlockStyle(entry.element)"
                  @click.stop="onSelectRowElement(i, rowIndex, entry.elementIndex)"
                  @mouseenter="onRowElementHoverEnter(i, rowIndex, entry.elementIndex)"
                  @mousemove.stop="onRowElementHoverEnter(i, rowIndex, entry.elementIndex)"
                  @mouseleave="onRowElementHoverLeave(i, rowIndex, entry.elementIndex)"
                  @dragover.stop.prevent="onRowElementDragOver(i, rowIndex, entry.elementIndex, c - 1, $event)"
                  @drop.stop.prevent="onRowElementDrop(i, rowIndex, entry.elementIndex, c - 1, $event)"
                >
                  <div
                    v-if="shouldShowRowElementDropIndicator(i, rowIndex, entry.elementIndex)"
                    class="absolute -top-[7px] left-0 right-0 h-[3px] rounded-full"
                    :class="getRowElementDropIndicatorClass(entry.element)"
                  />
                  <div
                    class="absolute inset-0 z-20 ring-2 ring-inset pointer-events-none"
                    :class="getRowElementRingClass(i, rowIndex, entry.elementIndex, entry.element)"
                  />
                  <div
                    class="absolute left-0 top-0 z-30 rounded-br-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white transition-opacity duration-150 pointer-events-none"
                    :class="[
                      getRowElementBadgeToneClass(entry.element),
                      getRowElementChromeVisibilityClasses(i, rowIndex, entry.elementIndex),
                    ]"
                  >
                    {{ getRowElementBadgeLabel(entry.element) }}
                  </div>
                  <div
                    class="absolute z-[120] flex items-center gap-1 transition-opacity duration-150"
                    :class="getRowElementToolbarClasses(i, rowIndex, entry.elementIndex)"
                  >
                    <div class="relative group/element-action">
                      <button
                        type="button"
                        :class="getRowElementToolbarButtonClass(entry.element)"
                        @click.stop="openRowElementSettingsMenu(i, rowIndex, entry.elementIndex, entry.element, $event)"
                      >
                        <Cog6ToothIcon class="w-4 h-4" />
                      </button>
                    </div>
                    <div
                      v-if="!isSystemManagedElement(entry.element)"
                      class="relative group/element-action"
                    >
                      <button
                        type="button"
                        :class="getRowElementToolbarButtonClass(entry.element)"
                        @click.stop="$emit('duplicate-row-element', { sectionIndex: i, rowIndex, elementIndex: entry.elementIndex })"
                      >
                        <DocumentDuplicateIcon class="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div class="relative group/element-action">
                      <button
                        type="button"
                        :class="`${getRowElementToolbarButtonClass(entry.element)} cursor-grab active:cursor-grabbing`"
                        draggable="true"
                        @mousedown.stop
                        @dragstart="onRowElementDragStart(i, rowIndex, entry.elementIndex, $event)"
                        @dragend="onRowElementDragEnd"
                      >
                        <Bars3Icon class="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div
                      v-if="!isSystemManagedElement(entry.element)"
                      class="relative group/element-action"
                    >
                      <button
                        type="button"
                        :class="getRowElementToolbarButtonClass(entry.element)"
                        @click.stop="$emit('delete-row-element', { sectionIndex: i, rowIndex, elementIndex: entry.elementIndex })"
                      >
                        <TrashIcon class="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div
                    v-if="entry.element.type === 'row'"
                    class="w-full h-full"
                  >
                    <div
                      class="grid min-h-full"
                      :style="getRowColumnsGridStyle(entry.element)"
                    >
                      <div
                        v-for="nestedColumn in getRowColumnCount(entry.element)"
                        :key="`nested-col-${i}-${rowIndex}-${entry.elementIndex}-${nestedColumn}`"
                        class="group/row-column relative space-y-2 transition-colors duration-150"
                        :class="[hasRowElementsInColumn(entry.element, nestedColumn - 1) ? 'min-h-0' : 'min-h-[64px]', getNestedRowColumnClasses(i, rowIndex, entry.elementIndex, nestedColumn - 1), getRowColumnLayerClass(i, rowIndex, nestedColumn - 1, entry.elementIndex)]"
                        :style="getRowColumnStyle(entry.element, nestedColumn - 1)"
                        @click.stop="onSelectRowColumn(i, rowIndex, nestedColumn - 1, entry.elementIndex)"
                        @mouseenter="onRowColumnHoverEnter(i, rowIndex, nestedColumn - 1, entry.elementIndex)"
                        @mouseleave="onRowColumnHoverLeave(i, rowIndex, nestedColumn - 1, entry.elementIndex)"
                        @dragover.stop.prevent="onNestedRowColumnDragOver(i, rowIndex, entry.elementIndex, entry.element, nestedColumn - 1, $event)"
                        @drop.stop.prevent="onNestedRowElementDrop(i, rowIndex, entry.elementIndex, entry.element, getRowElements(entry.element).length, nestedColumn - 1, $event)"
                      >
                        <div
                          class="absolute inset-0 z-20 ring-2 pointer-events-none"
                          :class="getNestedRowColumnRingClass(i, rowIndex, entry.elementIndex, nestedColumn - 1)"
                        />
                        <div
                          class="absolute bottom-full left-0 z-30 rounded-br-md bg-[#2D8CFF] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white transition-opacity duration-150 pointer-events-none"
                          :class="getRowColumnChromeVisibilityClasses(i, rowIndex, nestedColumn - 1, entry.elementIndex)"
                        >
                          Column {{ nestedColumn }}
                        </div>
                        <div
                          class="absolute bottom-full right-0 z-[120] flex items-center gap-1 transition-opacity duration-150"
                          :class="getRowColumnChromeVisibilityClasses(i, rowIndex, nestedColumn - 1, entry.elementIndex)"
                        >
                          <div class="relative group/element-action">
                            <button
                              type="button"
                              class="inline-flex h-8 w-8 items-center justify-center rounded-md bg-[#2D8CFF] text-white shadow-md hover:bg-[#1D4ED8]"
                              @click.stop="onSelectRowColumn(i, rowIndex, nestedColumn - 1, entry.elementIndex)"
                            >
                              <Cog6ToothIcon class="h-4 w-4" />
                            </button>
                            <div class="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 bg-black/60 text-white text-xs px-3 py-1 rounded-lg shadow-xl whitespace-nowrap opacity-0 group-hover/element-action:opacity-100 transition-opacity duration-200 delay-150 pointer-events-none">
                              Column settings
                            </div>
                          </div>
                          <div v-if="nestedColumn > 1" class="relative group/element-action">
                            <button
                              type="button"
                              class="inline-flex h-8 w-8 items-center justify-center rounded-md bg-[#2D8CFF] text-white shadow-md hover:bg-[#1D4ED8]"
                              aria-label="Move column left"
                              @click.stop="$emit('move-row-column', { sectionIndex: i, rowIndex, columnIndex: nestedColumn - 1, direction: -1, nestedRowElementIndex: entry.elementIndex })"
                            >
                              <ChevronLeftIcon class="h-4 w-4" />
                            </button>
                            <div class="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 bg-black/60 text-white text-xs px-3 py-1 rounded-lg shadow-xl whitespace-nowrap opacity-0 group-hover/element-action:opacity-100 transition-opacity duration-200 delay-150 pointer-events-none">Move left</div>
                          </div>
                          <div v-if="nestedColumn < getRowColumnCount(entry.element)" class="relative group/element-action">
                            <button
                              type="button"
                              class="inline-flex h-8 w-8 items-center justify-center rounded-md bg-[#2D8CFF] text-white shadow-md hover:bg-[#1D4ED8]"
                              aria-label="Move column right"
                              @click.stop="$emit('move-row-column', { sectionIndex: i, rowIndex, columnIndex: nestedColumn - 1, direction: 1, nestedRowElementIndex: entry.elementIndex })"
                            >
                              <ChevronRightIcon class="h-4 w-4" />
                            </button>
                            <div class="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 bg-black/60 text-white text-xs px-3 py-1 rounded-lg shadow-xl whitespace-nowrap opacity-0 group-hover/element-action:opacity-100 transition-opacity duration-200 delay-150 pointer-events-none">Move right</div>
                          </div>
                          <div v-if="canDeleteRowColumn(entry.element, nestedColumn - 1)" class="relative group/element-action">
                            <button
                              type="button"
                              class="inline-flex h-8 w-8 items-center justify-center rounded-md bg-[#2D8CFF] text-white shadow-md hover:bg-red-600"
                              aria-label="Delete column"
                              @click.stop="$emit('delete-row-column', { sectionIndex: i, rowIndex, columnIndex: nestedColumn - 1, nestedRowElementIndex: entry.elementIndex })"
                            >
                              <TrashIcon class="h-4 w-4" />
                            </button>
                            <div class="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 bg-black/60 text-white text-xs px-3 py-1 rounded-lg shadow-xl whitespace-nowrap opacity-0 group-hover/element-action:opacity-100 transition-opacity duration-200 delay-150 pointer-events-none">Delete column</div>
                          </div>
                        </div>
                        <div
                          v-for="nestedEntry in getRowElementEntriesForColumn(entry.element, nestedColumn - 1)"
                          :key="`nested-row-element-${i}-${rowIndex}-${entry.elementIndex}-${nestedEntry.elementIndex}`"
                          class="group/row-element relative transition-colors duration-150"
                          :class="getRowElementLayerClass(i, rowIndex, nestedEntry.elementIndex, entry.elementIndex)"
                          :style="getRowElementBlockStyle(nestedEntry.element)"
                          @click.stop="onSelectRowElement(i, rowIndex, nestedEntry.elementIndex, entry.elementIndex)"
                          @mouseenter="onRowElementHoverEnter(i, rowIndex, nestedEntry.elementIndex, entry.elementIndex)"
                          @mousemove.stop="onRowElementHoverEnter(i, rowIndex, nestedEntry.elementIndex, entry.elementIndex)"
                          @mouseleave="onRowElementHoverLeave(i, rowIndex, nestedEntry.elementIndex, entry.elementIndex)"
                          @dragover.stop.prevent="onNestedRowElementDragOver(i, rowIndex, entry.elementIndex, nestedEntry.elementIndex, nestedColumn - 1, $event)"
                          @drop.stop.prevent="onNestedRowElementDrop(i, rowIndex, entry.elementIndex, entry.element, nestedEntry.elementIndex, nestedColumn - 1, $event)"
                        >
                          <div
                            class="absolute inset-0 z-20 ring-2 ring-inset pointer-events-none"
                            :class="getNestedRowElementRingClasses(i, rowIndex, entry.elementIndex, nestedEntry.elementIndex, nestedEntry.element)"
                          />
                          <div
                            v-if="shouldShowNestedRowElementDropIndicator(i, rowIndex, entry.elementIndex, nestedEntry.elementIndex)"
                            class="absolute -top-[7px] left-0 right-0 h-[3px] rounded-full bg-[#2D8CFF]"
                          />
                          <div
                            class="absolute left-0 top-0 z-30 rounded-br-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white transition-opacity duration-150 pointer-events-none"
                            :class="[
                              getRowElementBadgeToneClass(nestedEntry.element),
                              getRowElementChromeVisibilityClasses(i, rowIndex, nestedEntry.elementIndex, entry.elementIndex),
                            ]"
                          >
                            {{ getRowElementBadgeLabel(nestedEntry.element) }}
                          </div>
                          <div
                            class="absolute bottom-full right-0 z-40 flex items-center gap-1 transition-opacity duration-150"
                            :class="getRowElementChromeVisibilityClasses(i, rowIndex, nestedEntry.elementIndex, entry.elementIndex)"
                          >
                            <button
                              type="button"
                              :class="getRowElementToolbarButtonClass(nestedEntry.element)"
                              @click.stop="openRowElementSettingsMenu(i, rowIndex, nestedEntry.elementIndex, nestedEntry.element, $event, entry.elementIndex)"
                            >
                              <Cog6ToothIcon class="h-4 w-4" />
                            </button>
                            <button
                              v-if="!isSystemManagedElement(nestedEntry.element)"
                              type="button"
                              :class="getRowElementToolbarButtonClass(nestedEntry.element)"
                              @click.stop="$emit('duplicate-row-element', { sectionIndex: i, rowIndex, elementIndex: nestedEntry.elementIndex, nestedRowElementIndex: entry.elementIndex })"
                            >
                              <DocumentDuplicateIcon class="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              :class="`${getRowElementToolbarButtonClass(nestedEntry.element)} cursor-grab active:cursor-grabbing`"
                              draggable="true"
                              @mousedown.stop
                              @dragstart="onRowElementDragStart(i, rowIndex, nestedEntry.elementIndex, $event, entry.elementIndex)"
                              @dragend="onRowElementDragEnd"
                            >
                              <Bars3Icon class="h-3.5 w-3.5" />
                            </button>
                            <button
                              v-if="!isSystemManagedElement(nestedEntry.element)"
                              type="button"
                              :class="getRowElementToolbarButtonClass(nestedEntry.element)"
                              @click.stop="$emit('delete-row-element', { sectionIndex: i, rowIndex, elementIndex: nestedEntry.elementIndex, nestedRowElementIndex: entry.elementIndex })"
                            >
                              <TrashIcon class="h-4 w-4" />
                            </button>
                          </div>
                          <div
                            v-if="nestedEntry.element.type === 'row'"
                            class="w-full h-full p-1"
                          >
                            <div
                              class="grid gap-2"
                              :style="getRowColumnsGridStyle(nestedEntry.element)"
                            >
                              <div
                                v-for="nestedLevelColumn in getRowColumnCount(nestedEntry.element)"
                                :key="`nested-level-col-${i}-${rowIndex}-${entry.elementIndex}-${nestedEntry.elementIndex}-${nestedLevelColumn}`"
                                class="min-h-[52px] border border-[#2D8CFF]/45 bg-[#2D8CFF]/[0.03]"
                              />
                            </div>
                          </div>
                          <template v-else-if="nestedEntry.element.type === 'text'">
                            <p
                              v-if="!isRowElementTextEditing(i, rowIndex, nestedEntry.elementIndex, entry.elementIndex)"
                              class="m-0 w-full px-0 py-0 text-sm leading-relaxed whitespace-pre-wrap break-words [overflow-wrap:anywhere] cursor-text"
                              :style="getTextBlockStyle(nestedEntry.element)"
                              @click.stop="startRowElementTextEdit(i, rowIndex, nestedEntry.elementIndex, entry.elementIndex)"
                            >
                              {{ nestedEntry.element.props?.content || 'New text' }}
                            </p>
                            <textarea
                              v-else
                              :ref="(el) => setRowElementTextEditorRef(i, rowIndex, nestedEntry.elementIndex, el, entry.elementIndex)"
                              :value="getRowElementTextDraft(i, rowIndex, nestedEntry.elementIndex, nestedEntry.element, entry.elementIndex)"
                              rows="1"
                              class="m-0 block w-full px-0 py-0 text-sm leading-relaxed whitespace-pre-wrap border-0 focus:border-0 outline-none focus:outline-none focus:ring-0 resize-none bg-transparent shadow-none appearance-none break-words [overflow-wrap:anywhere] overflow-x-hidden"
                              :style="getTextBlockStyle(nestedEntry.element)"
                              placeholder="Write your text..."
                              @input="onRowElementTextDraftInput(i, rowIndex, nestedEntry.elementIndex, $event, entry.elementIndex)"
                              @focus="onSelectRowElement(i, rowIndex, nestedEntry.elementIndex, entry.elementIndex)"
                              @blur="commitRowElementTextEdit(i, rowIndex, nestedEntry.elementIndex, entry.elementIndex)"
                              @keydown.enter.exact.prevent="commitRowElementTextEdit(i, rowIndex, nestedEntry.elementIndex, entry.elementIndex)"
                              @keydown.ctrl.enter.prevent="commitRowElementTextEdit(i, rowIndex, nestedEntry.elementIndex, entry.elementIndex)"
                              @keydown.meta.enter.prevent="commitRowElementTextEdit(i, rowIndex, nestedEntry.elementIndex, entry.elementIndex)"
                              @keydown.esc.prevent="cancelRowElementTextEdit(i, rowIndex, nestedEntry.elementIndex, entry.elementIndex)"
                            />
                          </template>
                          <div
                            v-else-if="nestedEntry.element.type === 'header'"
                            class="p-2"
                          >
                            <component
                              :is="nestedEntry.element.props?.level || 'h2'"
                              class="font-bold tracking-tight"
                              :style="{
                                fontSize: ((nestedEntry.element.props && Number(nestedEntry.element.props.size)) || 24) + 'px',
                                lineHeight: '1.15',
                                whiteSpace: 'pre-wrap',
                                overflowWrap: 'anywhere',
                                wordBreak: 'break-word',
                                ...getHeaderBlockTextStyle(nestedEntry.element),
                              }"
                              v-text="nestedEntry.element.props?.text || 'Section heading'"
                            />
                          </div>
                          <div
                            v-else-if="nestedEntry.element.type === 'image'"
                            class="p-0"
                          >
                            <div
                              class="w-full"
                              :class="getImageBlockAlignClass(nestedEntry.element)"
                            >
                              <a
                                v-if="getImageBlockHref(nestedEntry.element)"
                                :href="getImageBlockHref(nestedEntry.element)"
                                :target="getImageBlockTarget(nestedEntry.element)"
                                :rel="getImageBlockRel(nestedEntry.element)"
                                class="block max-w-full"
                                :style="getImageBlockLinkStyle(nestedEntry.element)"
                                @click.stop.prevent="onSelectRowElement(i, rowIndex, nestedEntry.elementIndex, entry.elementIndex)"
                              >
                                <img
                                  :src="resolveImageBlockUrl(nestedEntry.element)"
                                  alt="Page image"
                                  class="block h-auto object-cover"
                                  :style="getImageBlockLinkedImageStyle(nestedEntry.element)"
                                >
                              </a>
                              <img
                                v-else
                                :src="resolveImageBlockUrl(nestedEntry.element)"
                                alt="Page image"
                                class="block h-auto object-cover"
                                :style="getImageBlockRenderStyle(nestedEntry.element)"
                              >
                            </div>
                          </div>
                          <div
                            v-else-if="nestedEntry.element.type === 'button'"
                            class="p-0"
                          >
                            <div
                              class="w-full"
                              :class="getButtonBlockAlignClass(nestedEntry.element)"
                            >
                              <button
                                type="button"
                                class="rounded-xl font-semibold shadow-sm transition-colors break-words [overflow-wrap:anywhere]"
                                :style="getButtonBlockRenderStyle(nestedEntry.element)"
                              >
                                <span
                                  class="inline-block min-w-[2ch] cursor-text outline-none"
                                  contenteditable="true"
                                  spellcheck="false"
                                  @click.stop="onRowElementButtonTextClick(i, rowIndex, nestedEntry.elementIndex, entry.elementIndex)"
                                  @focus="onRowElementButtonTextFocus(i, rowIndex, nestedEntry.elementIndex, entry.elementIndex)"
                                  @blur="onRowElementButtonTextBlur(i, rowIndex, nestedEntry.elementIndex, $event, entry.elementIndex)"
                                  @keydown.enter.prevent="onRowElementButtonTextEnter(i, rowIndex, nestedEntry.elementIndex, $event, entry.elementIndex)"
                                >
                                  {{ getButtonElementText(nestedEntry.element?.props) }}
                                </span>
                              </button>
                            </div>
                          </div>
                          <div v-else-if="nestedEntry.element.type === 'bullet-list'" class="p-0">
                            <ul class="m-0 grid list-none p-0" :style="getBulletListStyle(nestedEntry.element)">
                              <li v-for="(item, bulletIndex) in getBulletItems(nestedEntry.element)" :key="`nested-bullet-${bulletIndex}`" class="flex min-w-0 items-start">
                                <span class="mt-[0.15em] inline-flex shrink-0 items-center justify-center font-bold" :style="getBulletIconStyle(nestedEntry.element)">
                                  <CheckCircleIcon v-if="isCircleCheckBullet(nestedEntry.element)" class="h-full w-full" aria-hidden="true" />
                                  <template v-else>{{ getBulletIconText(nestedEntry.element) }}</template>
                                </span>
                                <span contenteditable="true" spellcheck="true" class="min-w-0 flex-1 cursor-text outline-none" @click.stop @input="onBulletItemInput(nestedEntry.element, bulletIndex, $event)">{{ item.text }}</span>
                              </li>
                            </ul>
                          </div>
                          <div v-else-if="nestedEntry.element.type === 'share'" class="p-0">
                            <div class="flex w-full flex-col gap-2" :class="getShareAlignmentClass(nestedEntry.element)">
                              <div v-if="nestedEntry.element.props?.shareShowLabel !== false" class="text-sm font-semibold" :style="{ color: nestedEntry.element.props?.shareButtonColor || '#18181B' }">{{ nestedEntry.element.props?.shareLabel || 'Share this page' }}</div>
                              <div class="flex flex-wrap" :style="{ gap: `${Number(nestedEntry.element.props?.shareButtonGap ?? 10)}px` }">
                                <button v-for="network in getShareNetworks(nestedEntry.element)" :key="network" type="button" :aria-label="getShareNetworkLabel(network)" class="inline-flex items-center justify-center font-bold shadow-sm" :style="getShareButtonStyle(nestedEntry.element)" @click.stop.prevent>{{ getShareNetworkMark(network) }}</button>
                              </div>
                            </div>
                          </div>
                          <div v-else-if="nestedEntry.element.type === 'optin-form'" class="p-0">
                            <div class="w-full">
                              <h3 class="text-xl font-bold" :style="{ color: nestedEntry.element.props?.optinInputTextColor || '#18181B' }">{{ nestedEntry.element.props?.optinHeadline }}</h3>
                              <p v-if="nestedEntry.element.props?.optinDescription" class="mt-1 text-sm" :style="{ color: nestedEntry.element.props?.optinLabelColor || '#52525B' }">{{ nestedEntry.element.props.optinDescription }}</p>
                              <div class="mt-4 grid" :style="{ gap: `${Number(nestedEntry.element.props?.optinFieldGap ?? 12)}px` }">
                                <label v-for="field in getOptinFields(nestedEntry.element)" :key="field.key" class="grid gap-1 text-xs font-semibold" :style="{ color: nestedEntry.element.props?.optinLabelColor || '#3F3F46' }">
                                  <span v-if="nestedEntry.element.props?.optinShowLabels !== false && field.showLabel !== false">{{ field.label }}<span v-if="field.required"> *</span></span>
                                  <input disabled :type="field.type" :placeholder="field.placeholder" class="w-full border px-3" :style="getOptinInputStyle(nestedEntry.element)">
                                </label>
                                <button type="button" class="w-full px-4 font-semibold shadow-sm" :style="getOptinButtonStyle(nestedEntry.element)">{{ nestedEntry.element.props?.optinButtonLabel || 'Subscribe' }}</button>
                              </div>
                            </div>
                          </div>
                          <div
                            v-else-if="nestedEntry.element.type === 'video'"
                            class="p-0"
                          >
                            <div
                              class="w-full overflow-hidden bg-transparent"
                              :style="getVideoBlockContainerStyle(nestedEntry.element)"
                            >
                              <div
                                v-if="resolveVideoEmbedHtml(nestedEntry.element)"
                                class="w-full h-full [&_iframe]:w-full [&_iframe]:h-full"
                                v-html="resolveVideoEmbedHtml(nestedEntry.element)"
                              />
                              <iframe
                                v-else-if="resolveVideoEmbedUrl(nestedEntry.element)"
                                :src="resolveVideoEmbedUrl(nestedEntry.element)"
                                class="w-full h-full border-0"
                                loading="lazy"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                allowfullscreen
                              />
                              <PageEditorVideoPlayer
                                v-else-if="shouldUsePageEditorVideoPlayer(nestedEntry.element)"
                                :file-id="resolveVideoFileId(nestedEntry.element)"
                                :playlist-url="resolveVideoPlaylistUrl(nestedEntry.element)"
                                :poster-url="resolveVideoPosterUrl(nestedEntry.element)"
                                :controls-color="resolveVideoControlColor(nestedEntry.element)"
                                :source-key="resolveVideoSourceKey(nestedEntry.element)"
                                :border-radius="resolveVideoBorderRadius(nestedEntry.element)"
                                class="w-full h-full"
                              />
                              <video
                                v-else-if="resolveVideoDirectUrl(nestedEntry.element)"
                                :src="resolveVideoDirectUrl(nestedEntry.element)"
                                class="w-full h-full object-cover bg-black"
                                controls
                                playsinline
                                preload="metadata"
                              />
                              <img
                                v-else-if="resolveVideoPosterUrl(nestedEntry.element)"
                                :src="resolveVideoPosterUrl(nestedEntry.element)"
                                alt="Video poster"
                                class="w-full h-full object-cover"
                              >
                              <div
                                v-else
                                class="w-full h-full flex items-center justify-center text-zinc-500 text-sm"
                              >
                                Upload video in settings
                              </div>
                            </div>
                          </div>
                          <div
                            v-else-if="nestedEntry.element.type === 'member-pricing-plans'"
                            class="p-0"
                          >
                            <div class="w-full rounded-2xl border border-zinc-200 bg-white p-4">
                              <div class="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                                Dynamic Membership Plans
                              </div>
                              <div class="mt-2 rounded-2xl border border-zinc-200 bg-white p-4">
                                <div class="text-lg font-bold text-zinc-900">
                                  Plan cards render from creator tiers
                                </div>
                                <div class="mt-2 text-sm text-zinc-600">
                                  This section is connected to your Membership Plan Tier settings.
                                </div>
                                <div class="mt-3 inline-flex rounded-md bg-[#2D8CFF] px-3 py-2 text-sm font-semibold text-white">
                                  Get Full Access
                                </div>
                              </div>
                            </div>
                          </div>
                          <div
                            v-else-if="nestedEntry.element.type === 'member-get-access-form'"
                            class="p-0"
                          >
                            <div class="w-full rounded-2xl border border-zinc-200 bg-white p-4">
                              <div class="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                                Dynamic Get Access Form
                              </div>
                              <div class="mt-2 rounded-2xl border border-zinc-200 bg-white p-4">
                                <div class="text-lg font-bold text-zinc-900">
                                  Your Details
                                </div>
                                <div class="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                                  <div class="rounded-md border border-zinc-300 px-3 py-2 text-sm text-zinc-400">
                                    First Name
                                  </div>
                                  <div class="rounded-md border border-zinc-300 px-3 py-2 text-sm text-zinc-400">
                                    Last Name
                                  </div>
                                </div>
                                <div class="mt-2 rounded-md border border-zinc-300 px-3 py-2 text-sm text-zinc-400">
                                  Email address
                                </div>
                                <div class="mt-2 rounded-md border border-zinc-300 px-3 py-2 text-sm text-zinc-400">
                                  Create Password
                                </div>
                                <div class="mt-3 text-xs text-zinc-600">
                                  Locked for editing to preserve account creation logic.
                                </div>
                                <div class="mt-3 inline-flex rounded-md bg-[#2D8CFF] px-3 py-2 text-sm font-semibold text-white">
                                  Continue To The Member's Area
                                </div>
                              </div>
                            </div>
                          </div>
                          <div
                            v-else-if="nestedEntry.element.type === 'checkout-order-form'"
                            class="p-0"
                          >
                            <div class="w-full" :style="checkoutPreviewCardStyle(nestedEntry.element)">
                              <div class="flex items-center justify-between gap-3">
                                <div class="text-lg font-bold text-zinc-900">{{ nestedEntry.element.props?.heading || 'Complete your order' }}</div>
                                <span v-if="nestedEntry.element.props?.showSecureBadge !== false" class="rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wide" :style="checkoutPreviewBadgeStyle(nestedEntry.element)">{{ checkoutSecureBadgeIcon(nestedEntry.element) }}{{ nestedEntry.element.props?.secureBadgeText || 'Secure checkout' }}</span>
                              </div>
                              <div v-if="nestedEntry.element.props?.checkoutMode === 'two_step' && showCheckoutStepTabs()" class="mt-3 grid grid-cols-2 gap-2 text-center text-[11px] font-bold">
                                <div class="rounded-lg px-2 py-2" :class="nestedEntry.element.props?.editorPreviewStep === 'payment' ? 'bg-emerald-50 text-emerald-700' : 'text-white'" :style="nestedEntry.element.props?.editorPreviewStep === 'payment' ? {} : { backgroundColor: nestedEntry.element.props?.accentColor || '#2D8CFF' }">{{ nestedEntry.element.props?.editorPreviewStep === 'payment' ? '✓' : '1 ·' }} {{ nestedEntry.element.props?.contactStepLabel || 'Your information' }}</div>
                                <div class="rounded-lg px-2 py-2" :class="nestedEntry.element.props?.editorPreviewStep === 'payment' ? 'text-white' : 'bg-zinc-100 text-zinc-500'" :style="nestedEntry.element.props?.editorPreviewStep === 'payment' ? { backgroundColor: nestedEntry.element.props?.accentColor || '#2D8CFF' } : {}">2 · {{ nestedEntry.element.props?.paymentStepLabel || 'Payment details' }}</div>
                              </div>
                              <template v-if="nestedEntry.element.props?.checkoutMode !== 'two_step' || nestedEntry.element.props?.editorPreviewStep !== 'payment'">
                                <div class="mt-3 grid grid-cols-2 gap-2"><div class="border px-3 py-2 text-xs text-zinc-400" :style="checkoutPreviewInputStyle(nestedEntry.element)">{{ nestedEntry.element.props?.firstNameLabel || 'First name' }}</div><div class="border px-3 py-2 text-xs text-zinc-400" :style="checkoutPreviewInputStyle(nestedEntry.element)">{{ nestedEntry.element.props?.lastNameLabel || 'Last name' }}</div></div>
                                <div class="mt-2 border px-3 py-2 text-xs text-zinc-400" :style="checkoutPreviewInputStyle(nestedEntry.element)">{{ nestedEntry.element.props?.emailLabel || 'Email address' }}</div>
                                <div v-if="nestedEntry.element.props?.collectCompany" class="mt-2 border px-3 py-2 text-xs text-zinc-400" :style="checkoutPreviewInputStyle(nestedEntry.element)">{{ nestedEntry.element.props?.companyLabel || 'Company' }}</div>
                                <div v-if="nestedEntry.element.props?.collectPhone !== false" class="mt-2 border px-3 py-2 text-xs text-zinc-400" :style="checkoutPreviewInputStyle(nestedEntry.element)">{{ nestedEntry.element.props?.phoneLabel || 'Phone number' }}{{ nestedEntry.element.props?.phoneRequired ? '' : ' (optional)' }}</div>
                                <label v-if="nestedEntry.element.props?.smsConsentEnabled" class="mt-2 flex gap-2 text-[10px] leading-4 text-zinc-500"><input type="checkbox" disabled>{{ nestedEntry.element.props?.smsConsentLabel }}</label>
                                <template v-if="nestedEntry.element.props?.collectShippingAddress"><div class="mt-3 text-xs font-bold text-zinc-700">{{ nestedEntry.element.props?.shippingHeading || 'Shipping address' }}</div><div class="mt-2 border px-3 py-2 text-xs text-zinc-400" :style="checkoutPreviewInputStyle(nestedEntry.element)">{{ nestedEntry.element.props?.addressLabel || 'Street address' }}</div><div class="mt-2 grid grid-cols-2 gap-2"><div class="border px-3 py-2 text-xs text-zinc-400" :style="checkoutPreviewInputStyle(nestedEntry.element)">{{ nestedEntry.element.props?.cityLabel || 'City' }}</div><div class="border px-3 py-2 text-xs text-zinc-400" :style="checkoutPreviewInputStyle(nestedEntry.element)">{{ nestedEntry.element.props?.stateLabel || 'State / province' }}</div></div></template>
                              </template>
                              <fieldset v-if="nestedEntry.element.props?.productSelectorEnabled && (nestedEntry.element.props?.checkoutMode !== 'two_step' || nestedEntry.element.props?.editorPreviewStep !== 'payment')" class="mt-3 space-y-1.5"><legend class="mb-1 text-[11px] font-extrabold text-zinc-800">{{ nestedEntry.element.props?.productSelectorHeading || '2 · Select payment plan' }}</legend><div v-for="(option, optionIndex) in checkoutProductSelectorOptions(nestedEntry.element)" :key="option.id" class="flex items-center justify-between gap-2 border px-2.5 py-2 text-left" :style="{ borderColor: optionIndex === 0 ? '#16A34A' : (nestedEntry.element.props?.inputBorderColor || '#D4D4D8'), borderWidth: optionIndex === 0 ? '2px' : '1px', borderRadius: `${Math.max(0, Math.min(32, Number(nestedEntry.element.props?.formCornerRadius ?? 12) || 0))}px` }"><span class="min-w-0"><strong class="block truncate text-[10px] text-zinc-800">{{ option.title }}</strong><small class="block truncate text-[9px] text-zinc-400">{{ checkoutProductOptionPrice(option) }}</small></span><CheckCircleSolidIcon v-if="optionIndex === 0" class="h-5 w-5 shrink-0 text-green-600" aria-hidden="true" /><span v-else class="h-4 w-4 shrink-0 rounded-full border border-zinc-300" /></div></fieldset>
                              <div v-if="nestedEntry.element.props?.checkoutMode !== 'two_step' || nestedEntry.element.props?.editorPreviewStep === 'payment'" class="mt-2 border px-3 py-3 text-xs text-zinc-400" :style="checkoutPreviewInputStyle(nestedEntry.element)">Card information</div>
                              <label v-if="nestedEntry.element.props?.orderBumpEnabled && nestedEntry.element.props?.editorPreviewStep === 'payment'" class="mt-3 flex gap-2 rounded-lg border-2 border-dashed p-3 text-left" :style="{ borderColor: nestedEntry.element.props?.accentColor || '#2D8CFF' }"><input type="checkbox" :checked="nestedEntry.element.props?.orderBumpSelectedByDefault" disabled><span><strong class="block text-xs text-zinc-800">{{ nestedEntry.element.props?.orderBumpHeadline || 'Yes, add this to my order' }}</strong><span class="mt-1 block text-[10px] text-zinc-500">{{ nestedEntry.element.props?.orderBumpDescription }}</span></span></label>
                              <label v-if="nestedEntry.element.props?.termsEnabled" class="mt-2 flex gap-2 text-[10px] leading-4 text-zinc-500"><input type="checkbox" disabled>{{ nestedEntry.element.props?.termsLabel }}</label>
                              <div class="mt-3 px-3 py-2 text-center" :style="checkoutPreviewButtonStyle(nestedEntry.element)">{{ nestedEntry.element.props?.checkoutMode === 'two_step' && nestedEntry.element.props?.editorPreviewStep !== 'payment' ? (nestedEntry.element.props?.continueButtonLabel || 'Continue to secure payment') : (nestedEntry.element.props?.buttonLabel || 'Complete purchase') }}</div><p v-if="nestedEntry.element.props?.buttonSubtext" class="mt-1 text-center text-[10px] text-zinc-400">{{ nestedEntry.element.props.buttonSubtext }}</p>
                            </div>
                          </div>
                          <div
                            v-else-if="nestedEntry.element.type === 'quiz-result-history'"
                            class="w-full"
                          >
                            <div class="mb-4 flex items-center justify-between">
                              <h2 class="text-base font-semibold leading-6 text-gray-900">
                                My Past Results
                              </h2>
                            </div>
                            <div class="rounded-md border border-dashed border-zinc-300 bg-white p-8 text-center text-sm text-zinc-500 shadow-sm">
                              Member results will appear here.
                            </div>
                          </div>
                          <MeetSchedulerCard
                            v-else-if="nestedEntry.element.type === 'meet-scheduler'"
                            class="w-full"
                            :settings="meetSchedulerSettings"
                            :logo-url="meetSchedulerLogoUrl"
                            :compact-layout="currentViewportMode !== 'desktop'"
                            preview
                          />
                          <div
                            v-else-if="nestedEntry.element.type === 'member-approval-form'"
                            class="p-0"
                          >
                            <div class="w-full">
                              <form class="space-y-3">
                                <div class="h-11 w-full rounded-md border border-zinc-300 px-3 text-sm leading-[44px] text-zinc-400">
                                  Full name
                                </div>
                                <div class="h-11 w-full rounded-md border border-zinc-300 px-3 text-sm leading-[44px] text-zinc-400">
                                  Email
                                </div>
                                <div class="h-11 w-full rounded-md border border-zinc-300 px-3 text-sm leading-[44px] text-zinc-400">
                                  Phone (optional)
                                </div>
                                <div class="h-[96px] w-full rounded-md border border-zinc-300 px-3 py-2 text-sm text-zinc-400">
                                  Message (optional)
                                </div>
                                <button
                                  type="button"
                                  class="inline-flex min-h-[44px] w-full items-center justify-center rounded-md bg-[#2D8CFF] px-4 py-2 text-sm font-semibold text-white"
                                  disabled
                                >
                                  Send Request
                                </button>
                              </form>
                            </div>
                          </div>
                          <div
                            v-else-if="nestedEntry.element.type === 'member-get-access-success'"
                            class="p-0"
                          >
                            <div class="mx-auto mt-16 max-w-3xl rounded-3xl bg-white px-[30px] pb-8 ring-1 ring-gray-200 sm:mt-10 md:px-[50px] lg:w-[700px]">
                              <div class="p-8 sm:p-10 lg:flex-auto">
                                <h3 class="text-center text-4xl font-bold tracking-tight text-gray-900">
                                  Your Account is Confirmed!
                                </h3>
                                <CheckBadgeIcon class="mx-auto mt-8 h-20 w-20 text-[#21c251f6]" />
                              </div>
                              <div>
                                <h4 class="ml-[15px] text-center text-2xl font-semibold tracking-tight text-gray-900">
                                  Please wait while we redirect you to your account...
                                </h4>
                                <p class="mt-4 text-center text-base text-gray-600">
                                  Auto login begins in <span class="font-semibold text-gray-900">10</span> seconds.
                                </p>
                                <div class="mt-10 flex justify-center space-x-2">
                                  <div class="h-4 w-4 rounded-full bg-blue-500" />
                                  <div class="h-4 w-4 rounded-full bg-blue-500" />
                                  <div class="h-4 w-4 rounded-full bg-blue-500" />
                                </div>
                                <h4 class="ml-[15px] mt-8 text-center text-2xl font-semibold tracking-tight text-gray-900">
                                  {{ String(nestedEntry.element?.props?.successHelperText || 'Please check your inbox and spam folder for your confirmation and welcome email.') }}
                                </h4>
                              </div>
                            </div>
                          </div>
                          <div
                            v-else
                            class="px-3 py-2 text-sm text-zinc-500"
                          >
                            {{ getRowElementBadgeLabel(nestedEntry.element) }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    v-else-if="entry.element.type === 'text'"
                    class="group/text"
                  >
                    <p
                      v-if="!isRowElementTextEditing(i, rowIndex, entry.elementIndex)"
                      class="m-0 w-full px-0 py-0 rounded-none text-md font-normal leading-relaxed border-0 outline-none whitespace-pre-wrap break-words [overflow-wrap:anywhere] cursor-text transition-colors duration-200"
                      :style="getTextBlockStyle(entry.element)"
                      @click.stop="startRowElementTextEdit(i, rowIndex, entry.elementIndex)"
                    >
                      {{ entry.element.props?.content || 'New text' }}
                    </p>
                    <textarea
                      v-else
                      :ref="(el) => setRowElementTextEditorRef(i, rowIndex, entry.elementIndex, el)"
                      :value="getRowElementTextDraft(i, rowIndex, entry.elementIndex, entry.element)"
                      rows="1"
                      class="m-0 block w-full px-0 py-0 rounded-none text-md font-normal leading-relaxed whitespace-pre-wrap border-0 focus:border-0 outline-none focus:outline-none focus:ring-0 resize-none bg-transparent shadow-none appearance-none break-words [overflow-wrap:anywhere] overflow-x-hidden"
                      :style="getTextBlockStyle(entry.element)"
                      placeholder="Write your text..."
                      @input="onRowElementTextDraftInput(i, rowIndex, entry.elementIndex, $event)"
                      @focus="onSelectRowElement(i, rowIndex, entry.elementIndex)"
                      @blur="commitRowElementTextEdit(i, rowIndex, entry.elementIndex)"
                      @keydown.enter.exact.prevent="commitRowElementTextEdit(i, rowIndex, entry.elementIndex)"
                      @keydown.ctrl.enter.prevent="commitRowElementTextEdit(i, rowIndex, entry.elementIndex)"
                      @keydown.meta.enter.prevent="commitRowElementTextEdit(i, rowIndex, entry.elementIndex)"
                      @keydown.esc.prevent="cancelRowElementTextEdit(i, rowIndex, entry.elementIndex)"
                    />
                  </div>
                  <div
                    v-else-if="entry.element.type === 'header'"
                    class="bg-transparent p-0"
                  >
                    <component
                      :is="entry.element.props?.level || 'h2'"
                      class="font-bold tracking-tight"
                      :style="{
                        fontSize: ((entry.element.props && Number(entry.element.props.size)) || 32) + 'px',
                        lineHeight: '1.15',
                        whiteSpace: 'pre-wrap',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                        hyphens: 'auto',
                        maxWidth: '100%',
                        ...getHeaderBlockTextStyle(entry.element)
                      }"
                      v-text="entry.element.props?.text || 'Section heading'"
                    />
                  </div>
                  <div
                    v-else-if="entry.element.type === 'image'"
                    class="bg-transparent p-0"
                  >
                    <div
                      class="w-full"
                      :class="getImageBlockAlignClass(entry.element)"
                    >
                      <a
                        v-if="getImageBlockHref(entry.element)"
                        :href="getImageBlockHref(entry.element)"
                        :target="getImageBlockTarget(entry.element)"
                        :rel="getImageBlockRel(entry.element)"
                        class="block max-w-full"
                        :style="getImageBlockLinkStyle(entry.element)"
                        @click.stop.prevent="onSelectRowElement(i, rowIndex, entry.elementIndex)"
                      >
                        <img
                          :src="resolveImageBlockUrl(entry.element)"
                          alt="Page image"
                          class="block h-auto object-cover"
                          :style="getImageBlockLinkedImageStyle(entry.element)"
                        >
                      </a>
                      <img
                        v-else
                        :src="resolveImageBlockUrl(entry.element)"
                        alt="Page image"
                        class="block h-auto object-cover"
                        :style="getImageBlockRenderStyle(entry.element)"
                      >
                    </div>
                  </div>
                  <div
                    v-else-if="entry.element.type === 'button'"
                    class="bg-transparent p-0"
                  >
                    <div
                      class="w-full"
                      :class="getButtonBlockAlignClass(entry.element)"
                    >
                      <button
                        type="button"
                        class="rounded-xl font-semibold shadow-sm transition-colors break-words [overflow-wrap:anywhere]"
                        :style="getButtonBlockRenderStyle(entry.element)"
                      >
                        <span
                          class="inline-block min-w-[2ch] cursor-text outline-none"
                          contenteditable="true"
                          spellcheck="false"
                          @click.stop="onRowElementButtonTextClick(i, rowIndex, entry.elementIndex)"
                          @focus="onRowElementButtonTextFocus(i, rowIndex, entry.elementIndex)"
                          @blur="onRowElementButtonTextBlur(i, rowIndex, entry.elementIndex, $event)"
                          @keydown.enter.prevent="onRowElementButtonTextEnter(i, rowIndex, entry.elementIndex, $event)"
                        >{{ getButtonElementText(entry.element?.props) }}</span>
                      </button>
                    </div>
                  </div>
                  <div v-else-if="entry.element.type === 'bullet-list'" class="bg-transparent p-0">
                    <ul class="m-0 grid list-none p-0" :style="getBulletListStyle(entry.element)">
                      <li v-for="(item, bulletIndex) in getBulletItems(entry.element)" :key="`bullet-${bulletIndex}`" class="flex min-w-0 items-start">
                        <span class="mt-[0.15em] inline-flex shrink-0 items-center justify-center font-bold" :style="getBulletIconStyle(entry.element)">
                          <CheckCircleIcon v-if="isCircleCheckBullet(entry.element)" class="h-full w-full" aria-hidden="true" />
                          <template v-else>{{ getBulletIconText(entry.element) }}</template>
                        </span>
                        <span contenteditable="true" spellcheck="true" class="min-w-0 flex-1 cursor-text outline-none" @click.stop @input="onBulletItemInput(entry.element, bulletIndex, $event)">{{ item.text }}</span>
                      </li>
                    </ul>
                  </div>
                  <div v-else-if="entry.element.type === 'share'" class="bg-transparent p-0">
                    <div class="flex w-full flex-col gap-2" :class="getShareAlignmentClass(entry.element)">
                      <div v-if="entry.element.props?.shareShowLabel !== false" class="text-sm font-semibold" :style="{ color: entry.element.props?.shareButtonColor || '#18181B' }">{{ entry.element.props?.shareLabel || 'Share this page' }}</div>
                      <div class="flex flex-wrap" :style="{ gap: `${Number(entry.element.props?.shareButtonGap ?? 10)}px` }">
                        <button v-for="network in getShareNetworks(entry.element)" :key="network" type="button" :aria-label="getShareNetworkLabel(network)" class="inline-flex items-center justify-center font-bold shadow-sm" :style="getShareButtonStyle(entry.element)" @click.stop.prevent>{{ getShareNetworkMark(network) }}</button>
                      </div>
                    </div>
                  </div>
                  <div v-else-if="entry.element.type === 'optin-form'" class="bg-transparent p-0">
                    <div class="w-full">
                      <h3 class="text-2xl font-bold" :style="{ color: entry.element.props?.optinInputTextColor || '#18181B' }">{{ entry.element.props?.optinHeadline }}</h3>
                      <p v-if="entry.element.props?.optinDescription" class="mt-2 text-sm" :style="{ color: entry.element.props?.optinLabelColor || '#52525B' }">{{ entry.element.props.optinDescription }}</p>
                      <div class="mt-5 grid" :style="{ gap: `${Number(entry.element.props?.optinFieldGap ?? 12)}px` }">
                        <label v-for="field in getOptinFields(entry.element)" :key="field.key" class="grid gap-1 text-xs font-semibold" :style="{ color: entry.element.props?.optinLabelColor || '#3F3F46' }">
                          <span v-if="entry.element.props?.optinShowLabels !== false && field.showLabel !== false">{{ field.label }}<span v-if="field.required"> *</span></span>
                          <input disabled :type="field.type" :placeholder="field.placeholder" class="w-full border px-3" :style="getOptinInputStyle(entry.element)">
                        </label>
                        <button type="button" class="w-full px-4 font-semibold shadow-sm" :style="getOptinButtonStyle(entry.element)">{{ entry.element.props?.optinButtonLabel || 'Subscribe' }}</button>
                      </div>
                    </div>
                  </div>
                  <div
                    v-else-if="entry.element.type === 'video'"
                    class="bg-transparent p-0"
                  >
                    <div
                      class="w-full overflow-hidden bg-transparent"
                      :style="getVideoBlockContainerStyle(entry.element)"
                    >
                      <div
                        v-if="resolveVideoEmbedHtml(entry.element)"
                        class="w-full h-full [&_iframe]:w-full [&_iframe]:h-full"
                        v-html="resolveVideoEmbedHtml(entry.element)"
                      />
                      <iframe
                        v-else-if="resolveVideoEmbedUrl(entry.element)"
                        :src="resolveVideoEmbedUrl(entry.element)"
                        class="w-full h-full border-0"
                        loading="lazy"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowfullscreen
                      />
                      <PageEditorVideoPlayer
                        v-else-if="shouldUsePageEditorVideoPlayer(entry.element)"
                        :file-id="resolveVideoFileId(entry.element)"
                        :playlist-url="resolveVideoPlaylistUrl(entry.element)"
                        :poster-url="resolveVideoPosterUrl(entry.element)"
                        :controls-color="resolveVideoControlColor(entry.element)"
                        :source-key="resolveVideoSourceKey(entry.element)"
                        :border-radius="resolveVideoBorderRadius(entry.element)"
                        class="w-full h-full"
                      />
                      <video
                        v-else-if="resolveVideoDirectUrl(entry.element)"
                        :src="resolveVideoDirectUrl(entry.element)"
                        class="w-full h-full object-cover bg-black"
                        controls
                        playsinline
                        preload="metadata"
                      />
                      <img
                        v-else-if="resolveVideoPosterUrl(entry.element)"
                        :src="resolveVideoPosterUrl(entry.element)"
                        alt="Video poster"
                        class="w-full h-full object-cover"
                      >
                      <div
                        v-else
                        class="w-full h-full flex items-center justify-center text-zinc-500 text-sm"
                      >
                        Upload video in settings
                      </div>
                    </div>
                  </div>
                  <div
                    v-else-if="isMemberAuthFormElement(entry.element)"
                    class="bg-transparent p-0"
                  >
                    <div
                      v-if="isCreateFreeAccountFormElement(entry.element)"
                      class="space-y-4"
                    >
                      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div>
                          <label class="block text-sm font-medium leading-6 text-gray-900">First Name</label>
                          <div class="mt-2 block w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 px-3 text-sm text-gray-400 sm:leading-6">
                            First Name
                          </div>
                        </div>
                        <div>
                          <label class="block text-sm font-medium leading-6 text-gray-900">Last Name</label>
                          <div class="mt-2 block w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 px-3 text-sm text-gray-400 sm:leading-6">
                            Last Name
                          </div>
                        </div>
                      </div>
                      <div>
                        <label class="block text-sm font-medium leading-6 text-gray-900">Email address</label>
                        <div class="mt-2 block w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 px-3 text-sm text-gray-400 sm:leading-6">
                          Email address
                        </div>
                      </div>
                      <div>
                        <label class="block text-sm font-medium leading-6 text-gray-900">Create a Password</label>
                        <div class="mt-2 relative block w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 px-3 pr-10 text-sm text-gray-400 sm:leading-6">
                          Create a Password
                          <EyeSlashIcon class="absolute h-5 w-5 text-gray-400 right-3 top-[8px]" />
                        </div>
                      </div>
                      <button
                        type="button"
                        class="mt-3 flex w-full justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm"
                        :style="{ backgroundColor: getLoginSubmitButtonColor(entry.element) }"
                        disabled
                      >
                        {{ String(entry.element?.props?.submitButtonText || 'Sign Up') }}
                      </button>
                    </div>
                    <div
                      v-else
                      class="space-y-6"
                    >
                      <div>
                        <label class="block text-sm font-medium leading-6 text-gray-900">Email address</label>
                        <div class="mt-2 block w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 px-3 text-sm text-gray-400 sm:leading-6">
                          member@example.com
                        </div>
                      </div>
                      <div>
                        <label class="block text-sm font-medium leading-6 text-gray-900">Password</label>
                        <div class="mt-2 relative">
                          <div class="block w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 px-3 pr-10 text-sm text-gray-400 sm:leading-6">
                            ********
                          </div>
                          <EyeSlashIcon class="absolute h-5 w-5 text-gray-400 right-3 top-[8px]" />
                        </div>
                      </div>
                      <div class="flex items-center justify-between">
                        <div class="flex items-center gap-3 text-sm leading-6 text-gray-700">
                          <input
                            type="checkbox"
                            disabled
                            class="h-4 w-4 rounded border-gray-300"
                            :style="{ accentColor: getLoginRememberCheckboxColor(entry.element) }"
                          >
                          <span>Remember me</span>
                        </div>
                        <div
                          class="text-sm font-semibold leading-6"
                          :style="{ color: getLoginForgotPasswordColor(entry.element) }"
                        >
                          {{ String(entry.element?.props?.forgotPasswordText || 'Forgot password?') }}
                        </div>
                      </div>
                      <button
                        type="button"
                        class="flex w-full justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm"
                        :style="{ backgroundColor: getLoginSubmitButtonColor(entry.element) }"
                        disabled
                      >
                        {{ String(entry.element?.props?.submitButtonText || 'Sign in') }}
                      </button>
                    </div>
                  </div>
                  <div
                    v-else-if="entry.element.type === 'quiz-result-history'"
                    class="w-full"
                  >
                    <div class="mb-4 flex items-center justify-between">
                      <h2 class="text-base font-semibold leading-6 text-gray-900">
                        My Past Results
                      </h2>
                    </div>
                    <div class="rounded-md border border-dashed border-zinc-300 bg-white p-8 text-center text-sm text-zinc-500 shadow-sm">
                      Member results will appear here.
                    </div>
                  </div>
                  <MeetSchedulerCard
                    v-else-if="entry.element.type === 'meet-scheduler'"
                    class="w-full"
                    :settings="meetSchedulerSettings"
                    :logo-url="meetSchedulerLogoUrl"
                    :compact-layout="currentViewportMode !== 'desktop'"
                    preview
                  />
                  <div
                    v-else-if="entry.element.type === 'member-pricing-plans'"
                    class="bg-transparent p-0"
                  >
                    <div class="w-full rounded-2xl border border-zinc-200 bg-white p-6">
                      <div class="text-sm font-semibold uppercase tracking-wide text-zinc-500">
                        Dynamic Membership Plans
                      </div>
                      <div class="mt-3 rounded-2xl border border-zinc-200 bg-white p-6">
                        <div class="text-2xl font-bold tracking-tight text-zinc-900">
                          Plan cards render from creator tiers
                        </div>
                        <div class="mt-3 text-sm leading-6 text-zinc-600">
                          This section is connected to Membership Plan Tiers and is intentionally locked in the page editor.
                        </div>
                        <div class="mt-4 inline-flex rounded-md bg-[#2D8CFF] px-4 py-2 text-sm font-semibold text-white">
                          Get Full Access
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-else-if="entry.element.type === 'member-get-access-form'"
                    class="bg-transparent p-0"
                  >
                    <div class="w-full rounded-2xl border border-zinc-200 bg-white p-6">
                      <div class="text-sm font-semibold uppercase tracking-wide text-zinc-500">
                        Dynamic Get Access Form
                      </div>
                      <div class="mt-3 rounded-2xl border border-zinc-200 bg-white p-6">
                        <div class="text-2xl font-bold tracking-tight text-zinc-900">
                          Your Details
                        </div>
                        <div class="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <div class="rounded-md border border-zinc-300 px-3 py-2 text-sm text-zinc-400">
                            First Name
                          </div>
                          <div class="rounded-md border border-zinc-300 px-3 py-2 text-sm text-zinc-400">
                            Last Name
                          </div>
                        </div>
                        <div class="mt-3 rounded-md border border-zinc-300 px-3 py-2 text-sm text-zinc-400">
                          Email address
                        </div>
                        <div class="mt-3 rounded-md border border-zinc-300 px-3 py-2 text-sm text-zinc-400">
                          Create Password
                        </div>
                        <div class="mt-3 text-sm leading-6 text-zinc-600">
                          This block is connected to member onboarding and is intentionally locked in the page editor.
                        </div>
                        <div class="mt-4 inline-flex rounded-md bg-[#2D8CFF] px-4 py-2 text-sm font-semibold text-white">
                          Continue To The Member's Area
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-else-if="entry.element.type === 'checkout-order-form'"
                    class="bg-transparent p-0"
                  >
                    <div class="w-full" :style="checkoutPreviewCardStyle(entry.element)">
                      <div class="flex items-center justify-between gap-3"><div><div class="text-xl font-extrabold tracking-tight text-zinc-950">{{ entry.element.props?.heading || 'Complete your order' }}</div><p class="mt-1 text-xs text-zinc-500">{{ entry.element.props?.editorPreviewStep === 'payment' ? (entry.element.props?.paymentStepLabel || 'Payment details') : (entry.element.props?.contactStepLabel || 'Your information') }}</p></div><span v-if="entry.element.props?.showSecureBadge !== false" class="rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wide" :style="checkoutPreviewBadgeStyle(entry.element)">{{ checkoutSecureBadgeIcon(entry.element) }}{{ entry.element.props?.secureBadgeText || 'Secure checkout' }}</span></div>
                      <div v-if="entry.element.props?.checkoutMode === 'two_step' && showCheckoutStepTabs()" class="mt-5 grid grid-cols-2 gap-2 text-center text-xs font-bold"><div class="rounded-xl px-3 py-2.5" :class="entry.element.props?.editorPreviewStep === 'payment' ? 'bg-emerald-50 text-emerald-700' : 'text-white'" :style="entry.element.props?.editorPreviewStep === 'payment' ? {} : { backgroundColor: entry.element.props?.accentColor || '#2D8CFF' }">{{ entry.element.props?.editorPreviewStep === 'payment' ? '✓' : '1 ·' }} {{ entry.element.props?.contactStepLabel || 'Your information' }}</div><div class="rounded-xl px-3 py-2.5" :class="entry.element.props?.editorPreviewStep === 'payment' ? 'text-white' : 'bg-zinc-100 text-zinc-500'" :style="entry.element.props?.editorPreviewStep === 'payment' ? { backgroundColor: entry.element.props?.accentColor || '#2D8CFF' } : {}">2 · {{ entry.element.props?.paymentStepLabel || 'Payment details' }}</div></div>
                      <template v-if="entry.element.props?.checkoutMode !== 'two_step' || entry.element.props?.editorPreviewStep !== 'payment'">
                        <div class="mt-6 grid grid-cols-2 gap-4"><div class="border px-4 py-3 text-sm text-zinc-400 shadow-sm" :style="checkoutPreviewInputStyle(entry.element)">{{ entry.element.props?.firstNameLabel || 'First name' }}</div><div class="border px-4 py-3 text-sm text-zinc-400 shadow-sm" :style="checkoutPreviewInputStyle(entry.element)">{{ entry.element.props?.lastNameLabel || 'Last name' }}</div></div>
                        <div class="mt-4 border px-4 py-3 text-sm text-zinc-400 shadow-sm" :style="checkoutPreviewInputStyle(entry.element)">{{ entry.element.props?.emailLabel || 'Email address' }}</div>
                        <div v-if="entry.element.props?.collectCompany" class="mt-4 border px-4 py-3 text-sm text-zinc-400 shadow-sm" :style="checkoutPreviewInputStyle(entry.element)">{{ entry.element.props?.companyLabel || 'Company' }}</div>
                        <div v-if="entry.element.props?.collectPhone !== false" class="mt-4 border px-4 py-3 text-sm text-zinc-400 shadow-sm" :style="checkoutPreviewInputStyle(entry.element)">{{ entry.element.props?.phoneLabel || 'Phone number' }}{{ entry.element.props?.phoneRequired ? '' : ' (optional)' }}</div>
                        <label v-if="entry.element.props?.smsConsentEnabled" class="mt-3 flex gap-2 text-xs leading-5 text-zinc-500"><input type="checkbox" disabled>{{ entry.element.props?.smsConsentLabel }}</label>
                        <template v-if="entry.element.props?.collectShippingAddress"><div class="mt-5 text-sm font-extrabold text-zinc-800">{{ entry.element.props?.shippingHeading || 'Shipping address' }}</div><div class="mt-3 border px-4 py-3 text-sm text-zinc-400" :style="checkoutPreviewInputStyle(entry.element)">{{ entry.element.props?.addressLabel || 'Street address' }}</div><div class="mt-3 grid grid-cols-2 gap-4"><div class="border px-4 py-3 text-sm text-zinc-400" :style="checkoutPreviewInputStyle(entry.element)">{{ entry.element.props?.cityLabel || 'City' }}</div><div class="border px-4 py-3 text-sm text-zinc-400" :style="checkoutPreviewInputStyle(entry.element)">{{ entry.element.props?.stateLabel || 'State / province' }}</div></div><div class="mt-3 grid grid-cols-2 gap-4"><div class="border px-4 py-3 text-sm text-zinc-400" :style="checkoutPreviewInputStyle(entry.element)">{{ entry.element.props?.postalCodeLabel || 'Postal code' }}</div><div class="border px-4 py-3 text-sm text-zinc-400" :style="checkoutPreviewInputStyle(entry.element)">{{ entry.element.props?.countryLabel || 'Country' }}</div></div></template>
                      </template>
                      <fieldset v-if="entry.element.props?.productSelectorEnabled && (entry.element.props?.checkoutMode !== 'two_step' || entry.element.props?.editorPreviewStep !== 'payment')" class="mt-5 space-y-2"><legend class="mb-2 text-sm font-extrabold text-zinc-900">{{ entry.element.props?.productSelectorHeading || '2 · Select payment plan' }}</legend><div v-for="(option, optionIndex) in checkoutProductSelectorOptions(entry.element)" :key="option.id" class="flex items-center justify-between gap-3 border px-4 py-3 text-left" :style="{ borderColor: optionIndex === 0 ? '#16A34A' : (entry.element.props?.inputBorderColor || '#D4D4D8'), borderWidth: optionIndex === 0 ? '2px' : '1px', borderRadius: `${Math.max(0, Math.min(32, Number(entry.element.props?.formCornerRadius ?? 12) || 0))}px` }"><span class="min-w-0"><strong class="block text-sm text-zinc-900">{{ option.title }}</strong><small class="mt-0.5 block text-xs text-zinc-500">{{ checkoutProductOptionPrice(option) }}</small></span><CheckCircleSolidIcon v-if="optionIndex === 0" class="h-6 w-6 shrink-0 text-green-600" aria-hidden="true" /><span v-else class="h-5 w-5 shrink-0 rounded-full border border-zinc-300" /></div></fieldset>
                      <div v-if="entry.element.props?.checkoutMode !== 'two_step' || entry.element.props?.editorPreviewStep === 'payment'" class="mt-4 border px-4 py-3 text-sm text-zinc-400 shadow-sm" :style="checkoutPreviewInputStyle(entry.element)">Card information</div>
                      <label v-if="entry.element.props?.orderBumpEnabled && entry.element.props?.editorPreviewStep === 'payment'" class="mt-4 flex gap-3 rounded-xl border-2 border-dashed p-4 text-left" :style="{ borderColor: entry.element.props?.accentColor || '#2D8CFF' }"><input type="checkbox" :checked="entry.element.props?.orderBumpSelectedByDefault" disabled><span><strong class="block text-sm text-zinc-900">{{ entry.element.props?.orderBumpHeadline || 'Yes, add this to my order' }}</strong><span class="mt-1 block text-xs leading-5 text-zinc-500">{{ entry.element.props?.orderBumpDescription }}</span></span></label>
                      <label v-if="entry.element.props?.termsEnabled" class="mt-3 flex gap-2 text-xs leading-5 text-zinc-500"><input type="checkbox" disabled>{{ entry.element.props?.termsLabel }}</label>
                      <div class="mt-4 px-5 py-3.5 text-center" :style="checkoutPreviewButtonStyle(entry.element)">{{ entry.element.props?.checkoutMode === 'two_step' && entry.element.props?.editorPreviewStep !== 'payment' ? (entry.element.props?.continueButtonLabel || 'Continue to secure payment') : (entry.element.props?.buttonLabel || 'Complete purchase') }}</div><p v-if="entry.element.props?.buttonSubtext" class="mt-2 text-center text-xs text-zinc-400">{{ entry.element.props.buttonSubtext }}</p>
                      <div v-if="entry.element.props?.showTrustBadges !== false" class="mt-5 grid grid-cols-3 gap-2 text-center text-[10px] font-semibold text-zinc-400"><span>🔒 Secure</span><span>✓ Private</span><span>↻ Protected</span></div>
                    </div>
                  </div>
                  <div
                    v-else-if="entry.element.type === 'member-approval-form'"
                    class="bg-transparent p-0"
                  >
                    <div class="w-full">
                      <form class="space-y-3">
                        <div class="h-11 w-full rounded-md border border-zinc-300 px-3 text-sm leading-[44px] text-zinc-400">
                          Full name
                        </div>
                        <div class="h-11 w-full rounded-md border border-zinc-300 px-3 text-sm leading-[44px] text-zinc-400">
                          Email
                        </div>
                        <div class="h-11 w-full rounded-md border border-zinc-300 px-3 text-sm leading-[44px] text-zinc-400">
                          Phone (optional)
                        </div>
                        <div class="h-[96px] w-full rounded-md border border-zinc-300 px-3 py-2 text-sm text-zinc-400">
                          Message (optional)
                        </div>
                        <button
                          type="button"
                          class="inline-flex min-h-[44px] w-full items-center justify-center rounded-md bg-[#2D8CFF] px-4 py-2 text-sm font-semibold text-white"
                          disabled
                        >
                          Send Request
                        </button>
                      </form>
                    </div>
                  </div>
                  <div
                    v-else-if="entry.element.type === 'member-get-access-success'"
                    class="bg-transparent p-0"
                  >
                    <div class="mx-auto mt-16 max-w-3xl rounded-3xl bg-white px-[30px] pb-8 ring-1 ring-gray-200 sm:mt-10 md:px-[50px] lg:w-[700px]">
                      <div class="p-8 sm:p-10 lg:flex-auto">
                        <h3 class="text-center text-4xl font-bold tracking-tight text-gray-900">
                          Your Account is Confirmed!
                        </h3>
                        <CheckBadgeIcon class="mx-auto mt-8 h-20 w-20 text-[#21c251f6]" />
                      </div>
                      <div>
                        <h4 class="ml-[15px] text-center text-2xl font-semibold tracking-tight text-gray-900">
                          Please wait while we redirect you to your account...
                        </h4>
                        <p class="mt-4 text-center text-base text-gray-600">
                          Auto login begins in <span class="font-semibold text-gray-900">10</span> seconds.
                        </p>
                        <div class="mt-10 flex justify-center space-x-2">
                          <div class="h-4 w-4 rounded-full bg-blue-500" />
                          <div class="h-4 w-4 rounded-full bg-blue-500" />
                          <div class="h-4 w-4 rounded-full bg-blue-500" />
                        </div>
                        <h4 class="ml-[15px] mt-8 text-center text-2xl font-semibold tracking-tight text-gray-900">
                          {{ String(entry.element?.props?.successHelperText || 'Please check your inbox and spam folder for your confirmation and welcome email.') }}
                        </h4>
                      </div>
                    </div>
                  </div>
                  <div
                    v-else-if="entry.element.type === 'mp3'"
                    class="rounded-md bg-white/80 p-3"
                  >
                    <div class="mx-auto w-full rounded-xl border border-zinc-200 bg-zinc-50 p-3 text-center">
                      <div class="text-sm font-medium text-zinc-700">
                        MP3 element
                      </div>
                    </div>
                  </div>
                  <div
                    v-else-if="entry.element.type === 'embed'"
                    class="rounded-md bg-white/80 p-3"
                  >
                    <div class="mx-auto w-full rounded-xl border border-dashed border-zinc-300 bg-zinc-50 p-4 text-center">
                      <div class="text-sm font-medium text-zinc-700">
                        Embed element
                      </div>
                    </div>
                  </div>
                  <div
                    v-else
                    class="rounded-md bg-white/80 p-3 text-sm text-zinc-500"
                  >
                    Unknown element: <span class="font-mono">{{ entry.element.type }}</span>
                  </div>

                  <div
                    class="absolute left-1/2 bottom-0 z-[140] -translate-x-1/2 translate-y-1/2 transition-opacity duration-150"
                    :class="getRowElementChromeVisibilityClasses(i, rowIndex, entry.elementIndex)"
                  >
                    <button
                      type="button"
                      :class="getRowElementPlusButtonClass(entry.element)"
                      @click.stop="openAddRowElementAfterPicker(i, rowIndex, entry.elementIndex, $event)"
                    >
                      <PlusIcon class="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div
              class="absolute left-1/2 bottom-0 z-[140] -translate-x-1/2 translate-y-1/2 transition-opacity duration-150"
              :class="getRowChromeVisibilityClasses(i, rowIndex)"
            >
              <button
                type="button"
                class="inline-flex h-8 w-8 items-center justify-center rounded-full text-white border-2 border-[#1D4ED8] shadow-md bg-[#2D8CFF] hover:bg-[#1D4ED8]"
                @click.stop="openAddRowAfterPicker(i, rowIndex, $event)"
              >
                <PlusIcon class="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- text block render (course-style inline click-to-edit) -->
      <div
        v-else-if="block.type === 'text'"
        class="rounded-md bg-white p-6"
      >
        <div class="group/text">
          <p
            v-if="textEditingIndex !== i"
            class="m-0 w-full px-0 py-0 rounded-none text-md font-normal whitespace-pre-line break-words [overflow-wrap:anywhere] cursor-pointer transition-colors duration-200"
            :style="getTextBlockStyle(block)"
            @click.stop="startTextBlockEdit(i)"
          >
            {{ block.props?.content || 'Click to add your page text...' }}
          </p>
          <textarea
            v-else
            ref="textBlockEditorRef"
            v-model="textEditingDraft"
            class="m-0 block w-full px-0 py-0 rounded-none text-md font-normal leading-relaxed whitespace-pre-wrap border-0 focus:border-0 outline-none focus:outline-none focus:ring-0 resize-none bg-transparent shadow-none appearance-none break-words [overflow-wrap:anywhere] overflow-x-hidden"
            :style="getTextBlockStyle(block)"
            placeholder="Write your page text..."
            @input="onTextBlockDraftInput"
            @blur="commitTextBlockEdit(i)"
            @keydown.enter.exact.prevent="commitTextBlockEdit(i)"
            @keydown.ctrl.enter.prevent="commitTextBlockEdit(i)"
            @keydown.meta.enter.prevent="commitTextBlockEdit(i)"
            @keydown.esc.prevent="cancelTextBlockEdit"
          />
        </div>
      </div>

      <!-- header block (inline editable when selected) -->
      <div
        v-else-if="block.type === 'header'"
        class="rounded-md bg-transparent p-6"
        @click.stop="openBlockSettingsMenu(i, block, $event)"
      >
        <!-- Selected: editable component, DOM owns text -->
        <!-- Selected: editable component, DOM owns text -->
        <component
          :is="block.props?.level || 'h2'"
          v-if="props.selectedIndex === i"
          :ref="el => initEditable(i, el, block.props?.text || '')"
          class="font-bold tracking-tight outline-none editable editable-active"
          :style="{
            fontSize: ((block.props && Number(block.props.size)) || 32) + 'px',
            lineHeight: '1.15',
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
            hyphens: 'auto',
            maxWidth: '100%',
            ...getHeaderBlockTextStyle(block)
          }"
          contenteditable="plaintext-only"
          :data-placeholder="'Section heading'"
          :data-index="i"
          @keydown="onHeaderKeydown($event, i)"
          @input="onEditableInput($event, i, 'text', false)"
          @compositionstart="onCompositionStart"
          @compositionend="onCompositionEnd"
        />

        <!-- Not selected: static component (Vue owns text) -->
        <component
          :is="block.props?.level || 'h2'"
          v-else
          class="font-bold tracking-tight"
          :style="{
            fontSize: ((block.props && Number(block.props.size)) || 32) + 'px',
            lineHeight: '1.15',
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
            hyphens: 'auto',
            maxWidth: '100%',
            ...getHeaderBlockTextStyle(block)
          }"
          v-text="block.props?.text || ''"
        />
      </div>


      <div
        v-else-if="block.type === 'image'"
        class="rounded-md bg-white p-6"
      >
        <div
          class="w-full"
          :class="getImageBlockAlignClass(block)"
        >
          <a
            v-if="getImageBlockHref(block)"
            :href="getImageBlockHref(block)"
            :target="getImageBlockTarget(block)"
            :rel="getImageBlockRel(block)"
            class="block max-w-full"
            :style="getImageBlockLinkStyle(block)"
            @click.stop.prevent="$emit('select', i)"
          >
            <img
              :src="resolveImageBlockUrl(block)"
              alt="Page image"
              class="h-auto rounded-lg border border-zinc-200 object-cover"
              :style="getImageBlockLinkedImageStyle(block)"
            >
          </a>
          <img
            v-else
            :src="resolveImageBlockUrl(block)"
            alt="Page image"
            class="h-auto rounded-lg border border-zinc-200 object-cover"
            :style="getImageBlockRenderStyle(block)"
            @click.stop="$emit('select', i)"
          >
        </div>
      </div>

      <div
        v-else-if="block.type === 'button'"
        class="bg-transparent p-0"
      >
        <div
          class="w-full"
          :class="getButtonBlockAlignClass(block)"
        >
          <button
            type="button"
            class="rounded-xl font-semibold shadow-sm transition-colors break-words [overflow-wrap:anywhere]"
            :style="getButtonBlockRenderStyle(block)"
            @click.stop="$emit('select', i)"
          >
            <span
              class="inline-block min-w-[2ch] cursor-text outline-none"
              contenteditable="true"
              spellcheck="false"
              @click.stop="onButtonBlockTextClick(i)"
              @focus="onButtonBlockTextFocus(i)"
              @blur="onButtonBlockTextBlur(i, $event)"
              @keydown.enter.prevent="onButtonBlockTextEnter(i, $event)"
            >{{ getButtonElementText(block?.props) }}</span>
          </button>
        </div>
      </div>

      <div
        v-else-if="block.type === 'video'"
        class="bg-transparent p-0"
      >
        <div
          class="w-full overflow-hidden bg-transparent"
          :style="getVideoBlockContainerStyle(block)"
        >
          <div
            v-if="resolveVideoEmbedHtml(block)"
            class="w-full h-full [&_iframe]:w-full [&_iframe]:h-full"
            v-html="resolveVideoEmbedHtml(block)"
          />
          <iframe
            v-else-if="resolveVideoEmbedUrl(block)"
            :src="resolveVideoEmbedUrl(block)"
            class="w-full h-full border-0"
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowfullscreen
          />
          <PageEditorVideoPlayer
            v-else-if="shouldUsePageEditorVideoPlayer(block)"
            :file-id="resolveVideoFileId(block)"
            :playlist-url="resolveVideoPlaylistUrl(block)"
            :poster-url="resolveVideoPosterUrl(block)"
            :controls-color="resolveVideoControlColor(block)"
            :source-key="resolveVideoSourceKey(block)"
            :border-radius="resolveVideoBorderRadius(block)"
            class="w-full h-full"
          />
          <video
            v-else-if="resolveVideoDirectUrl(block)"
            :src="resolveVideoDirectUrl(block)"
            class="w-full h-full object-cover bg-black"
            controls
            playsinline
            preload="metadata"
          />
          <img
            v-else-if="resolveVideoPosterUrl(block)"
            :src="resolveVideoPosterUrl(block)"
            alt="Video poster"
            class="w-full h-full object-cover"
          >
          <div
            v-else
            class="w-full h-full flex items-center justify-center text-zinc-500 text-sm"
          >
            Upload video in settings
          </div>
        </div>
      </div>

      <div
        v-else-if="block.type === 'mp3'"
        class="rounded-md bg-white p-6"
      >
        <div class="mx-auto w-full max-w-[540px] rounded-xl border border-zinc-200 bg-zinc-50 p-4 text-center">
          <div class="text-sm font-medium text-zinc-700">
            MP3 element
          </div>
          <div class="mt-1 text-xs text-zinc-500">
            Add settings support in next pass.
          </div>
        </div>
      </div>

      <div
        v-else-if="block.type === 'embed'"
        class="rounded-md bg-white p-6"
      >
        <div class="mx-auto w-full max-w-[860px] rounded-xl border border-dashed border-zinc-300 bg-zinc-50 p-6 text-center">
          <div class="text-sm font-medium text-zinc-700">
            Embed element
          </div>
          <div class="mt-1 text-xs text-zinc-500">
            Add settings support in next pass.
          </div>
        </div>
      </div>

      <!-- fallback -->
      <div
        v-else
        class="rounded-md p-8 bg-white"
      >
        <div class="text-sm text-zinc-500">
          Unknown block type: <span class="font-mono">{{ block.type }}</span>
        </div>
      </div>

      <div
        class="absolute left-1/2 z-[140] -translate-x-1/2 transition-opacity duration-150"
        :class="[
          'bottom-0 translate-y-1/2',
          getBlockChromeVisibilityClasses(i, block),
        ]"
      >
        <button
          type="button"
          class="inline-flex h-8 w-8 items-center justify-center rounded-full text-white border-2 shadow-md"
          :class="getBlockAddButtonClasses(block)"
          @click.stop="openAddAfterPicker(i, $event)"
        >
          <PlusIcon class="w-4 h-4" />
        </button>
      </div>
    </section>
    <div
      v-if="shouldShowDropIndicator((props.doc.sections || []).length)"
      class="h-[3px] rounded-full bg-[#ff009dce]"
    />
  </div>
</template>


<script setup>
import {
  TrashIcon,
  DocumentDuplicateIcon,
  Bars3Icon,
  Cog6ToothIcon,
  PlusIcon,
  EyeSlashIcon,
  CheckBadgeIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/vue/24/outline'
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/vue/24/solid'
import { nextTick, watch, ref, computed } from 'vue'
import loginBgDefault from '@/assets/media/images/bg_hold_reg.gif'
import loginLeftLogoDefault from '@/assets/media/images/platforma-logo-white.svg'
import loginRightLogoDefault from '@/assets/media/images/platforma-logo-in-color.svg'
import onboardingLogoDefault from '@/assets/media/images/platforma-creator-icons-onboarding-page.png'
import homeBusinessGroupDefault from '@/assets/media/images/business-group.jpg'
import homeBusinessmanDefault from '@/assets/media/images/platforma-businessman-rv.jpeg'
import pageTemplateCourseDefault from '@/assets/media/images/online-learning-courses-video-thumbnail.jpg'
import pageTemplateWebinarDefault from '@/assets/media/images/webinar.png'
import pageTemplateSkincareDefault from '@/assets/media/images/skin-care-2-default.png'
import pageTemplateBusinessHeroDefault from '@/assets/media/images/business-group-hero.jpeg'
import pageTemplateBusinessHeroAltDefault from '@/assets/media/images/business-group-hero2.png'
import pageTemplateBusinessPortraitDefault from '@/assets/media/images/businessman2.jpg'
import pageTemplateCourseDashboardDefault from '@/assets/media/images/online-course.png'
import pageTemplateSkincareAltDefault from '@/assets/media/images/skin-care-1-default.png'
import cartTemplateFounderStudio from '@/assets/media/images/cart-templates/founder-studio.png'
import cartTemplateFitnessFocus from '@/assets/media/images/cart-templates/fitness-focus.png'
import cartTemplateBotanicalLuxe from '@/assets/media/images/cart-templates/botanical-luxe.png'
import pageImagePlaceholder from '@/assets/media/images/business-group-hero.jpeg'
import PageEditorVideoPlayer from '@/Components/PageEditorVideoPlayer.vue'
import MeetSchedulerCard from '@/Components/MeetSchedulerCard.vue'
import { sanitizeVideoEmbedHtml } from '@/utils/videoEmbedSanitizer'

/* eslint-env vue/setup-compiler-macros */
const props = defineProps({
  doc: { type: Object, required: true },
  pageSlug: { type: String, default: '' },
  selectedIndex: { type: Number, default: -1 },
  viewportMode: { type: String, default: 'desktop' },
  defaultVideoUrl: { type: String, default: '' },
  selectedRowContext: { type: Object, default: () => ({ sectionIndex: -1, rowIndex: -1 }) },
  selectedColumnContext: { type: Object, default: () => ({ sectionIndex: -1, rowIndex: -1, columnIndex: -1, nestedRowElementIndex: -1 }) },
  selectedElementContext: { type: Object, default: () => ({ sectionIndex: -1, rowIndex: -1, elementIndex: -1, nestedRowElementIndex: -1 }) },
  systemElementTypes: { type: Array, default: () => [] },
  hiddenElementTypes: { type: Array, default: () => [] },
  meetSchedulerSettings: { type: Object, default: () => ({}) },
})
const emit = defineEmits([
  'select',
  'select-row',
  'select-row-column',
  'select-row-element',
  'clear-selection',
  'add-after',
  'add-row-after',
  'add-row-element-after',
  'add-end',
  'delete',
  'delete-row',
  'move-row-column',
  'delete-row-column',
  'delete-row-element',
  'duplicate',
  'duplicate-row',
  'duplicate-row-element',
  'reorder',
  'reorder-row',
  'reorder-row-element',
  'open-settings-menu',
  'insert-from-drop',
  'insert-row',
  'insert-row-element',
  'nest-row-into-row',
])
const normalizeApiBaseUrl = (value) => {
  const raw = String(value ?? '').trim()
  if (!raw || raw.toLowerCase() === 'undefined' || raw.toLowerCase() === 'null') return ''
  return raw.replace(/\/+$/, '')
}
const apiProxyUrl = normalizeApiBaseUrl(process.env.VUE_APP_API_PROXY_URL)
const meetSchedulerSettings = computed(() => props.meetSchedulerSettings || {})
const meetSchedulerLogoUrl = computed(() => String(meetSchedulerSettings.value?.logoUrl || ''))
const MEMBER_CREATE_FREE_ACCOUNT_FORM_TYPE = 'member-create-free-account-form'
const normalizeViewportMode = (value) => {
  const normalized = String(value || '').trim().toLowerCase()
  if (normalized === 'mobile' || normalized === 'tablet' || normalized === 'desktop') return normalized
  return 'desktop'
}
const normalizeNodeVisibility = (value) => {
  const source = value && typeof value === 'object' ? value : {}
  return {
    mobile: source.mobile !== false,
    tablet: source.tablet !== false,
    desktop: source.desktop !== false,
  }
}
const currentViewportMode = computed(() => normalizeViewportMode(props.viewportMode))
const systemElementTypeSet = computed(() => new Set(
  (Array.isArray(props.systemElementTypes) ? props.systemElementTypes : [])
    .map((type) => String(type || '').trim().toLowerCase())
    .filter(Boolean)
))
const hiddenElementTypeSet = computed(() => new Set(
  (Array.isArray(props.hiddenElementTypes) ? props.hiddenElementTypes : [])
    .map((type) => String(type || '').trim().toLowerCase())
    .filter(Boolean)
))
const isSystemManagedElement = (element) =>
  systemElementTypeSet.value.has(String(element?.type || '').trim().toLowerCase()) || element?.props?.systemManaged === true
const isElementTypeHidden = (element) =>
  hiddenElementTypeSet.value.has(String(element?.type || '').trim().toLowerCase())
const isNodeVisibleForCurrentViewport = (node) => {
  if (!node || typeof node !== 'object') return true
  const visibility = normalizeNodeVisibility(node.visibility)
  const viewport = currentViewportMode.value
  if (viewport === 'mobile') return visibility.mobile
  if (viewport === 'tablet') return visibility.tablet
  return visibility.desktop
}
const normalizeMemberPageSlug = (value) => {
  const raw = String(value || '').trim()
  if (!raw || raw === '/') return '/'
  return `/${raw.replace(/^\/+/, '').replace(/\/+$/, '').toLowerCase()}`
}
const isJoinPageContext = computed(() => normalizeMemberPageSlug(props.pageSlug) === '/join')
const isCreateFreeAccountFormElement = (element) =>
  String(element?.type || '').trim().toLowerCase() === MEMBER_CREATE_FREE_ACCOUNT_FORM_TYPE
const isMemberAuthFormElement = (element) => {
  const normalized = String(element?.type || '').trim().toLowerCase()
  return normalized === 'member-login-form' || normalized === MEMBER_CREATE_FREE_ACCOUNT_FORM_TYPE
}

function onCanvasBackgroundClick() {
  commitActiveRowElementTextEdit()
  hoveredSectionIndex.value = -1
  hoveredRowContext.value = { sectionIndex: -1, rowIndex: -1 }
  hoveredColumnContext.value = { sectionIndex: -1, rowIndex: -1, columnIndex: -1, nestedRowElementIndex: -1 }
  hoveredElementContext.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1, nestedRowElementIndex: -1 }
  emit('clear-selection')
}

/** Keep refs to editable nodes so we can focus them when selected. */
const editableEls = ref([])

/** Assign or clear a ref for index i. */
function setEditableRef(i, el) {
  editableEls.value[i] = el || null
}

/** Initialize editable node once when it mounts or selection switches. */
function initEditable(i, el, initialText) {
  setEditableRef(i, el)
  if (!el) return
  // Only set once when entering edit mode or when content changed externally
  if (el.textContent !== initialText) {
    el.textContent = initialText
  }
}


/** Focus helper: place caret at end for nicer UX. */
function focusEditable(i) {
  const el = editableEls.value[i]
  if (!el) return
  el.focus()
  // place caret at end
  const range = document.createRange()
  range.selectNodeContents(el)
  range.collapse(false)
  const sel = window.getSelection()
  sel.removeAllRanges()
  sel.addRange(range)
}

const textEditingIndex = ref(-1)
const textEditingDraft = ref('')
const textBlockEditorRef = ref(null)

function autosizeTextEditor() {
  nextTick(() => {
    const el = textBlockEditorRef.value
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  })
}

function startTextBlockEdit(index) {
  const idx = Number(index)
  if (!Number.isFinite(idx) || idx < 0) return
  const blk = props.doc?.sections?.[idx]
  if (!blk || String(blk.type || '') !== 'text') return
  emit('select', idx)
  textEditingIndex.value = idx
  textEditingDraft.value = String(blk?.props?.content || '')
  autosizeTextEditor()
}

function onTextBlockDraftInput() {
  autosizeTextEditor()
}

function commitTextBlockEdit(index) {
  const idx = Number(index)
  if (!Number.isFinite(idx) || idx < 0) return
  const blk = props.doc?.sections?.[idx]
  if (!blk) return
  if (!blk.props) blk.props = {}
  blk.props.content = String(textEditingDraft.value || '')
  textEditingIndex.value = -1
}

function cancelTextBlockEdit() {
  textEditingIndex.value = -1
}

function onHeaderKeydown(e, i) {
  // SHIFT+Enter → allow newline
  if (e.key === 'Enter' && e.shiftKey) {
    return
  }
  // Enter alone → finish edit
  if (e.key === 'Enter') {
    e.preventDefault()
    const el = editableEls.value[i]
    if (el) el.blur()
  }
}

/** When selection changes, focus the active editable node. */
watch(
  () => props.selectedIndex,
  async (i) => {
    if (i == null || i < 0) return
    if (textEditingIndex.value > -1 && Number(i) !== Number(textEditingIndex.value)) {
      commitTextBlockEdit(textEditingIndex.value)
    }
    await nextTick()
    focusEditable(i)
  }
)

// --- Caret helpers for contenteditable ---
function getCaretOffset(el) {
  const sel = window.getSelection()
  if (!sel || sel.rangeCount === 0) return { start: 0, end: 0 }
  const range = sel.getRangeAt(0)

  const preRange = document.createRange()
  preRange.selectNodeContents(el)
  preRange.setEnd(range.startContainer, range.startOffset)
  const start = preRange.toString().length

  const preRangeEnd = document.createRange()
  preRangeEnd.selectNodeContents(el)
  preRangeEnd.setEnd(range.endContainer, range.endOffset)
  const end = preRangeEnd.toString().length

  return { start, end }
}

function setCaretOffset(el, start, end = start) {
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null)
  let node = walker.nextNode()
  let s = start
  let e = end

  const range = document.createRange()

  // start
  while (node) {
    const len = node.nodeValue.length
    if (s <= len) {
      range.setStart(node, s)
      break
    }
    s -= len
    node = walker.nextNode()
  }
  if (!node) range.setStart(el, el.childNodes.length)

  // end
  node = el
  walker.currentNode = el
  node = walker.nextNode()
  while (node) {
    const len = node.nodeValue.length
    if (e <= len) {
      range.setEnd(node, e)
      break
    }
    e -= len
    node = walker.nextNode()
  }
  if (!node) range.setEnd(el, el.childNodes.length)

  const sel = window.getSelection()
  sel.removeAllRanges()
  sel.addRange(range)
}

let isComposing = false

/** Apply contenteditable text into the block props (caret-stable). */
function onEditableInput(e, i, key, singleLine = false) {
  const el = e?.target
  if (!el) return
  if (isComposing) return

  // 1) Save caret before reactive update
  const { start, end } = getCaretOffset(el)

  // 2) Read value; keep newlines unless singleLine=true
  let v = (el.innerText ?? '').replace(/\u00A0/g, ' ')
  if (singleLine) v = v.replace(/\n/g, ' ')

  // 3) Sync into doc
  const blk = props.doc?.sections?.[i]
  if (!blk) return
  if (!blk.props) blk.props = {}
  blk.props[key] = v

  // 4) Restore caret after Vue patch
  nextTick(() => {
    const cur = editableEls.value[i]
    if (cur) setCaretOffset(cur, start, end)
  })
}

function onCompositionStart() { isComposing = true }
function onCompositionEnd(e) {
  isComposing = false
  // Resync once composition ends, preserving newlines
  onEditableInput(e, Number(e?.target?.dataset?.index), 'text', false)
}

const PAGE_BLOCK_INSERT_MIME = 'application/x-platforma-page-block-insert'
const PAGE_BLOCK_REORDER_MIME = 'application/x-platforma-page-block-reorder'
const PAGE_ROW_INSERT_MIME = 'application/x-platforma-row-insert'
const PAGE_ROW_REORDER_MIME = 'application/x-platforma-row-reorder'
const PAGE_ROW_ELEMENT_INSERT_MIME = 'application/x-platforma-row-element-insert'
const PAGE_ROW_ELEMENT_REORDER_MIME = 'application/x-platforma-row-element-reorder'
const PAGE_ROW_INSERT_TEXT_PREFIX = 'platforma-row-insert:'
const PAGE_ROW_REORDER_TEXT_PREFIX = 'platforma-row-reorder:'
const PAGE_ROW_ELEMENT_INSERT_TEXT_PREFIX = 'platforma-row-element-insert:'
const PAGE_ROW_ELEMENT_REORDER_TEXT_PREFIX = 'platforma-row-element-reorder:'
const dragDropIndex = ref(null)
const dragActive = ref(false)
const hoveredSectionIndex = ref(-1)
const hoveredRowContext = ref({ sectionIndex: -1, rowIndex: -1 })
const hoveredColumnContext = ref({ sectionIndex: -1, rowIndex: -1, columnIndex: -1, nestedRowElementIndex: -1 })
const hoveredElementContext = ref({ sectionIndex: -1, rowIndex: -1, elementIndex: -1, nestedRowElementIndex: -1 })
const rowElementTextEditingKey = ref('')
const rowElementTextDrafts = ref({})
const rowElementTextEditorRefs = Object.create(null)
const rowDragDropTarget = ref({ sectionIndex: -1, rowIndex: -1 })
const rowDragActive = ref(false)
const rowElementDragDropTarget = ref({ sectionIndex: -1, rowIndex: -1, elementIndex: -1, columnIndex: 0, nestedRowElementIndex: -1 })
const rowElementDragActive = ref(false)
const DEFAULT_BUTTON_TEXT = 'Button Element'
const DEFAULT_BUTTON_BACKGROUND_COLOR = '#2D8BFF'
const DEFAULT_BUTTON_TEXT_COLOR = '#FFFFFF'
const DEFAULT_BUTTON_TEXT_SIZE = 100
const DEFAULT_BUTTON_FONT_SIZE = 18
const DEFAULT_BUTTON_WIDTH = 36
const DEFAULT_BUTTON_PADDING_X = 24
const DEFAULT_BUTTON_PADDING_Y = 14

const normalizeTextAlign = (value, fallback = 'center') => {
  const v = String(value || '').trim().toLowerCase()
  if (v === 'left' || v === 'center' || v === 'right') return v
  return fallback
}
const normalizeButtonTextSize = (value) => {
  const num = Number(value)
  if (!Number.isFinite(num)) return DEFAULT_BUTTON_TEXT_SIZE
  return Math.min(160, Math.max(60, Math.round(num)))
}
const normalizeButtonFontSize = (value) => {
  const num = Number(value)
  if (!Number.isFinite(num)) return DEFAULT_BUTTON_FONT_SIZE
  return Math.min(56, Math.max(10, Math.round(num)))
}
const normalizeButtonWidth = (value) => {
  const num = Number(value)
  if (!Number.isFinite(num)) return DEFAULT_BUTTON_WIDTH
  return Math.min(100, Math.max(20, Math.round(num)))
}
const normalizeButtonPaddingX = (value) => {
  const num = Number(value)
  if (!Number.isFinite(num)) return DEFAULT_BUTTON_PADDING_X
  return Math.min(80, Math.max(4, Math.round(num)))
}
const normalizeButtonPaddingY = (value) => {
  const num = Number(value)
  if (!Number.isFinite(num)) return DEFAULT_BUTTON_PADDING_Y
  return Math.min(56, Math.max(4, Math.round(num)))
}
const normalizeHexColor = (value, fallback = '#18181B') => {
  const raw = String(value || '').trim()
  const withHash = raw.startsWith('#') ? raw : `#${raw}`
  if (/^#[0-9a-fA-F]{6}$/.test(withHash)) return withHash
  return fallback
}
const getLoginSubmitButtonColor = (element) => normalizeHexColor(
  element?.props?.loginSubmitButtonColor || element?.props?.submitButtonColor,
  '#2D8CFF'
)
const getLoginForgotPasswordColor = (element) => normalizeHexColor(
  element?.props?.loginForgotPasswordColor || element?.props?.forgotPasswordColor,
  '#2D8CFF'
)
const getLoginRememberCheckboxColor = (element) => normalizeHexColor(
  element?.props?.loginRememberCheckboxColor || element?.props?.rememberCheckboxColor,
  '#2D8CFF'
)
const normalizeBackgroundColor = (value, fallback = '#FFFFFF') => {
  const raw = String(value || '').trim()
  if (!raw) return fallback
  if (raw.toLowerCase() === 'transparent') return 'transparent'
  const withHash = raw.startsWith('#') ? raw : `#${raw}`
  if (/^#[0-9a-fA-F]{6}$/.test(withHash)) return withHash
  const rgbMatch = raw.match(/^rgb\(\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*\)$/i)
  if (rgbMatch) {
    const r = Math.max(0, Math.min(255, Number(rgbMatch[1])))
    const g = Math.max(0, Math.min(255, Number(rgbMatch[2])))
    const b = Math.max(0, Math.min(255, Number(rgbMatch[3])))
    return `rgb(${r}, ${g}, ${b})`
  }
  const rgbaMatch = raw.match(/^rgba\(\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*,\s*([01](?:\.\d+)?)\s*\)$/i)
  if (rgbaMatch) {
    const r = Math.max(0, Math.min(255, Number(rgbaMatch[1])))
    const g = Math.max(0, Math.min(255, Number(rgbaMatch[2])))
    const b = Math.max(0, Math.min(255, Number(rgbaMatch[3])))
    const a = Math.max(0, Math.min(1, Number(rgbaMatch[4])))
    return `rgba(${r}, ${g}, ${b}, ${Number(a.toFixed(2))})`
  }
  return fallback
}
const normalizeHeightUnit = (value, fallback = 'px') => {
  const unit = String(value || '').trim().toLowerCase()
  if (unit === 'vh') return 'vh'
  return fallback === 'vh' ? 'vh' : 'px'
}
const getHeightRangeByUnit = (unit) => {
  const normalizedUnit = normalizeHeightUnit(unit, 'px')
  if (normalizedUnit === 'vh') return { min: 5, max: 300 }
  return { min: 50, max: 2400 }
}
const getRowHeightRangeByUnit = (unit) => {
  const normalizedUnit = normalizeHeightUnit(unit, 'px')
  if (normalizedUnit === 'vh') return { min: 0, max: 300 }
  return { min: 0, max: 2400 }
}
const getColumnHeightRangeByUnit = (unit) => {
  const normalizedUnit = normalizeHeightUnit(unit, 'px')
  if (normalizedUnit === 'vh') return { min: 0, max: 300 }
  return { min: 0, max: 2400 }
}
const getElementHeightRangeByType = (elementType, unit) => {
  const normalizedUnit = normalizeHeightUnit(unit, 'px')
  if (normalizedUnit === 'vh') return { min: 0, max: 300 }
  const normalizedType = String(elementType || '').trim().toLowerCase()
  if (normalizedType === 'text') return { min: 24, max: 2400 }
  return { min: 50, max: 2400 }
}
const toHeightCssValue = (value, unit, fallback = 120) => {
  const normalizedUnit = normalizeHeightUnit(unit, 'px')
  const range = getHeightRangeByUnit(normalizedUnit)
  const parsed = Number(value)
  const normalizedValue = Number.isFinite(parsed) ? Math.round(parsed) : fallback
  const clamped = Math.max(range.min, Math.min(range.max, normalizedValue))
  return `${clamped}${normalizedUnit}`
}
const toRowHeightCssValue = (value, unit, fallback = 120) => {
  const normalizedUnit = normalizeHeightUnit(unit, 'px')
  const range = getRowHeightRangeByUnit(normalizedUnit)
  const parsed = Number(value)
  const normalizedValue = Number.isFinite(parsed) ? Math.round(parsed) : fallback
  const clamped = Math.max(range.min, Math.min(range.max, normalizedValue))
  return `${clamped}${normalizedUnit}`
}
const toColumnHeightCssValue = (value, unit, fallback = 120) => {
  const normalizedUnit = normalizeHeightUnit(unit, 'px')
  const range = getColumnHeightRangeByUnit(normalizedUnit)
  const parsed = Number(value)
  const normalizedValue = Number.isFinite(parsed) ? Math.round(parsed) : fallback
  const clamped = Math.max(range.min, Math.min(range.max, normalizedValue))
  return `${clamped}${normalizedUnit}`
}
const toElementHeightCssValue = (value, unit, fallback = 120, elementType = '') => {
  const normalizedUnit = normalizeHeightUnit(unit, 'px')
  const range = getElementHeightRangeByType(elementType, normalizedUnit)
  const parsed = Number(value)
  const normalizedValue = Number.isFinite(parsed) ? Math.round(parsed) : fallback
  const clamped = Math.max(range.min, Math.min(range.max, normalizedValue))
  return `${clamped}${normalizedUnit}`
}

function normalizeButtonTextValue(value) {
  return String(value || '').replace(/\u00A0/g, ' ').replace(/\n/g, '').trim() || DEFAULT_BUTTON_TEXT
}

function getButtonElementText(buttonProps) {
  return String(buttonProps?.buttonText ?? buttonProps?.button_text ?? buttonProps?.content ?? DEFAULT_BUTTON_TEXT)
}

function getRowElementRecord(sectionIndex, rowIndex, elementIndex, nestedRowElementIndex = -1) {
  const section = props.doc?.sections?.[sectionIndex]
  if (!section || String(section?.type || '') !== 'section') return null
  const row = getSectionRows(section)[rowIndex]
  if (!row || String(row?.type || '') !== 'row') return null
  if (Number.isFinite(Number(nestedRowElementIndex)) && Number(nestedRowElementIndex) >= 0) {
    const nestedContainer = getRowElements(row)[Number(nestedRowElementIndex)]
    if (!nestedContainer || String(nestedContainer?.type || '') !== 'row') return null
    const nestedRowElements = getRowElements(nestedContainer)
    const element = nestedRowElements[elementIndex]
    if (!element) return null
    return { section, row, targetRow: nestedContainer, element }
  }
  const element = getRowElements(row)[elementIndex]
  if (!element) return null
  return { section, row, targetRow: row, element }
}

function setButtonBlockText(index, value) {
  const blk = props.doc?.sections?.[index]
  if (!blk) return
  if (!blk.props) blk.props = {}
  const nextText = normalizeButtonTextValue(value)
  blk.props.buttonText = nextText
  blk.props.button_text = nextText
  blk.props.content = nextText
}

function onButtonBlockTextClick(index) {
  emit('select', index)
}

function onButtonBlockTextFocus(index) {
  emit('select', index)
}

function onButtonBlockTextBlur(index, event) {
  setButtonBlockText(index, event?.target?.innerText)
}

function onButtonBlockTextEnter(index, event) {
  event?.preventDefault?.()
  setButtonBlockText(index, event?.target?.innerText)
  event?.target?.blur?.()
}

function setRowElementButtonText(sectionIndex, rowIndex, elementIndex, value, nestedRowElementIndex = -1) {
  const record = getRowElementRecord(sectionIndex, rowIndex, elementIndex, nestedRowElementIndex)
  if (!record?.element) return
  const element = record.element
  if (!element.props) element.props = {}
  const nextText = normalizeButtonTextValue(value)
  element.props.buttonText = nextText
  element.props.button_text = nextText
  element.props.content = nextText
}

function onRowElementButtonTextClick(sectionIndex, rowIndex, elementIndex, nestedRowElementIndex = -1) {
  onSelectRowElement(sectionIndex, rowIndex, elementIndex, nestedRowElementIndex)
}

function onRowElementButtonTextFocus(sectionIndex, rowIndex, elementIndex, nestedRowElementIndex = -1) {
  onSelectRowElement(sectionIndex, rowIndex, elementIndex, nestedRowElementIndex)
}

function onRowElementButtonTextBlur(sectionIndex, rowIndex, elementIndex, event, nestedRowElementIndex = -1) {
  setRowElementButtonText(sectionIndex, rowIndex, elementIndex, event?.target?.innerText, nestedRowElementIndex)
}

function onRowElementButtonTextEnter(sectionIndex, rowIndex, elementIndex, event, nestedRowElementIndex = -1) {
  event?.preventDefault?.()
  setRowElementButtonText(sectionIndex, rowIndex, elementIndex, event?.target?.innerText, nestedRowElementIndex)
  event?.target?.blur?.()
}

function getRowElementTextKey(sectionIndex, rowIndex, elementIndex, nestedRowElementIndex = -1) {
  return `${Number(sectionIndex)}:${Number(rowIndex)}:${Number(nestedRowElementIndex)}:${Number(elementIndex)}`
}

function setRowElementTextEditorRef(sectionIndex, rowIndex, elementIndex, el, nestedRowElementIndex = -1) {
  const key = getRowElementTextKey(sectionIndex, rowIndex, elementIndex, nestedRowElementIndex)
  if (el) {
    rowElementTextEditorRefs[key] = el
    return
  }
  if (!Object.prototype.hasOwnProperty.call(rowElementTextEditorRefs, key)) return
  delete rowElementTextEditorRefs[key]
}

function isRowElementTextEditing(sectionIndex, rowIndex, elementIndex, nestedRowElementIndex = -1) {
  return rowElementTextEditingKey.value === getRowElementTextKey(sectionIndex, rowIndex, elementIndex, nestedRowElementIndex)
}

function getRowElementTextDraft(sectionIndex, rowIndex, elementIndex, element, nestedRowElementIndex = -1) {
  const key = getRowElementTextKey(sectionIndex, rowIndex, elementIndex, nestedRowElementIndex)
  if (Object.prototype.hasOwnProperty.call(rowElementTextDrafts.value, key)) {
    return rowElementTextDrafts.value[key]
  }
  return String(element?.props?.content || '')
}

function onRowElementTextDraftInput(sectionIndex, rowIndex, elementIndex, event, nestedRowElementIndex = -1) {
  const key = getRowElementTextKey(sectionIndex, rowIndex, elementIndex, nestedRowElementIndex)
  const nextValue = String(event?.target?.value || '').replace(/\u00A0/g, ' ')
  rowElementTextDrafts.value = { ...rowElementTextDrafts.value, [key]: nextValue }
  autosizeRowElementTextEditor(key)
}

function autosizeRowElementTextEditor(key) {
  nextTick(() => {
    const textarea = rowElementTextEditorRefs[key]
    if (!textarea) return
    textarea.style.height = 'auto'
    textarea.style.height = `${textarea.scrollHeight}px`
  })
}

function startRowElementTextEdit(sectionIndex, rowIndex, elementIndex, nestedRowElementIndex = -1) {
  const record = getRowElementRecord(sectionIndex, rowIndex, elementIndex, nestedRowElementIndex)
  if (!record?.element || String(record.element?.type || '') !== 'text') return
  const element = record.element
  const key = getRowElementTextKey(sectionIndex, rowIndex, elementIndex, nestedRowElementIndex)
  rowElementTextEditingKey.value = key
  rowElementTextDrafts.value = {
    ...rowElementTextDrafts.value,
    [key]: String(element?.props?.content || ''),
  }
  onSelectRowElement(sectionIndex, rowIndex, elementIndex, nestedRowElementIndex)
  nextTick(() => {
    const textarea = rowElementTextEditorRefs[key]
    textarea?.focus?.()
    autosizeRowElementTextEditor(key)
  })
}

function commitRowElementTextEdit(sectionIndex, rowIndex, elementIndex, nestedRowElementIndex = -1) {
  const record = getRowElementRecord(sectionIndex, rowIndex, elementIndex, nestedRowElementIndex)
  if (!record?.element || String(record.element?.type || '') !== 'text') return
  const element = record.element
  const key = getRowElementTextKey(sectionIndex, rowIndex, elementIndex, nestedRowElementIndex)
  const nextValue = String(rowElementTextDrafts.value[key] ?? element?.props?.content ?? '')
  if (!element.props) element.props = {}
  element.props.content = nextValue
  rowElementTextDrafts.value = { ...rowElementTextDrafts.value, [key]: nextValue }
  if (rowElementTextEditingKey.value === key) rowElementTextEditingKey.value = ''
}

function cancelRowElementTextEdit(sectionIndex, rowIndex, elementIndex, nestedRowElementIndex = -1) {
  const record = getRowElementRecord(sectionIndex, rowIndex, elementIndex, nestedRowElementIndex)
  if (!record?.element || String(record.element?.type || '') !== 'text') return
  const element = record.element
  const key = getRowElementTextKey(sectionIndex, rowIndex, elementIndex, nestedRowElementIndex)
  const existingValue = String(element?.props?.content || '')
  rowElementTextDrafts.value = { ...rowElementTextDrafts.value, [key]: existingValue }
  if (rowElementTextEditingKey.value === key) rowElementTextEditingKey.value = ''
}

function commitActiveRowElementTextEdit() {
  const key = String(rowElementTextEditingKey.value || '')
  if (!key) return
  const parts = key.split(':').map((part) => Number(part))
  const sectionIndex = parts[0]
  const rowIndex = parts[1]
  const nestedRowElementIndex = parts.length >= 4 ? parts[2] : -1
  const elementIndex = parts.length >= 4 ? parts[3] : parts[2]
  if (!Number.isFinite(sectionIndex) || !Number.isFinite(rowIndex) || !Number.isFinite(elementIndex)) {
    rowElementTextEditingKey.value = ''
    return
  }
  commitRowElementTextEdit(sectionIndex, rowIndex, elementIndex, Number.isFinite(nestedRowElementIndex) ? nestedRowElementIndex : -1)
}

function openAddAfterPicker(index, event) {
  const rect = event?.currentTarget?.getBoundingClientRect?.()
  emit('add-after', { index, anchorRect: rect ? { top: rect.top, left: rect.left, width: rect.width, height: rect.height } : null })
}

function openAddEndPicker(event) {
  const rect = event?.currentTarget?.getBoundingClientRect?.()
  emit('add-end', { anchorRect: rect ? { top: rect.top, left: rect.left, width: rect.width, height: rect.height } : null })
}

function getSectionRows(block) {
  const rows = block?.props?.rows
  return Array.isArray(rows) ? rows : []
}

function getVisibleSectionEntries() {
  const sections = Array.isArray(props.doc?.sections) ? props.doc.sections : []
  const entries = []
  sections.forEach((block, index) => {
    if (!isNodeVisibleForCurrentViewport(block)) return
    entries.push({ block, index })
  })
  return entries
}

function getVisibleRowEntries(block) {
  const rows = getSectionRows(block)
  const entries = []
  rows.forEach((row, rowIndex) => {
    if (!isNodeVisibleForCurrentViewport(row)) return
    entries.push({ row, rowIndex })
  })
  return entries
}

function getSectionRingClass(sectionIndex) {
  if (isSectionChromeSuppressed(sectionIndex)) return 'ring-transparent'
  const active = hasAnyHoveredPageNode()
    ? Number(hoveredSectionIndex.value) === Number(sectionIndex)
    : Number(props.selectedIndex) === Number(sectionIndex)
  if (active) {
    return 'ring-[#14b8a6]'
  }
  return 'ring-transparent'
}

function isRowSelected(sectionIndex, rowIndex) {
  return (
    Number(props.selectedRowContext?.sectionIndex) === Number(sectionIndex) &&
    Number(props.selectedRowContext?.rowIndex) === Number(rowIndex)
  )
}

function isRowColumnSelected(sectionIndex, rowIndex, columnIndex, nestedRowElementIndex = -1) {
  return (
    Number(props.selectedColumnContext?.sectionIndex) === Number(sectionIndex) &&
    Number(props.selectedColumnContext?.rowIndex) === Number(rowIndex) &&
    Number(props.selectedColumnContext?.columnIndex) === Number(columnIndex) &&
    Number(props.selectedColumnContext?.nestedRowElementIndex ?? -1) === Number(nestedRowElementIndex)
  )
}

function hasDataTransferType(dt, mimeType) {
  const types = Array.isArray(dt?.types) ? dt.types : Array.from(dt?.types || [])
  return types.includes(mimeType)
}

function readPayloadFromTransfer(dt, mimeType, textPrefix) {
  if (!dt) return null
  const rawCustom = String(dt.getData(mimeType) || '').trim()
  if (rawCustom) {
    try {
      return JSON.parse(rawCustom)
    } catch (_) {
      return null
    }
  }
  const rawPlain = String(dt.getData('text/plain') || '').trim()
  if (!rawPlain.startsWith(textPrefix)) return null
  try {
    return JSON.parse(rawPlain.slice(textPrefix.length))
  } catch (_) {
    return null
  }
}

function onSelectRow(sectionIndex, rowIndex) {
  emit('select-row', { sectionIndex, rowIndex })
}

function onSelectRowColumn(sectionIndex, rowIndex, columnIndex, nestedRowElementIndex = -1) {
  emit('select-row-column', { sectionIndex, rowIndex, columnIndex, nestedRowElementIndex })
}

function onSectionHoverEnter(sectionIndex) {
  hoveredSectionIndex.value = Number(sectionIndex)
}

function onSectionHoverLeave(sectionIndex) {
  if (Number(hoveredSectionIndex.value) === Number(sectionIndex)) {
    hoveredSectionIndex.value = -1
  }
}

function onRowHoverEnter(sectionIndex, rowIndex) {
  hoveredRowContext.value = { sectionIndex, rowIndex }
}

function onRowHoverLeave(sectionIndex, rowIndex) {
  const hoveredSection = Number(hoveredRowContext.value?.sectionIndex)
  const hoveredRow = Number(hoveredRowContext.value?.rowIndex)
  if (hoveredSection === Number(sectionIndex) && hoveredRow === Number(rowIndex)) {
    hoveredRowContext.value = { sectionIndex: -1, rowIndex: -1 }
  }
}

function onRowColumnHoverEnter(sectionIndex, rowIndex, columnIndex, nestedRowElementIndex = -1) {
  hoveredColumnContext.value = { sectionIndex, rowIndex, columnIndex, nestedRowElementIndex }
}

function onRowColumnHoverLeave(sectionIndex, rowIndex, columnIndex, nestedRowElementIndex = -1) {
  const hoveredSection = Number(hoveredColumnContext.value?.sectionIndex)
  const hoveredRow = Number(hoveredColumnContext.value?.rowIndex)
  const hoveredColumn = Number(hoveredColumnContext.value?.columnIndex)
  const hoveredNested = Number(hoveredColumnContext.value?.nestedRowElementIndex ?? -1)
  if (
    hoveredSection === Number(sectionIndex) &&
    hoveredRow === Number(rowIndex) &&
    hoveredColumn === Number(columnIndex) &&
    hoveredNested === Number(nestedRowElementIndex)
  ) {
    hoveredColumnContext.value = { sectionIndex: -1, rowIndex: -1, columnIndex: -1, nestedRowElementIndex: -1 }
  }
}

function onRowElementHoverEnter(sectionIndex, rowIndex, elementIndex, nestedRowElementIndex = -1) {
  hoveredElementContext.value = { sectionIndex, rowIndex, elementIndex, nestedRowElementIndex }
}

function onRowElementHoverLeave(sectionIndex, rowIndex, elementIndex, nestedRowElementIndex = -1) {
  const hoveredSection = Number(hoveredElementContext.value?.sectionIndex)
  const hoveredRow = Number(hoveredElementContext.value?.rowIndex)
  const hoveredElement = Number(hoveredElementContext.value?.elementIndex)
  const hoveredNested = Number(hoveredElementContext.value?.nestedRowElementIndex ?? -1)
  if (
    hoveredSection === Number(sectionIndex) &&
    hoveredRow === Number(rowIndex) &&
    hoveredElement === Number(elementIndex) &&
    hoveredNested === Number(nestedRowElementIndex)
  ) {
    hoveredElementContext.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1, nestedRowElementIndex: -1 }
  }
}

function getRowColumnCount(row) {
  const count = Number(row?.props?.rowColumnCount ?? 1)
  return Math.max(1, Math.min(6, Number.isFinite(count) ? Math.round(count) : 1))
}

function isSectionContextActive(sectionIndex) {
  const target = Number(sectionIndex)
  return (
    Number(props.selectedIndex) === target ||
    Number(props.selectedRowContext?.sectionIndex) === target ||
    Number(props.selectedColumnContext?.sectionIndex) === target ||
    Number(props.selectedElementContext?.sectionIndex) === target
  )
}

function isRowContextActive(sectionIndex, rowIndex) {
  const section = Number(sectionIndex)
  const row = Number(rowIndex)
  return (
    isRowSelected(section, row) ||
    (
      Number(props.selectedColumnContext?.sectionIndex) === section &&
      Number(props.selectedColumnContext?.rowIndex) === row
    ) ||
    (
      Number(props.selectedElementContext?.sectionIndex) === section &&
      Number(props.selectedElementContext?.rowIndex) === row
    )
  )
}

function isRowColumnContextActive(sectionIndex, rowIndex, columnIndex, nestedRowElementIndex = -1) {
  const section = Number(sectionIndex)
  const row = Number(rowIndex)
  const column = Number(columnIndex)
  const nested = Number(nestedRowElementIndex)
  if (isRowColumnSelected(section, row, column, nested)) return true

  const selectedElement = props.selectedElementContext
  if (
    Number(selectedElement?.sectionIndex) !== section ||
    Number(selectedElement?.rowIndex) !== row ||
    Number(selectedElement?.nestedRowElementIndex ?? -1) !== nested
  ) return false

  const selectedRow = getSectionRows(props.doc?.sections?.[section])?.[row]
  const elementContainer = nested >= 0
    ? getRowElements(selectedRow)?.[nested]
    : selectedRow
  const element = getRowElements(elementContainer)?.[Number(selectedElement?.elementIndex)]
  return Number(element?.props?.rowElementColumnIndex ?? 0) === column
}

function getBlockLayerClass(sectionIndex, block) {
  if (String(block?.type || '') !== 'section') {
    return Number(props.selectedIndex) === Number(sectionIndex) ? 'z-[70]' : 'z-0 hover:z-[60]'
  }
  return isSectionContextActive(sectionIndex) ? 'z-[70]' : 'z-0 hover:z-[60]'
}

function getRowLayerClass(sectionIndex, rowIndex) {
  return isRowContextActive(sectionIndex, rowIndex) ? 'z-[80]' : 'z-0 hover:z-[75]'
}

function getRowColumnLayerClass(sectionIndex, rowIndex, columnIndex, nestedRowElementIndex = -1) {
  return isRowColumnContextActive(sectionIndex, rowIndex, columnIndex, nestedRowElementIndex)
    ? 'z-[90]'
    : 'z-0 hover:z-[85]'
}

function getRowElementLayerClass(sectionIndex, rowIndex, elementIndex, nestedRowElementIndex = -1) {
  return isRowElementSelected(sectionIndex, rowIndex, elementIndex, nestedRowElementIndex)
    ? 'z-[110]'
    : 'z-0 hover:z-[100]'
}

function nodeContainsSystemManagedElement(node) {
  if (!node || typeof node !== 'object') return false
  if (isSystemManagedElement(node)) return true
  const rows = Array.isArray(node?.props?.rows) ? node.props.rows : []
  const elements = Array.isArray(node?.props?.elements) ? node.props.elements : []
  return [...rows, ...elements].some((child) => nodeContainsSystemManagedElement(child))
}

function canDeleteRowColumn(row, columnIndex) {
  if (getRowColumnCount(row) <= 1) return false
  return !getRowElements(row).some((element) => (
    getRowElementColumnIndex(element, row) === Number(columnIndex) &&
    nodeContainsSystemManagedElement(element)
  ))
}

function getRowColumnsGridStyle(row) {
  const columnCount = getRowColumnCount(row)
  const styles = Array.isArray(row?.props?.rowColumnStyles) ? row.props.rowColumnStyles : []
  const gutterRaw = Number(row?.props?.rowColumnGutter ?? 12)
  const gutter = Number.isFinite(gutterRaw) ? Math.max(0, Math.min(120, Math.round(gutterRaw))) : 12
  const defaultWidth = 100 / Math.max(1, columnCount)
  const widthWeights = Array.from({ length: columnCount }, (_, index) => {
    const styleEntry = styles[index] && typeof styles[index] === 'object' ? styles[index] : {}
    const widthRaw = Number(styleEntry?.widthPercent)
    if (!Number.isFinite(widthRaw)) return defaultWidth
    return Math.max(5, Math.min(100, widthRaw))
  })
  if (row?.props?.stackColumns === true && columnCount > 1) {
    return {
      gridTemplateColumns: 'minmax(0, 1fr)',
      columnGap: '0px',
      rowGap: `${gutter}px`,
    }
  }
  return {
    gridTemplateColumns: widthWeights.map((weight) => `minmax(0, ${Number(weight.toFixed(4))}fr)`).join(' '),
    columnGap: `${gutter}px`,
  }
}

function getRowElements(row) {
  const elements = row?.props?.elements
  return Array.isArray(elements) ? elements : []
}

function getRowElementColumnIndex(element, row) {
  const rowColumns = getRowColumnCount(row)
  const raw = Number(element?.props?.rowElementColumnIndex ?? 0)
  return Math.max(0, Math.min(rowColumns - 1, Number.isFinite(raw) ? Math.round(raw) : 0))
}

function getRowElementEntriesForColumn(row, columnIndex) {
  const entries = []
  getRowElements(row).forEach((element, elementIndex) => {
    if (!isNodeVisibleForCurrentViewport(element)) return
    if (isElementTypeHidden(element)) return
    if (getRowElementColumnIndex(element, row) === Number(columnIndex)) {
      entries.push({ element, elementIndex })
    }
  })
  return entries
}

function hasRowElementsInColumn(row, columnIndex) {
  return getRowElementEntriesForColumn(row, columnIndex).length > 0
}

function isRowElementSelected(sectionIndex, rowIndex, elementIndex, nestedRowElementIndex = -1) {
  return (
    Number(props.selectedElementContext?.sectionIndex) === Number(sectionIndex) &&
    Number(props.selectedElementContext?.rowIndex) === Number(rowIndex) &&
    Number(props.selectedElementContext?.elementIndex) === Number(elementIndex) &&
    Number(props.selectedElementContext?.nestedRowElementIndex ?? -1) === Number(nestedRowElementIndex)
  )
}

function onSelectRowElement(sectionIndex, rowIndex, elementIndex, nestedRowElementIndex = -1) {
  const selectedKey = getRowElementTextKey(sectionIndex, rowIndex, elementIndex, nestedRowElementIndex)
  if (rowElementTextEditingKey.value && rowElementTextEditingKey.value !== selectedKey) {
    commitActiveRowElementTextEdit()
  }
  hoveredElementContext.value = { sectionIndex, rowIndex, elementIndex, nestedRowElementIndex }
  emit('select-row-element', { sectionIndex, rowIndex, elementIndex, nestedRowElementIndex })
}

function isCheckoutTemplateRow(row) {
  return Array.isArray(row?.props?.elements) && row.props.elements.some((element) => String(element?.type || '').trim().toLowerCase() === 'checkout-order-form')
}

const CHECKOUT_TEMPLATE_WIDTHS = Object.freeze({
  spotlight: 1152,
  shopify: 1060,
  clean: 860,
  compact: 760,
  two_step: 1152,
  editorial: 1152,
  founder: 1152,
  fitness: 1152,
  botanical: 1152,
  tai_lopez: 980,
})

function getCheckoutTemplateName(row = null) {
  const rowTemplate = String(row?.props?.checkoutTemplate || '').trim().toLowerCase()
  if (Object.prototype.hasOwnProperty.call(CHECKOUT_TEMPLATE_WIDTHS, rowTemplate)) return rowTemplate
  const documentTemplate = String(props.doc?.template || '').trim().toLowerCase()
  if (Object.prototype.hasOwnProperty.call(CHECKOUT_TEMPLATE_WIDTHS, documentTemplate)) return documentTemplate
  const sections = Array.isArray(props.doc?.sections) ? props.doc.sections : []
  for (const section of sections) {
    const rows = Array.isArray(section?.props?.rows) ? section.props.rows : []
    const checkoutRow = rows.find((candidate) => isCheckoutTemplateRow(candidate))
    const candidateTemplate = String(checkoutRow?.props?.checkoutTemplate || '').trim().toLowerCase()
    if (Object.prototype.hasOwnProperty.call(CHECKOUT_TEMPLATE_WIDTHS, candidateTemplate)) return candidateTemplate
  }
  return ''
}

function isCheckoutBrandingRow(row) {
  if (!getCheckoutTemplateName()) return false
  if (isCheckoutTemplateRow(row)) return false
  const elements = Array.isArray(row?.props?.elements) ? row.props.elements : []
  const height = Number(row?.props?.rowHeight)
  return Number(row?.props?.rowColumnCount) === 2 && elements.length > 0 && (!Number.isFinite(height) || height <= 96)
}

function getRowBlockStyle(row) {
  const checkoutTemplateRow = isCheckoutTemplateRow(row)
  const checkoutTemplate = getCheckoutTemplateName(row)
  const checkoutTemplateContext = Boolean(checkoutTemplate)
  const legacyCheckoutRow = checkoutTemplateRow && checkoutTemplateContext && !row?.props?.checkoutTemplate
  const checkoutBrandingRow = isCheckoutBrandingRow(row)
  const widthPercentRaw = Number(row?.props?.rowWidthPercent ?? 100)
  const widthPercent = Number.isFinite(widthPercentRaw) ? Math.max(10, Math.min(100, Math.round(widthPercentRaw))) : 100
  const paddingXRaw = Number(row?.props?.rowPaddingX ?? row?.props?.rowPadding ?? 16)
  const paddingYRaw = Number(row?.props?.rowPaddingY ?? row?.props?.rowPadding ?? 16)
  const marginRaw = Number(row?.props?.rowMargin ?? 8)
  const paddingX = legacyCheckoutRow ? 0 : (Number.isFinite(paddingXRaw) ? Math.max(0, Math.min(240, Math.round(paddingXRaw))) : 16)
  const paddingY = legacyCheckoutRow ? 0 : (Number.isFinite(paddingYRaw) ? Math.max(0, Math.min(240, Math.round(paddingYRaw))) : 16)
  const margin = Number.isFinite(marginRaw) ? Math.max(0, Math.min(240, Math.round(marginRaw))) : 8
  const alignRaw = String(row?.props?.rowAlign || ((checkoutTemplateRow && checkoutTemplateContext) || checkoutBrandingRow ? 'center' : 'left')).toLowerCase()
  const align = ['left', 'center', 'right'].includes(alignRaw) ? alignRaw : 'left'
  const maxWidthValue = row?.props?.rowMaxWidthPx
  const maxWidthRaw = Number(maxWidthValue)
  const hasExplicitMaxWidth = maxWidthValue !== null && maxWidthValue !== undefined && maxWidthValue !== '' && Number.isFinite(maxWidthRaw)
  const maxWidth = hasExplicitMaxWidth
    ? Math.max(320, Math.min(1920, Math.round(maxWidthRaw)))
    : (checkoutTemplateRow && checkoutTemplateContext ? (CHECKOUT_TEMPLATE_WIDTHS[checkoutTemplate] || 1152) : (checkoutBrandingRow ? 1152 : null))
  const isFullWidth = widthPercent >= 100 && !maxWidth

  let marginLeft = '0px'
  let marginRight = 'auto'
  if (isFullWidth) {
    marginLeft = '0px'
    marginRight = '0px'
  } else if (align === 'center') {
    marginLeft = 'auto'
    marginRight = 'auto'
  } else if (align === 'right') {
    marginLeft = 'auto'
    marginRight = '0px'
  }

  const fileId = sanitizeFileId(row?.props?.rowBackgroundImageFileId || row?.props?.row_background_image_file_id)
  const rawUrl = resolveLegacyTemplateAssetUrl(resolveStoredImageUrl(row?.props?.rowBackgroundImageUrl || row?.props?.row_background_image_url))
  const backgroundImageUrl = fileId ? buildImageUrlFromFileId(fileId) : rawUrl
  const backgroundSize = String(row?.props?.rowBackgroundSize || 'cover').trim().toLowerCase() === 'contain' ? 'contain' : 'cover'
  const backgroundPosition = String(row?.props?.rowBackgroundPosition || 'center').trim() || 'center'
  const backgroundRepeat = String(row?.props?.rowBackgroundRepeat || 'no-repeat').trim().toLowerCase() === 'repeat' ? 'repeat' : 'no-repeat'
  const borderRadiusRaw = Number(row?.props?.rowBorderRadius)
  const borderRadius = Number.isFinite(borderRadiusRaw) ? Math.max(0, Math.min(120, Math.round(borderRadiusRaw))) : (legacyCheckoutRow ? 30 : 0)
  const borderColor = normalizeBackgroundColor(row?.props?.rowBorderColor, legacyCheckoutRow ? '#FFFFFF' : 'transparent')
  const borderWidthRaw = Number(row?.props?.rowBorderWidth)
  const borderWidth = Number.isFinite(borderWidthRaw) ? Math.max(0, Math.min(8, borderWidthRaw)) : (borderColor === 'transparent' ? 0 : 1)
  const rowShadow = String(row?.props?.rowShadow || (legacyCheckoutRow ? '0 25px 50px -12px rgba(148, 163, 184, 0.5)' : '')).trim()

  return {
    width: `${widthPercent}%`,
    maxWidth: '100%',
    minWidth: '0',
    ...(maxWidth ? { maxWidth: `${maxWidth}px` } : {}),
    minHeight: toRowHeightCssValue(row?.props?.rowHeight, row?.props?.rowHeightUnit, 120),
    paddingLeft: `${paddingX}px`,
    paddingRight: `${paddingX}px`,
    paddingTop: `${paddingY}px`,
    paddingBottom: `${paddingY}px`,
    marginTop: `${margin}px`,
    marginBottom: `${margin}px`,
    marginLeft,
    marginRight,
    boxSizing: 'border-box',
    backgroundColor: normalizeBackgroundColor(row?.props?.rowBackgroundColor, '#FFFFFF'),
    backgroundImage: toCssBackgroundImage(backgroundImageUrl),
    backgroundSize,
    backgroundPosition,
    backgroundRepeat,
    borderRadius: `${borderRadius}px`,
    overflow: row?.props?.rowOverflowHidden === true || legacyCheckoutRow ? 'hidden' : 'visible',
    border: borderColor === 'transparent' || borderWidth <= 0 ? 'none' : `${borderWidth}px solid ${borderColor}`,
    boxShadow: rowShadow || 'none',
  }
}

function getRowElementBlockStyle(element) {
  const widthPercentRaw = Number(element?.props?.elementWidthPercent ?? 100)
  const widthPercent = Number.isFinite(widthPercentRaw) ? Math.max(10, Math.min(100, Math.round(widthPercentRaw))) : 100
  const paddingXRaw = Number(element?.props?.elementPaddingX ?? element?.props?.elementPadding ?? 12)
  const paddingYRaw = Number(element?.props?.elementPaddingY ?? element?.props?.elementPadding ?? 12)
  const marginRaw = Number(element?.props?.elementMargin ?? 0)
  const isCheckoutForm = String(element?.type || '').trim().toLowerCase() === 'checkout-order-form'
  const paddingX = isCheckoutForm ? 0 : (Number.isFinite(paddingXRaw) ? Math.max(0, Math.min(240, Math.round(paddingXRaw))) : 12)
  const paddingY = isCheckoutForm ? 0 : (Number.isFinite(paddingYRaw) ? Math.max(0, Math.min(240, Math.round(paddingYRaw))) : 12)
  const margin = Number.isFinite(marginRaw) ? Math.max(0, Math.min(240, Math.round(marginRaw))) : 0
  const elementType = String(element?.type || '').trim().toLowerCase()
  const rowHeightRaw = Number(element?.props?.rowHeight)
  const alignRaw = String(element?.props?.elementAlign || 'left').toLowerCase()
  const align = ['left', 'center', 'right'].includes(alignRaw) ? alignRaw : 'left'
  const isFullWidth = widthPercent >= 100
  const elementHeightFallback = elementType === 'text'
    ? 28
    : elementType === 'row'
      ? (Number.isFinite(rowHeightRaw) ? rowHeightRaw : 120)
      : 120

  let marginLeft = '0px'
  let marginRight = 'auto'
  if (isFullWidth) {
    marginLeft = '0px'
    marginRight = '0px'
  } else if (align === 'center') {
    marginLeft = 'auto'
    marginRight = 'auto'
  } else if (align === 'right') {
    marginLeft = 'auto'
    marginRight = '0px'
  }

  const storedElementBackground = normalizeBackgroundColor(element?.props?.elementBackgroundColor, 'transparent')
  const buttonBackground = normalizeBackgroundColor(
    element?.props?.buttonBackgroundColor || element?.props?.button_background_color,
    DEFAULT_BUTTON_BACKGROUND_COLOR
  )
  const legacyTemplateButtonChrome = elementType === 'button' && storedElementBackground === buttonBackground

  return {
    width: `${widthPercent}%`,
    maxWidth: '100%',
    minWidth: '0',
    minHeight: toElementHeightCssValue(
      element?.props?.elementHeight,
      element?.props?.elementHeightUnit,
      elementHeightFallback,
      element?.type
    ),
    paddingLeft: `${paddingX}px`,
    paddingRight: `${paddingX}px`,
    paddingTop: `${paddingY}px`,
    paddingBottom: `${paddingY}px`,
    marginTop: `${margin}px`,
    marginBottom: `${margin}px`,
    marginLeft,
    marginRight,
    boxSizing: 'border-box',
    backgroundColor: legacyTemplateButtonChrome ? 'transparent' : storedElementBackground,
    borderTop: element?.props?.topBorderColor
      ? `1px solid ${normalizeBackgroundColor(element.props.topBorderColor, 'transparent')}`
      : 'none',
  }
}

function getRowToolbarClasses(sectionIndex, rowIndex) {
  return ['bottom-full right-0', getRowChromeVisibilityClasses(sectionIndex, rowIndex)]
}

function isRowChromeSuppressed(sectionIndex, rowIndex) {
  const sIdx = Number(sectionIndex)
  const rIdx = Number(rowIndex)
  if (hasAnyHoveredPageNode()) {
    return (
      (Number(hoveredColumnContext.value?.sectionIndex) === sIdx && Number(hoveredColumnContext.value?.rowIndex) === rIdx) ||
      (Number(hoveredElementContext.value?.sectionIndex) === sIdx && Number(hoveredElementContext.value?.rowIndex) === rIdx)
    )
  }
  return (
    (Number(props.selectedColumnContext?.sectionIndex) === sIdx && Number(props.selectedColumnContext?.rowIndex) === rIdx) ||
    (Number(props.selectedElementContext?.sectionIndex) === sIdx && Number(props.selectedElementContext?.rowIndex) === rIdx)
  )
}

function isRowHovered(sectionIndex, rowIndex) {
  return (
    Number(hoveredRowContext.value?.sectionIndex) === Number(sectionIndex) &&
    Number(hoveredRowContext.value?.rowIndex) === Number(rowIndex)
  )
}

function getRowRingClass(sectionIndex, rowIndex) {
  if (isRowChromeSuppressed(sectionIndex, rowIndex)) return 'ring-transparent'
  const active = hasAnyHoveredPageNode()
    ? isRowHovered(sectionIndex, rowIndex)
    : isRowSelected(sectionIndex, rowIndex)
  return active
    ? 'ring-[#2D8CFF]'
    : 'ring-transparent'
}

function getRowChromeVisibilityClasses(sectionIndex, rowIndex) {
  if (isRowChromeSuppressed(sectionIndex, rowIndex)) return 'opacity-0 pointer-events-none'
  const active = hasAnyHoveredPageNode()
    ? isRowHovered(sectionIndex, rowIndex)
    : isRowSelected(sectionIndex, rowIndex)
  if (active) return 'opacity-100 pointer-events-auto'
  return 'opacity-0 pointer-events-none'
}

function getRowColumnClasses(sectionIndex, rowIndex, columnIndex) {
  if (isRowColumnSuppressedByElement(sectionIndex, rowIndex, columnIndex)) return 'bg-transparent'
  if (isRowColumnHovered(sectionIndex, rowIndex, columnIndex)) return 'bg-[#2D8CFF]/[0.03]'
  if (!hasAnyHoveredPageNode() && isRowColumnSelected(sectionIndex, rowIndex, columnIndex)) return 'bg-[#2D8CFF]/[0.06]'
  return 'bg-transparent'
}

function getRowColumnRingClass(sectionIndex, rowIndex, columnIndex) {
  if (isRowColumnSuppressedByElement(sectionIndex, rowIndex, columnIndex)) return 'ring-transparent'
  if (isRowColumnHovered(sectionIndex, rowIndex, columnIndex)) return 'ring-[#2D8CFF]/65'
  if (!hasAnyHoveredPageNode() && isRowColumnSelected(sectionIndex, rowIndex, columnIndex)) return 'ring-[#2D8CFF]'
  return 'ring-transparent'
}

function getRowColumnStyle(row, columnIndex) {
  const styles = Array.isArray(row?.props?.rowColumnStyles) ? row.props.rowColumnStyles : []
  const idx = Math.max(0, Number(columnIndex) || 0)
  const styleEntry = styles[idx] && typeof styles[idx] === 'object' ? styles[idx] : {}
  const backgroundColor = normalizeBackgroundColor(styleEntry?.backgroundColor, 'transparent')
  const fileId = sanitizeFileId(styleEntry?.backgroundImageFileId || styleEntry?.background_image_file_id)
  const rawUrl = resolveLegacyTemplateAssetUrl(resolveStoredImageUrl(styleEntry?.backgroundImageUrl || styleEntry?.background_image_url))
  const backgroundImageUrl = fileId ? buildImageUrlFromFileId(fileId) : rawUrl
  const backgroundSize = String(styleEntry?.backgroundSize || 'cover').trim().toLowerCase() === 'contain' ? 'contain' : 'cover'
  const backgroundPosition = String(styleEntry?.backgroundPosition || 'center').trim() || 'center'
  const backgroundRepeat = String(styleEntry?.backgroundRepeat || 'no-repeat').trim().toLowerCase() === 'repeat' ? 'repeat' : 'no-repeat'
  const legacyCheckoutRow = isCheckoutTemplateRow(row) && Boolean(getCheckoutTemplateName(row)) && !row?.props?.checkoutTemplate
  const legacyColumnPadding = idx === 0 ? 40 : 36
  const paddingXRaw = Number(styleEntry?.paddingX ?? (legacyCheckoutRow ? legacyColumnPadding : 0))
  const paddingYRaw = Number(styleEntry?.paddingY ?? (legacyCheckoutRow ? legacyColumnPadding : 0))
  const paddingX = Number.isFinite(paddingXRaw) ? Math.max(0, Math.min(240, Math.round(paddingXRaw))) : 0
  const paddingY = Number.isFinite(paddingYRaw) ? Math.max(0, Math.min(240, Math.round(paddingYRaw))) : 0
  const justifyRaw = String(styleEntry?.contentJustify || 'start').trim().toLowerCase()
  const justifyContent = justifyRaw === 'center' ? 'center' : justifyRaw === 'end' ? 'flex-end' : 'flex-start'
  const alignRaw = String(styleEntry?.contentAlign || 'start').trim().toLowerCase()
  const alignItems = alignRaw === 'center'
    ? 'center'
    : (alignRaw === 'right' || alignRaw === 'end' ? 'flex-end' : 'flex-start')
  return {
    display: 'flex',
    flexDirection: 'column',
    justifyContent,
    alignItems,
    backgroundColor,
    backgroundImage: toCssBackgroundImage(backgroundImageUrl),
    backgroundSize,
    backgroundPosition,
    backgroundRepeat,
    minHeight: toColumnHeightCssValue(styleEntry?.height, styleEntry?.heightUnit, 120),
    paddingLeft: `${paddingX}px`,
    paddingRight: `${paddingX}px`,
    paddingTop: `${paddingY}px`,
    paddingBottom: `${paddingY}px`,
    boxSizing: 'border-box',
  }
}

function getRowBadgeLabel(row) {
  const colCount = getRowColumnCount(row)
  return `${colCount} Col. Row`
}

function getRowElementBadgeLabel(element) {
  const type = String(element?.type || '').trim().toLowerCase()
  if (type === 'row') return getRowBadgeLabel(element)
  if (type === 'checkout-order-form') return 'Order Form'
  if (type === 'text') return 'Text'
  if (type === 'image') return 'Image'
  if (type === 'button') return 'Button'
  if (type === 'bullet-list') return 'Bullet List'
  if (type === 'share') return 'Share'
  if (type === 'optin-form') return 'Opt-in Form'
  if (type === 'quiz-result-history') return 'Member Results'
  if (type === 'member-login-form') return isJoinPageContext.value ? 'Create Free Account' : 'Login'
  if (type === MEMBER_CREATE_FREE_ACCOUNT_FORM_TYPE) return 'Create Free Account'
  if (type === 'member-pricing-plans') return 'Plans'
  if (type === 'member-get-access-form') return 'Get Access'
  if (type === 'member-approval-form') return 'Member Approval'
  if (type === 'member-get-access-success') return 'Access Success'
  if (type === 'video') return 'Video'
  if (type === 'mp3') return 'MP3'
  if (type === 'embed') return 'Embed'
  if (type === 'header') return 'Header'
  if (!type) return 'Element'
  return type
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function getBulletItems(element) {
  const items = Array.isArray(element?.props?.items) ? element.props.items : []
  return items.length ? items : [{ text: 'Add a bullet point' }]
}

function getBulletIconText(element) {
  const icon = String(element?.props?.bulletIcon || 'disc').trim().toLowerCase()
  if (icon === 'arrow') return '→'
  if (icon === 'check') return '✓'
  if (icon === 'check-circle') return ''
  if (icon === 'star') return '★'
  return '•'
}

function isCircleCheckBullet(element) {
  return String(element?.props?.bulletIcon || '').trim().toLowerCase() === 'check-circle'
}

function getBulletListStyle(element) {
  return {
    gap: `${Math.max(0, Math.min(80, Number(element?.props?.bulletGap ?? 12) || 0))}px`,
    color: normalizeHexColor(element?.props?.bulletTextColor, '#18181B'),
    fontSize: `${Math.max(10, Math.min(72, Number(element?.props?.bulletFontSize ?? 18) || 18))}px`,
    lineHeight: '1.5',
  }
}

function getBulletIconStyle(element) {
  const icon = String(element?.props?.bulletIcon || 'disc').trim().toLowerCase()
  const size = Math.max(10, Math.min(72, Number(element?.props?.bulletFontSize ?? 18) || 18))
  return {
    width: `${Math.max(22, Math.round(size * 1.45))}px`,
    height: icon === 'check-circle' ? `${Math.max(20, Math.round(size * 1.2))}px` : 'auto',
    marginRight: `${Math.max(6, Math.round(size * 0.45))}px`,
    color: normalizeHexColor(element?.props?.bulletIconColor, '#22C55E'),
    border: 'none',
    borderRadius: '0',
    fontSize: icon === 'check-circle' ? `${Math.max(20, Math.round(size * 1.2))}px` : '1em',
    lineHeight: '1',
  }
}

function onBulletItemInput(element, index, event) {
  if (!element?.props) return
  if (!Array.isArray(element.props.items)) element.props.items = []
  if (!element.props.items[index] || typeof element.props.items[index] !== 'object') {
    element.props.items[index] = { text: '' }
  }
  element.props.items[index].text = String(event?.currentTarget?.textContent || '').slice(0, 1000)
}

function getShareNetworks(element) {
  const allowed = new Set(['facebook', 'x', 'linkedin', 'whatsapp', 'email', 'copy'])
  const networks = Array.isArray(element?.props?.shareNetworks) ? element.props.shareNetworks : []
  return networks.map((network) => String(network || '').trim().toLowerCase()).filter((network) => allowed.has(network))
}

function getShareNetworkLabel(network) {
  return ({ facebook: 'Facebook', x: 'X', linkedin: 'LinkedIn', whatsapp: 'WhatsApp', email: 'Email', copy: 'Copy link' })[network] || 'Share'
}

function getShareNetworkMark(network) {
  return ({ facebook: 'f', x: '𝕏', linkedin: 'in', whatsapp: 'wa', email: '✉', copy: '↗' })[network] || '↗'
}

function getShareAlignmentClass(element) {
  const align = String(element?.props?.shareAlignment || 'left').trim().toLowerCase()
  if (align === 'center') return 'items-center'
  if (align === 'right') return 'items-end'
  return 'items-start'
}

function getShareButtonStyle(element) {
  const size = Math.max(28, Math.min(72, Number(element?.props?.shareButtonSize ?? 42) || 42))
  return {
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: String(element?.props?.shareButtonStyle || 'circle') === 'square' ? '10px' : '9999px',
    backgroundColor: normalizeHexColor(element?.props?.shareButtonColor, '#18181B'),
    color: normalizeHexColor(element?.props?.shareIconColor, '#FFFFFF'),
    fontSize: `${Math.max(11, Math.round(size * 0.34))}px`,
  }
}

function getOptinFields(element) {
  const fields = Array.isArray(element?.props?.optinFields) ? element.props.optinFields : []
  return fields.length ? fields : [{ key: 'email', type: 'email', label: 'Email address', placeholder: 'you@example.com', required: true }]
}

function getOptinInputStyle(element) {
  const props = element?.props || {}
  return {
    height: `${Math.max(36, Math.min(88, Number(props.optinInputHeight ?? 48) || 48))}px`,
    borderRadius: `${Math.max(0, Math.min(48, Number(props.optinCornerRadius ?? 10) || 10))}px`,
    borderColor: normalizeBackgroundColor(props.optinInputBorderColor, '#D4D4D8'),
    backgroundColor: normalizeBackgroundColor(props.optinInputBackgroundColor, '#FFFFFF'),
    color: normalizeHexColor(props.optinInputTextColor, '#18181B'),
  }
}

function getOptinButtonStyle(element) {
  const props = element?.props || {}
  return {
    minHeight: `${Math.max(36, Math.min(88, Number(props.optinInputHeight ?? 48) || 48))}px`,
    borderRadius: `${Math.max(0, Math.min(48, Number(props.optinCornerRadius ?? 10) || 10))}px`,
    backgroundColor: normalizeBackgroundColor(props.optinButtonBackgroundColor, '#2D8CFF'),
    color: normalizeHexColor(props.optinButtonTextColor, '#FFFFFF'),
  }
}

function checkoutPreviewInputStyle(element) {
  const props = element?.props || {}
  const radius = Math.max(0, Math.min(32, Number(props.formCornerRadius ?? 12) || 0))
  const borderColor = /^#[0-9a-f]{6}$/i.test(String(props.inputBorderColor || '')) ? props.inputBorderColor : '#D4D4D8'
  const backgroundColor = normalizeBackgroundColor(props.inputBackgroundColor, '#FFFFFF')
  const color = /^#[0-9a-f]{6}$/i.test(String(props.inputTextColor || '')) ? props.inputTextColor : '#71717A'
  const minHeight = `${Math.max(38, Math.min(72, Number(props.inputHeight ?? 48) || 48))}px`
  return {
    borderColor,
    borderRadius: `${radius}px`,
    backgroundColor,
    color,
    minHeight,
    boxShadow: props.inputShadow === true ? '0 4px 12px rgba(15, 23, 42, 0.08)' : 'none',
  }
}

function checkoutPreviewCardStyle(element) {
  const props = element?.props || {}
  const borderWidth = Math.max(0, Math.min(8, Number(props.cardBorderWidth ?? 1) || 0))
  const shadow = String(props.cardShadow || 'medium')
  const shadows = {
    none: 'none',
    soft: '0 8px 24px rgba(15, 23, 42, 0.07)',
    medium: '0 18px 45px rgba(15, 23, 42, 0.12)',
    strong: '0 28px 70px rgba(15, 23, 42, 0.2)',
  }
  const paddingX = Math.max(0, Math.min(96, Number(props.cardPaddingX ?? 36) || 0))
  const paddingY = Math.max(0, Math.min(96, Number(props.cardPaddingY ?? 36) || 0))
  return {
    width: '100%',
    maxWidth: '100%',
    minWidth: '0',
    overflow: 'hidden',
    boxSizing: 'border-box',
    backgroundColor: props.cardTransparent === true ? 'transparent' : (props.cardBackgroundColor || '#FFFFFF'),
    borderColor: props.cardBorderColor || '#E4E4E7',
    borderStyle: borderWidth > 0 ? 'solid' : 'none',
    borderWidth: `${borderWidth}px`,
    borderRadius: `${Math.max(0, Math.min(48, Number(props.cardCornerRadius ?? 16) || 0))}px`,
    boxShadow: shadows[shadow] || shadows.medium,
    paddingLeft: `${paddingX}px`,
    paddingRight: `${paddingX}px`,
    paddingTop: `${paddingY}px`,
    paddingBottom: `${paddingY}px`,
  }
}

function checkoutSecureBadgeIcon(element) {
  const icon = String(element?.props?.secureBadgeIcon || 'lock')
  if (icon === 'shield') return '◆ '
  if (icon === 'check') return '✓ '
  if (icon === 'none') return ''
  return '🔒 '
}

function checkoutProductSelectorOptions(element) {
  const options = Array.isArray(element?.props?.productSelectorOptions) ? element.props.productSelectorOptions : []
  return options.length ? options : [{ id: 'primary-offer', productPublicId: '', title: 'Current product', subtitle: '', badge: '', paymentType: 'one_time', priceCents: 0, currencyCode: 'USD' }]
}

function checkoutProductOptionPrice(option) {
  if (String(option?.paymentType || '') === 'free_forever') return 'Free'
  const amount = (Math.max(0, Number(option?.priceCents || 0)) / 100).toLocaleString('en-US', { style: 'currency', currency: String(option?.currencyCode || 'USD') })
  if (String(option?.paymentType || '') === 'monthly') return `${amount} / month`
  if (String(option?.paymentType || '') === 'yearly') return `${amount} / year`
  return amount
}

function showCheckoutStepTabs() {
  return String(props.doc?.template || '') !== 'tai_lopez'
}

function checkoutPreviewBadgeStyle(element) {
  const props = element?.props || {}
  return {
    color: props.secureBadgeTextColor || '#047857',
    backgroundColor: props.secureBadgeBackgroundColor || '#ECFDF5',
  }
}

function checkoutPreviewButtonStyle(element) {
  const props = element?.props || {}
  return {
    backgroundColor: props.buttonBackgroundColor || props.accentColor || '#2D8CFF',
    color: props.buttonTextColor || '#FFFFFF',
    borderRadius: `${Math.max(0, Math.min(32, Number(props.buttonCornerRadius ?? 12) || 0))}px`,
    fontSize: `${Math.max(11, Math.min(24, Number(props.buttonFontSize ?? 14) || 14))}px`,
    fontWeight: String([500, 600, 700, 800].includes(Number(props.buttonFontWeight)) ? Number(props.buttonFontWeight) : 800),
    boxShadow: props.buttonShadow === false ? 'none' : '0 10px 20px rgba(15, 23, 42, 0.12)',
  }
}

function getRowColumnChromeVisibilityClasses(sectionIndex, rowIndex, columnIndex, nestedRowElementIndex = -1) {
  if (isRowColumnSuppressedByElement(sectionIndex, rowIndex, columnIndex, nestedRowElementIndex)) {
    return 'opacity-0 pointer-events-none'
  }
  const active = hasAnyHoveredPageNode()
    ? isRowColumnHovered(sectionIndex, rowIndex, columnIndex, nestedRowElementIndex)
    : isRowColumnSelected(sectionIndex, rowIndex, columnIndex, nestedRowElementIndex)
  if (active) {
    return 'opacity-100 pointer-events-auto'
  }
  return 'opacity-0 pointer-events-none'
}

function getRowElementChromeVisibilityClasses(sectionIndex, rowIndex, elementIndex, nestedRowElementIndex = -1) {
  const active = hasAnyHoveredPageNode()
    ? isRowElementHovered(sectionIndex, rowIndex, elementIndex, nestedRowElementIndex)
    : isRowElementSelected(sectionIndex, rowIndex, elementIndex, nestedRowElementIndex)
  if (active) {
    return 'opacity-100 pointer-events-auto'
  }
  return 'opacity-0 pointer-events-none'
}

function isNestedRowElement(element) {
  return String(element?.type || '').trim().toLowerCase() === 'row'
}

function getRowElementDropIndicatorClass(element) {
  return isNestedRowElement(element) ? 'bg-[#2D8CFF]' : 'bg-[#F59E0B]'
}

function getRowElementRingClass(sectionIndex, rowIndex, elementIndex, element) {
  const active = hasAnyHoveredPageNode()
    ? isRowElementHovered(sectionIndex, rowIndex, elementIndex)
    : isRowElementSelected(sectionIndex, rowIndex, elementIndex)
  if (isNestedRowElement(element)) {
    return active ? 'ring-[#2D8CFF]' : 'ring-transparent'
  }
  return active ? 'ring-[#F59E0B]' : 'ring-transparent'
}

function getRowElementBadgeToneClass(element) {
  return isNestedRowElement(element) ? 'bg-[#2D8CFF]' : 'bg-[#F59E0B]'
}

function getRowElementToolbarButtonClass(element) {
  return isNestedRowElement(element)
    ? 'inline-flex h-8 w-8 items-center justify-center rounded-md text-white shadow-md bg-[#2D8CFF] hover:bg-[#1D4ED8]'
    : 'inline-flex h-8 w-8 items-center justify-center rounded-md text-white shadow-md bg-[#F59E0B] hover:bg-[#D97706]'
}

function getRowElementPlusButtonClass(element) {
  return isNestedRowElement(element)
    ? 'inline-flex h-8 w-8 items-center justify-center rounded-full text-white border-2 border-[#1D4ED8] shadow-md bg-[#2D8CFF] hover:bg-[#1D4ED8]'
    : 'inline-flex h-8 w-8 items-center justify-center rounded-full text-white border-2 border-[#B45309] shadow-md bg-[#F59E0B] hover:bg-[#D97706]'
}

function getRowElementToolbarClasses(sectionIndex, rowIndex, elementIndex, nestedRowElementIndex = -1) {
  return ['bottom-full right-0 z-40', getRowElementChromeVisibilityClasses(sectionIndex, rowIndex, elementIndex, nestedRowElementIndex)]
}

function shouldShowRowDropIndicator(sectionIndex, rowIndex) {
  return (
    rowDragActive.value &&
    Number(rowDragDropTarget.value?.sectionIndex) === Number(sectionIndex) &&
    Number(rowDragDropTarget.value?.rowIndex) === Number(rowIndex)
  )
}

function shouldShowRowElementDropIndicator(sectionIndex, rowIndex, elementIndex) {
  return (
    rowElementDragActive.value &&
    Number(rowElementDragDropTarget.value?.sectionIndex) === Number(sectionIndex) &&
    Number(rowElementDragDropTarget.value?.rowIndex) === Number(rowIndex) &&
    Number(rowElementDragDropTarget.value?.nestedRowElementIndex ?? -1) === -1 &&
    Number(rowElementDragDropTarget.value?.elementIndex) === Number(elementIndex)
  )
}

function isRowElementHovered(sectionIndex, rowIndex, elementIndex, nestedRowElementIndex = -1) {
  return (
    Number(hoveredElementContext.value?.sectionIndex) === Number(sectionIndex) &&
    Number(hoveredElementContext.value?.rowIndex) === Number(rowIndex) &&
    Number(hoveredElementContext.value?.elementIndex) === Number(elementIndex) &&
    Number(hoveredElementContext.value?.nestedRowElementIndex ?? -1) === Number(nestedRowElementIndex)
  )
}

function isRowColumnHovered(sectionIndex, rowIndex, columnIndex, nestedRowElementIndex = -1) {
  return (
    Number(hoveredColumnContext.value?.sectionIndex) === Number(sectionIndex) &&
    Number(hoveredColumnContext.value?.rowIndex) === Number(rowIndex) &&
    Number(hoveredColumnContext.value?.columnIndex) === Number(columnIndex) &&
    Number(hoveredColumnContext.value?.nestedRowElementIndex ?? -1) === Number(nestedRowElementIndex)
  )
}

function getRowFromContext(sectionIndex, rowIndex, nestedRowElementIndex = -1) {
  const section = props.doc?.sections?.[Number(sectionIndex)]
  if (!section || String(section?.type || '') !== 'section') return null
  const rows = Array.isArray(section?.props?.rows) ? section.props.rows : []
  const hostRow = rows[Number(rowIndex)]
  if (!hostRow || String(hostRow?.type || '') !== 'row') return null
  const nestedIdx = Number(nestedRowElementIndex ?? -1)
  if (!Number.isFinite(nestedIdx) || nestedIdx < 0) return hostRow
  const hostElements = Array.isArray(hostRow?.props?.elements) ? hostRow.props.elements : []
  const nestedRow = hostElements[nestedIdx]
  if (!nestedRow || String(nestedRow?.type || '') !== 'row') return null
  return nestedRow
}

function getElementColumnIndexForContext(sectionIndex, rowIndex, elementIndex, nestedRowElementIndex = -1) {
  const row = getRowFromContext(sectionIndex, rowIndex, nestedRowElementIndex)
  if (!row) return -1
  const elements = Array.isArray(row?.props?.elements) ? row.props.elements : []
  const element = elements[Number(elementIndex)]
  if (!element) return -1
  return getRowElementColumnIndex(element, row)
}

function isRowColumnSuppressedByElement(sectionIndex, rowIndex, columnIndex, nestedRowElementIndex = -1) {
  const sIdx = Number(sectionIndex)
  const rIdx = Number(rowIndex)
  const cIdx = Number(columnIndex)
  const nestedIdx = Number(nestedRowElementIndex ?? -1)

  const hoveredMatchesRow =
    Number(hoveredElementContext.value?.sectionIndex) === sIdx &&
    Number(hoveredElementContext.value?.rowIndex) === rIdx &&
    Number(hoveredElementContext.value?.nestedRowElementIndex ?? -1) === nestedIdx
  if (hoveredMatchesRow) {
    const hoveredColumn = getElementColumnIndexForContext(
      sIdx,
      rIdx,
      Number(hoveredElementContext.value?.elementIndex),
      nestedIdx
    )
    if (hoveredColumn === cIdx) return true
  }

  if (hasAnyHoveredPageNode()) return false

  const selectedMatchesRow =
    Number(props.selectedElementContext?.sectionIndex) === sIdx &&
    Number(props.selectedElementContext?.rowIndex) === rIdx &&
    Number(props.selectedElementContext?.nestedRowElementIndex ?? -1) === nestedIdx
  if (selectedMatchesRow) {
    const selectedColumn = getElementColumnIndexForContext(
      sIdx,
      rIdx,
      Number(props.selectedElementContext?.elementIndex),
      nestedIdx
    )
    if (selectedColumn === cIdx) return true
  }

  return false
}

function getNestedRowColumnClasses(sectionIndex, rowIndex, elementIndex, columnIndex) {
  if (isRowColumnSuppressedByElement(sectionIndex, rowIndex, columnIndex, elementIndex)) {
    return 'bg-transparent'
  }
  if (isRowColumnHovered(sectionIndex, rowIndex, columnIndex, elementIndex)) {
    return 'bg-[#2D8CFF]/[0.03]'
  }
  if (!hasAnyHoveredPageNode() && isRowColumnSelected(sectionIndex, rowIndex, columnIndex, elementIndex)) {
    return 'bg-[#2D8CFF]/[0.06]'
  }
  return 'bg-transparent'
}

function getNestedRowColumnRingClass(sectionIndex, rowIndex, elementIndex, columnIndex) {
  if (isRowColumnSuppressedByElement(sectionIndex, rowIndex, columnIndex, elementIndex)) {
    return 'ring-transparent'
  }
  if (isRowColumnHovered(sectionIndex, rowIndex, columnIndex, elementIndex)) {
    return 'ring-[#2D8CFF]/65'
  }
  if (!hasAnyHoveredPageNode() && isRowColumnSelected(sectionIndex, rowIndex, columnIndex, elementIndex)) {
    return 'ring-[#2D8CFF]'
  }
  return 'ring-transparent'
}

function getNestedRowElementRingClasses(sectionIndex, rowIndex, parentElementIndex, elementIndex, element) {
  const isSelected = isRowElementSelected(sectionIndex, rowIndex, elementIndex, parentElementIndex)
  const isHovered = isRowElementHovered(sectionIndex, rowIndex, elementIndex, parentElementIndex)
  const active = hasAnyHoveredPageNode() ? isHovered : isSelected
  if (!active) return 'ring-transparent'
  const type = String(element?.type || '').trim().toLowerCase()
  if (type === 'row') return 'ring-[#2D8CFF]'
  return 'ring-[#F59E0B]'
}

function shouldShowNestedRowElementDropIndicator(sectionIndex, rowIndex, parentElementIndex, elementIndex) {
  return (
    rowElementDragActive.value &&
    Number(rowElementDragDropTarget.value?.sectionIndex) === Number(sectionIndex) &&
    Number(rowElementDragDropTarget.value?.rowIndex) === Number(rowIndex) &&
    Number(rowElementDragDropTarget.value?.nestedRowElementIndex ?? -1) === Number(parentElementIndex) &&
    Number(rowElementDragDropTarget.value?.elementIndex) === Number(elementIndex)
  )
}

function openRowSettingsMenu(sectionIndex, rowIndex, event) {
  onSelectRow(sectionIndex, rowIndex)
  const rect = event?.currentTarget?.getBoundingClientRect?.()
  emit('open-settings-menu', {
    scope: 'section-row',
    index: sectionIndex,
    rowIndex,
    settingsType: 'row',
    anchorRect: rect ? { top: rect.top, left: rect.left, width: rect.width, height: rect.height } : null,
  })
}

function openAddRowAfterPicker(sectionIndex, rowIndex, event) {
  const rect = event?.currentTarget?.getBoundingClientRect?.()
  emit('add-row-after', {
    sectionIndex,
    rowIndex,
    anchorRect: rect ? { top: rect.top, left: rect.left, width: rect.width, height: rect.height } : null,
  })
}

function openRowElementSettingsMenu(sectionIndex, rowIndex, elementIndex, element, event, nestedRowElementIndex = -1) {
  onSelectRowElement(sectionIndex, rowIndex, elementIndex, nestedRowElementIndex)
  const type = String(element?.type || '').trim().toLowerCase()
  const settingsType =
    type === 'image' ? 'image'
      : type === 'button' ? 'button'
        : type === 'video' ? 'video'
          : type === 'member-login-form' || type === MEMBER_CREATE_FREE_ACCOUNT_FORM_TYPE || type === 'member-pricing-plans' || type === 'member-get-access-form' || type === 'checkout-order-form' || type === 'member-approval-form' || type === 'member-get-access-success' || type === 'quiz-result-history' || type === 'row' ? 'none'
          : 'text'
  const rect = event?.currentTarget?.getBoundingClientRect?.()
  emit('open-settings-menu', {
    scope: 'row-element',
    index: sectionIndex,
    rowIndex,
    elementIndex,
    nestedRowElementIndex,
    settingsType,
    anchorRect: rect ? { top: rect.top, left: rect.left, width: rect.width, height: rect.height } : null,
  })
}

function openAddRowElementAfterPicker(sectionIndex, rowIndex, elementIndex, event) {
  const rect = event?.currentTarget?.getBoundingClientRect?.()
  emit('add-row-element-after', {
    sectionIndex,
    rowIndex,
    elementIndex,
    anchorRect: rect ? { top: rect.top, left: rect.left, width: rect.width, height: rect.height } : null,
  })
}

function openBlockSettingsMenu(index, block, event) {
  emit('select', index)
  const type = String(block?.type || '').trim().toLowerCase()
  const settingsType =
    type === 'image' ? 'image'
      : type === 'button' ? 'button'
        : type === 'video' ? 'video'
          : 'text'
  const rect = event?.currentTarget?.getBoundingClientRect?.()
  emit('open-settings-menu', {
    scope: 'block',
    index,
    settingsType,
    anchorRect: rect ? { top: rect.top, left: rect.left, width: rect.width, height: rect.height } : null,
  })
}

function onBlockDragStart(index, event) {
  commitActiveRowElementTextEdit()
  const dt = event?.dataTransfer
  if (!dt) return
  dragActive.value = true
  dragDropIndex.value = index
  dt.effectAllowed = 'move'
  dt.setData(PAGE_BLOCK_REORDER_MIME, String(index))
}

function onBlockDragEnd() {
  dragActive.value = false
  dragDropIndex.value = null
}

function onRowDragStart(sectionIndex, rowIndex, event) {
  commitActiveRowElementTextEdit()
  const dt = event?.dataTransfer
  if (!dt) return
  const payload = { sectionIndex, rowIndex }
  dt.effectAllowed = 'move'
  dt.setData(PAGE_ROW_REORDER_MIME, JSON.stringify(payload))
  dt.setData('text/plain', `${PAGE_ROW_REORDER_TEXT_PREFIX}${JSON.stringify(payload)}`)
  rowDragActive.value = true
  rowDragDropTarget.value = { sectionIndex, rowIndex }
}

function onRowDragEnd() {
  rowDragActive.value = false
  rowDragDropTarget.value = { sectionIndex: -1, rowIndex: -1 }
  hoveredRowContext.value = { sectionIndex: -1, rowIndex: -1 }
  hoveredColumnContext.value = { sectionIndex: -1, rowIndex: -1, columnIndex: -1, nestedRowElementIndex: -1 }
  rowElementDragActive.value = false
  rowElementDragDropTarget.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1, columnIndex: 0 }
  hoveredElementContext.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1, nestedRowElementIndex: -1 }
}

function onRowDragOver(sectionIndex, rowIndex, event) {
  const dt = event?.dataTransfer
  if (!dt) return
  const hasElementInsert = hasDataTransferType(dt, PAGE_ROW_ELEMENT_INSERT_MIME) || Boolean(readPayloadFromTransfer(dt, PAGE_ROW_ELEMENT_INSERT_MIME, PAGE_ROW_ELEMENT_INSERT_TEXT_PREFIX))
  const hasElementReorder = hasDataTransferType(dt, PAGE_ROW_ELEMENT_REORDER_MIME) || Boolean(readPayloadFromTransfer(dt, PAGE_ROW_ELEMENT_REORDER_MIME, PAGE_ROW_ELEMENT_REORDER_TEXT_PREFIX))
  if (hasElementInsert || hasElementReorder) {
    dt.dropEffect = hasElementInsert ? 'copy' : 'move'
    rowElementDragActive.value = true
    const block = props.doc?.sections?.[sectionIndex]
    const row = getSectionRows(block)[rowIndex]
    const targetElementIndex = getRowElements(row).length
    rowElementDragDropTarget.value = { sectionIndex, rowIndex, elementIndex: targetElementIndex, columnIndex: 0 }
    return
  }
  const hasRowInsert = hasDataTransferType(dt, PAGE_ROW_INSERT_MIME) || Boolean(readPayloadFromTransfer(dt, PAGE_ROW_INSERT_MIME, PAGE_ROW_INSERT_TEXT_PREFIX))
  const hasRowReorder = hasDataTransferType(dt, PAGE_ROW_REORDER_MIME) || Boolean(readPayloadFromTransfer(dt, PAGE_ROW_REORDER_MIME, PAGE_ROW_REORDER_TEXT_PREFIX))
  if (!hasRowInsert && !hasRowReorder) return
  dt.dropEffect = hasRowInsert ? 'copy' : 'move'
  rowDragActive.value = true
  const rect = event?.currentTarget?.getBoundingClientRect?.()
  if (!rect) return
  const halfway = rect.top + rect.height / 2
  const targetRowIndex = event.clientY >= halfway ? rowIndex + 1 : rowIndex
  rowDragDropTarget.value = { sectionIndex, rowIndex: targetRowIndex }
}

function onRowDrop(sectionIndex, rowIndex, event) {
  const dt = event?.dataTransfer
  if (!dt) return
  const rowElementInsertPayload = readPayloadFromTransfer(dt, PAGE_ROW_ELEMENT_INSERT_MIME, PAGE_ROW_ELEMENT_INSERT_TEXT_PREFIX)
  const rowElementReorderPayload = readPayloadFromTransfer(dt, PAGE_ROW_ELEMENT_REORDER_MIME, PAGE_ROW_ELEMENT_REORDER_TEXT_PREFIX)
  if (rowElementInsertPayload) {
    const elementType = String(rowElementInsertPayload?.type || '').trim().toLowerCase()
    if (!elementType) return
    const targetElementIndex =
      Number(rowElementDragDropTarget.value?.sectionIndex) === Number(sectionIndex) &&
      Number(rowElementDragDropTarget.value?.rowIndex) === Number(rowIndex)
        ? Number(rowElementDragDropTarget.value?.elementIndex)
        : 0
    emit('insert-row-element', { sectionIndex, rowIndex, elementIndex: targetElementIndex, elementType, columnIndex: 0 })
    rowElementDragActive.value = false
    rowElementDragDropTarget.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1, columnIndex: 0 }
    return
  }
  if (rowElementReorderPayload) {
    const fromSectionIndex = Number(rowElementReorderPayload?.sectionIndex)
    const fromRowIndex = Number(rowElementReorderPayload?.rowIndex)
    const fromElementIndex = Number(rowElementReorderPayload?.elementIndex)
    if (!Number.isFinite(fromSectionIndex) || !Number.isFinite(fromRowIndex) || !Number.isFinite(fromElementIndex)) return
    const targetElementIndex =
      Number(rowElementDragDropTarget.value?.sectionIndex) === Number(sectionIndex) &&
      Number(rowElementDragDropTarget.value?.rowIndex) === Number(rowIndex)
        ? Number(rowElementDragDropTarget.value?.elementIndex)
        : 0
    emit('reorder-row-element', {
      fromSectionIndex,
      fromRowIndex,
      fromElementIndex,
      fromNestedRowElementIndex: Number(rowElementReorderPayload?.nestedRowElementIndex ?? -1),
      toSectionIndex: sectionIndex,
      toRowIndex: rowIndex,
      toElementIndex: targetElementIndex,
      toColumnIndex: 0,
    })
    rowElementDragActive.value = false
    rowElementDragDropTarget.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1, columnIndex: 0 }
    return
  }
  const rowInsertPayload = readPayloadFromTransfer(dt, PAGE_ROW_INSERT_MIME, PAGE_ROW_INSERT_TEXT_PREFIX)
  const rowReorderPayload = readPayloadFromTransfer(dt, PAGE_ROW_REORDER_MIME, PAGE_ROW_REORDER_TEXT_PREFIX)
  if (rowInsertPayload) {
    const columnCount = Math.max(1, Math.min(6, Number(rowInsertPayload?.columnCount || 1)))
    const targetRowIndex =
      Number(rowDragDropTarget.value?.sectionIndex) === Number(sectionIndex)
        ? Number(rowDragDropTarget.value?.rowIndex)
        : rowIndex
    emit('insert-row', { sectionIndex, rowIndex: targetRowIndex, columnCount })
    rowDragActive.value = false
    rowDragDropTarget.value = { sectionIndex: -1, rowIndex: -1 }
    rowElementDragActive.value = false
    rowElementDragDropTarget.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1, columnIndex: 0 }
    return
  }
  if (rowReorderPayload) {
    const fromSectionIndex = Number(rowReorderPayload?.sectionIndex)
    const fromRowIndex = Number(rowReorderPayload?.rowIndex)
    if (!Number.isFinite(fromSectionIndex) || !Number.isFinite(fromRowIndex)) return
    const targetRowIndex =
      Number(rowDragDropTarget.value?.sectionIndex) === Number(sectionIndex)
        ? Number(rowDragDropTarget.value?.rowIndex)
        : rowIndex
    emit('reorder-row', {
      fromSectionIndex,
      fromRowIndex,
      toSectionIndex: sectionIndex,
      toRowIndex: targetRowIndex,
    })
    rowDragActive.value = false
    rowDragDropTarget.value = { sectionIndex: -1, rowIndex: -1 }
    rowElementDragActive.value = false
    rowElementDragDropTarget.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1, columnIndex: 0 }
  }
}

function onRowElementDragStart(sectionIndex, rowIndex, elementIndex, event, nestedRowElementIndex = -1) {
  commitActiveRowElementTextEdit()
  const dt = event?.dataTransfer
  if (!dt) return
  const payload = { sectionIndex, rowIndex, elementIndex, nestedRowElementIndex }
  dt.effectAllowed = 'move'
  dt.setData(PAGE_ROW_ELEMENT_REORDER_MIME, JSON.stringify(payload))
  dt.setData('text/plain', `${PAGE_ROW_ELEMENT_REORDER_TEXT_PREFIX}${JSON.stringify(payload)}`)
  rowElementDragActive.value = true
  rowElementDragDropTarget.value = { sectionIndex, rowIndex, elementIndex, columnIndex: 0, nestedRowElementIndex }
}

function onRowElementDragEnd() {
  rowElementDragActive.value = false
  rowElementDragDropTarget.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1, columnIndex: 0 }
  hoveredColumnContext.value = { sectionIndex: -1, rowIndex: -1, columnIndex: -1, nestedRowElementIndex: -1 }
  hoveredElementContext.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1, nestedRowElementIndex: -1 }
}

function onRowColumnDragOver(sectionIndex, rowIndex, row, columnIndex, event) {
  const dt = event?.dataTransfer
  if (!dt) return
  const hasRowInsert = hasDataTransferType(dt, PAGE_ROW_INSERT_MIME) || Boolean(readPayloadFromTransfer(dt, PAGE_ROW_INSERT_MIME, PAGE_ROW_INSERT_TEXT_PREFIX))
  const hasRowReorder = hasDataTransferType(dt, PAGE_ROW_REORDER_MIME) || Boolean(readPayloadFromTransfer(dt, PAGE_ROW_REORDER_MIME, PAGE_ROW_REORDER_TEXT_PREFIX))
  const hasElementInsert = hasDataTransferType(dt, PAGE_ROW_ELEMENT_INSERT_MIME) || Boolean(readPayloadFromTransfer(dt, PAGE_ROW_ELEMENT_INSERT_MIME, PAGE_ROW_ELEMENT_INSERT_TEXT_PREFIX))
  const hasElementReorder = hasDataTransferType(dt, PAGE_ROW_ELEMENT_REORDER_MIME) || Boolean(readPayloadFromTransfer(dt, PAGE_ROW_ELEMENT_REORDER_MIME, PAGE_ROW_ELEMENT_REORDER_TEXT_PREFIX))
  if (!hasRowInsert && !hasRowReorder && !hasElementInsert && !hasElementReorder) return
  dt.dropEffect = (hasRowInsert || hasElementInsert) ? 'copy' : 'move'
  rowElementDragActive.value = true
  const targetIndex = getRowElements(row).length
  rowElementDragDropTarget.value = { sectionIndex, rowIndex, elementIndex: targetIndex, columnIndex, nestedRowElementIndex: -1 }
}

function onRowElementDragOver(sectionIndex, rowIndex, elementIndex, columnIndex, event) {
  const dt = event?.dataTransfer
  if (!dt) return
  const hasRowInsert = hasDataTransferType(dt, PAGE_ROW_INSERT_MIME) || Boolean(readPayloadFromTransfer(dt, PAGE_ROW_INSERT_MIME, PAGE_ROW_INSERT_TEXT_PREFIX))
  const hasRowReorder = hasDataTransferType(dt, PAGE_ROW_REORDER_MIME) || Boolean(readPayloadFromTransfer(dt, PAGE_ROW_REORDER_MIME, PAGE_ROW_REORDER_TEXT_PREFIX))
  const hasElementInsert = hasDataTransferType(dt, PAGE_ROW_ELEMENT_INSERT_MIME) || Boolean(readPayloadFromTransfer(dt, PAGE_ROW_ELEMENT_INSERT_MIME, PAGE_ROW_ELEMENT_INSERT_TEXT_PREFIX))
  const hasElementReorder = hasDataTransferType(dt, PAGE_ROW_ELEMENT_REORDER_MIME) || Boolean(readPayloadFromTransfer(dt, PAGE_ROW_ELEMENT_REORDER_MIME, PAGE_ROW_ELEMENT_REORDER_TEXT_PREFIX))
  if (!hasRowInsert && !hasRowReorder && !hasElementInsert && !hasElementReorder) return
  dt.dropEffect = (hasRowInsert || hasElementInsert) ? 'copy' : 'move'
  rowElementDragActive.value = true
  const rect = event?.currentTarget?.getBoundingClientRect?.()
  if (!rect) return
  const halfway = rect.top + rect.height / 2
  const targetElementIndex = event.clientY >= halfway ? elementIndex + 1 : elementIndex
  rowElementDragDropTarget.value = { sectionIndex, rowIndex, elementIndex: targetElementIndex, columnIndex, nestedRowElementIndex: -1 }
}

function onRowElementDrop(sectionIndex, rowIndex, elementIndex, columnIndex, event) {
  const dt = event?.dataTransfer
  if (!dt) return
  const rowInsertPayload = readPayloadFromTransfer(dt, PAGE_ROW_INSERT_MIME, PAGE_ROW_INSERT_TEXT_PREFIX)
  const insertPayload = readPayloadFromTransfer(dt, PAGE_ROW_ELEMENT_INSERT_MIME, PAGE_ROW_ELEMENT_INSERT_TEXT_PREFIX)
  const reorderPayload = readPayloadFromTransfer(dt, PAGE_ROW_ELEMENT_REORDER_MIME, PAGE_ROW_ELEMENT_REORDER_TEXT_PREFIX)
  const rowReorderPayload = readPayloadFromTransfer(dt, PAGE_ROW_REORDER_MIME, PAGE_ROW_REORDER_TEXT_PREFIX)
  if (rowInsertPayload) {
    const columnCount = Math.max(1, Math.min(6, Number(rowInsertPayload?.columnCount || 1)))
    const targetElementIndex =
      Number(rowElementDragDropTarget.value?.sectionIndex) === Number(sectionIndex) &&
      Number(rowElementDragDropTarget.value?.rowIndex) === Number(rowIndex) &&
      Number(rowElementDragDropTarget.value?.nestedRowElementIndex ?? -1) === -1
        ? Number(rowElementDragDropTarget.value?.elementIndex)
        : elementIndex
    emit('insert-row-element', {
      sectionIndex,
      rowIndex,
      elementIndex: targetElementIndex,
      elementType: 'row',
      columnIndex,
      columnCount,
      nestedRowElementIndex: -1,
    })
    rowElementDragActive.value = false
    rowElementDragDropTarget.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1, columnIndex: 0, nestedRowElementIndex: -1 }
    return
  }
  if (insertPayload) {
    const elementType = String(insertPayload?.type || '').trim().toLowerCase()
    if (!elementType) return
    const targetElementIndex =
      Number(rowElementDragDropTarget.value?.sectionIndex) === Number(sectionIndex) &&
      Number(rowElementDragDropTarget.value?.rowIndex) === Number(rowIndex) &&
      Number(rowElementDragDropTarget.value?.nestedRowElementIndex ?? -1) === -1
        ? Number(rowElementDragDropTarget.value?.elementIndex)
        : elementIndex
    emit('insert-row-element', { sectionIndex, rowIndex, elementIndex: targetElementIndex, elementType, columnIndex, nestedRowElementIndex: -1 })
    rowElementDragActive.value = false
    rowElementDragDropTarget.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1, columnIndex: 0, nestedRowElementIndex: -1 }
    return
  }
  if (reorderPayload) {
    const fromSectionIndex = Number(reorderPayload?.sectionIndex)
    const fromRowIndex = Number(reorderPayload?.rowIndex)
    const fromElementIndex = Number(reorderPayload?.elementIndex)
    if (!Number.isFinite(fromSectionIndex) || !Number.isFinite(fromRowIndex) || !Number.isFinite(fromElementIndex)) return
    const targetElementIndex =
      Number(rowElementDragDropTarget.value?.sectionIndex) === Number(sectionIndex) &&
      Number(rowElementDragDropTarget.value?.rowIndex) === Number(rowIndex) &&
      Number(rowElementDragDropTarget.value?.nestedRowElementIndex ?? -1) === -1
        ? Number(rowElementDragDropTarget.value?.elementIndex)
        : elementIndex
    emit('reorder-row-element', {
      fromSectionIndex,
      fromRowIndex,
      fromElementIndex,
      fromNestedRowElementIndex: Number(reorderPayload?.nestedRowElementIndex ?? -1),
      toSectionIndex: sectionIndex,
      toRowIndex: rowIndex,
      toElementIndex: targetElementIndex,
      toColumnIndex: columnIndex,
      toNestedRowElementIndex: -1,
    })
    rowElementDragActive.value = false
    rowElementDragDropTarget.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1, columnIndex: 0, nestedRowElementIndex: -1 }
    return
  }
  if (rowReorderPayload) {
    const fromSectionIndex = Number(rowReorderPayload?.sectionIndex)
    const fromRowIndex = Number(rowReorderPayload?.rowIndex)
    if (!Number.isFinite(fromSectionIndex) || !Number.isFinite(fromRowIndex)) return
    const targetElementIndex =
      Number(rowElementDragDropTarget.value?.sectionIndex) === Number(sectionIndex) &&
      Number(rowElementDragDropTarget.value?.rowIndex) === Number(rowIndex) &&
      Number(rowElementDragDropTarget.value?.nestedRowElementIndex ?? -1) === -1
        ? Number(rowElementDragDropTarget.value?.elementIndex)
        : elementIndex
    emit('nest-row-into-row', {
      fromSectionIndex,
      fromRowIndex,
      toSectionIndex: sectionIndex,
      toRowIndex: rowIndex,
      toElementIndex: targetElementIndex,
      toColumnIndex: columnIndex,
      toNestedRowElementIndex: -1,
    })
    rowElementDragActive.value = false
    rowElementDragDropTarget.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1, columnIndex: 0, nestedRowElementIndex: -1 }
  }
}

function onNestedRowColumnDragOver(sectionIndex, rowIndex, nestedRowElementIndex, nestedRow, columnIndex, event) {
  const dt = event?.dataTransfer
  if (!dt) return
  const hasRowInsert = hasDataTransferType(dt, PAGE_ROW_INSERT_MIME) || Boolean(readPayloadFromTransfer(dt, PAGE_ROW_INSERT_MIME, PAGE_ROW_INSERT_TEXT_PREFIX))
  const hasRowReorder = hasDataTransferType(dt, PAGE_ROW_REORDER_MIME) || Boolean(readPayloadFromTransfer(dt, PAGE_ROW_REORDER_MIME, PAGE_ROW_REORDER_TEXT_PREFIX))
  const hasElementInsert = hasDataTransferType(dt, PAGE_ROW_ELEMENT_INSERT_MIME) || Boolean(readPayloadFromTransfer(dt, PAGE_ROW_ELEMENT_INSERT_MIME, PAGE_ROW_ELEMENT_INSERT_TEXT_PREFIX))
  const hasElementReorder = hasDataTransferType(dt, PAGE_ROW_ELEMENT_REORDER_MIME) || Boolean(readPayloadFromTransfer(dt, PAGE_ROW_ELEMENT_REORDER_MIME, PAGE_ROW_ELEMENT_REORDER_TEXT_PREFIX))
  if (!hasRowInsert && !hasRowReorder && !hasElementInsert && !hasElementReorder) return
  dt.dropEffect = (hasRowInsert || hasElementInsert) ? 'copy' : 'move'
  rowElementDragActive.value = true
  const targetIndex = getRowElements(nestedRow).length
  rowElementDragDropTarget.value = { sectionIndex, rowIndex, elementIndex: targetIndex, columnIndex, nestedRowElementIndex }
}

function onNestedRowElementDragOver(sectionIndex, rowIndex, nestedRowElementIndex, elementIndex, columnIndex, event) {
  const dt = event?.dataTransfer
  if (!dt) return
  const hasRowInsert = hasDataTransferType(dt, PAGE_ROW_INSERT_MIME) || Boolean(readPayloadFromTransfer(dt, PAGE_ROW_INSERT_MIME, PAGE_ROW_INSERT_TEXT_PREFIX))
  const hasRowReorder = hasDataTransferType(dt, PAGE_ROW_REORDER_MIME) || Boolean(readPayloadFromTransfer(dt, PAGE_ROW_REORDER_MIME, PAGE_ROW_REORDER_TEXT_PREFIX))
  const hasElementInsert = hasDataTransferType(dt, PAGE_ROW_ELEMENT_INSERT_MIME) || Boolean(readPayloadFromTransfer(dt, PAGE_ROW_ELEMENT_INSERT_MIME, PAGE_ROW_ELEMENT_INSERT_TEXT_PREFIX))
  const hasElementReorder = hasDataTransferType(dt, PAGE_ROW_ELEMENT_REORDER_MIME) || Boolean(readPayloadFromTransfer(dt, PAGE_ROW_ELEMENT_REORDER_MIME, PAGE_ROW_ELEMENT_REORDER_TEXT_PREFIX))
  if (!hasRowInsert && !hasRowReorder && !hasElementInsert && !hasElementReorder) return
  dt.dropEffect = (hasRowInsert || hasElementInsert) ? 'copy' : 'move'
  rowElementDragActive.value = true
  const rect = event?.currentTarget?.getBoundingClientRect?.()
  if (!rect) return
  const halfway = rect.top + rect.height / 2
  const targetElementIndex = event.clientY >= halfway ? elementIndex + 1 : elementIndex
  rowElementDragDropTarget.value = { sectionIndex, rowIndex, elementIndex: targetElementIndex, columnIndex, nestedRowElementIndex }
}

function onNestedRowElementDrop(sectionIndex, rowIndex, nestedRowElementIndex, nestedRow, elementIndex, columnIndex, event) {
  const dt = event?.dataTransfer
  if (!dt) return
  const rowInsertPayload = readPayloadFromTransfer(dt, PAGE_ROW_INSERT_MIME, PAGE_ROW_INSERT_TEXT_PREFIX)
  const rowReorderPayload = readPayloadFromTransfer(dt, PAGE_ROW_REORDER_MIME, PAGE_ROW_REORDER_TEXT_PREFIX)
  const insertPayload = readPayloadFromTransfer(dt, PAGE_ROW_ELEMENT_INSERT_MIME, PAGE_ROW_ELEMENT_INSERT_TEXT_PREFIX)
  const reorderPayload = readPayloadFromTransfer(dt, PAGE_ROW_ELEMENT_REORDER_MIME, PAGE_ROW_ELEMENT_REORDER_TEXT_PREFIX)
  const targetElementIndex =
    Number(rowElementDragDropTarget.value?.sectionIndex) === Number(sectionIndex) &&
    Number(rowElementDragDropTarget.value?.rowIndex) === Number(rowIndex) &&
    Number(rowElementDragDropTarget.value?.nestedRowElementIndex ?? -1) === Number(nestedRowElementIndex)
      ? Number(rowElementDragDropTarget.value?.elementIndex)
      : elementIndex
  if (rowInsertPayload) {
    const columnCount = Math.max(1, Math.min(6, Number(rowInsertPayload?.columnCount || 1)))
    emit('insert-row-element', {
      sectionIndex,
      rowIndex,
      elementIndex: targetElementIndex,
      elementType: 'row',
      columnIndex,
      columnCount,
      nestedRowElementIndex,
    })
    rowElementDragActive.value = false
    rowElementDragDropTarget.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1, columnIndex: 0, nestedRowElementIndex: -1 }
    return
  }
  if (rowReorderPayload) {
    const fromSectionIndex = Number(rowReorderPayload?.sectionIndex)
    const fromRowIndex = Number(rowReorderPayload?.rowIndex)
    if (!Number.isFinite(fromSectionIndex) || !Number.isFinite(fromRowIndex)) return
    emit('nest-row-into-row', {
      fromSectionIndex,
      fromRowIndex,
      toSectionIndex: sectionIndex,
      toRowIndex: rowIndex,
      toElementIndex: targetElementIndex,
      toColumnIndex: columnIndex,
      toNestedRowElementIndex: nestedRowElementIndex,
    })
    rowElementDragActive.value = false
    rowElementDragDropTarget.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1, columnIndex: 0, nestedRowElementIndex: -1 }
    return
  }
  if (insertPayload) {
    const elementType = String(insertPayload?.type || '').trim().toLowerCase()
    if (!elementType) return
    emit('insert-row-element', { sectionIndex, rowIndex, elementIndex: targetElementIndex, elementType, columnIndex, nestedRowElementIndex })
    rowElementDragActive.value = false
    rowElementDragDropTarget.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1, columnIndex: 0, nestedRowElementIndex: -1 }
    return
  }
  if (reorderPayload) {
    const fromSectionIndex = Number(reorderPayload?.sectionIndex)
    const fromRowIndex = Number(reorderPayload?.rowIndex)
    const fromElementIndex = Number(reorderPayload?.elementIndex)
    if (!Number.isFinite(fromSectionIndex) || !Number.isFinite(fromRowIndex) || !Number.isFinite(fromElementIndex)) return
    emit('reorder-row-element', {
      fromSectionIndex,
      fromRowIndex,
      fromElementIndex,
      fromNestedRowElementIndex: Number(reorderPayload?.nestedRowElementIndex ?? -1),
      toSectionIndex: sectionIndex,
      toRowIndex: rowIndex,
      toElementIndex: targetElementIndex,
      toColumnIndex: columnIndex,
      toNestedRowElementIndex: nestedRowElementIndex,
    })
    rowElementDragActive.value = false
    rowElementDragDropTarget.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1, columnIndex: 0, nestedRowElementIndex: -1 }
  }
}

function onCanvasDragEnter(event) {
  const dt = event?.dataTransfer
  if (!dt) return
  const insertType = String(dt.getData(PAGE_BLOCK_INSERT_MIME) || '').trim()
  const reorderFrom = String(dt.getData(PAGE_BLOCK_REORDER_MIME) || '').trim()
  const rowInsert = hasDataTransferType(dt, PAGE_ROW_INSERT_MIME) || Boolean(readPayloadFromTransfer(dt, PAGE_ROW_INSERT_MIME, PAGE_ROW_INSERT_TEXT_PREFIX))
  const rowReorder = hasDataTransferType(dt, PAGE_ROW_REORDER_MIME) || Boolean(readPayloadFromTransfer(dt, PAGE_ROW_REORDER_MIME, PAGE_ROW_REORDER_TEXT_PREFIX))
  const rowElementInsert = hasDataTransferType(dt, PAGE_ROW_ELEMENT_INSERT_MIME) || Boolean(readPayloadFromTransfer(dt, PAGE_ROW_ELEMENT_INSERT_MIME, PAGE_ROW_ELEMENT_INSERT_TEXT_PREFIX))
  const rowElementReorder = hasDataTransferType(dt, PAGE_ROW_ELEMENT_REORDER_MIME) || Boolean(readPayloadFromTransfer(dt, PAGE_ROW_ELEMENT_REORDER_MIME, PAGE_ROW_ELEMENT_REORDER_TEXT_PREFIX))
  if (insertType || reorderFrom || rowInsert || rowReorder || rowElementInsert || rowElementReorder) dragActive.value = true
}

function onCanvasDragOver(event) {
  const dt = event?.dataTransfer
  if (!dt) return
  const insertType = String(dt.getData(PAGE_BLOCK_INSERT_MIME) || '').trim()
  const reorderFrom = String(dt.getData(PAGE_BLOCK_REORDER_MIME) || '').trim()
  const rowInsert = hasDataTransferType(dt, PAGE_ROW_INSERT_MIME) || Boolean(readPayloadFromTransfer(dt, PAGE_ROW_INSERT_MIME, PAGE_ROW_INSERT_TEXT_PREFIX))
  const rowReorder = hasDataTransferType(dt, PAGE_ROW_REORDER_MIME) || Boolean(readPayloadFromTransfer(dt, PAGE_ROW_REORDER_MIME, PAGE_ROW_REORDER_TEXT_PREFIX))
  const rowElementInsert = hasDataTransferType(dt, PAGE_ROW_ELEMENT_INSERT_MIME) || Boolean(readPayloadFromTransfer(dt, PAGE_ROW_ELEMENT_INSERT_MIME, PAGE_ROW_ELEMENT_INSERT_TEXT_PREFIX))
  const rowElementReorder = hasDataTransferType(dt, PAGE_ROW_ELEMENT_REORDER_MIME) || Boolean(readPayloadFromTransfer(dt, PAGE_ROW_ELEMENT_REORDER_MIME, PAGE_ROW_ELEMENT_REORDER_TEXT_PREFIX))
  if (!insertType && !reorderFrom && !rowInsert && !rowReorder && !rowElementInsert && !rowElementReorder) return
  event.preventDefault()
  if (insertType || rowInsert || rowElementInsert) dt.dropEffect = 'copy'
  else dt.dropEffect = 'move'
}

function onCanvasDragLeave(event) {
  const related = event?.relatedTarget
  if (related && related.closest && related.closest('.lesson-content-block')) return
  dragActive.value = false
  dragDropIndex.value = null
  rowDragActive.value = false
  rowDragDropTarget.value = { sectionIndex: -1, rowIndex: -1 }
  rowElementDragActive.value = false
  rowElementDragDropTarget.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1, columnIndex: 0 }
}

function onSectionDragOver(index, event) {
  const dt = event?.dataTransfer
  if (!dt) return
  const block = props.doc?.sections?.[index]
  const rowInsert = hasDataTransferType(dt, PAGE_ROW_INSERT_MIME) || Boolean(readPayloadFromTransfer(dt, PAGE_ROW_INSERT_MIME, PAGE_ROW_INSERT_TEXT_PREFIX))
  const rowReorder = hasDataTransferType(dt, PAGE_ROW_REORDER_MIME) || Boolean(readPayloadFromTransfer(dt, PAGE_ROW_REORDER_MIME, PAGE_ROW_REORDER_TEXT_PREFIX))
  const rowElementInsert = hasDataTransferType(dt, PAGE_ROW_ELEMENT_INSERT_MIME) || Boolean(readPayloadFromTransfer(dt, PAGE_ROW_ELEMENT_INSERT_MIME, PAGE_ROW_ELEMENT_INSERT_TEXT_PREFIX))
  const rowElementReorder = hasDataTransferType(dt, PAGE_ROW_ELEMENT_REORDER_MIME) || Boolean(readPayloadFromTransfer(dt, PAGE_ROW_ELEMENT_REORDER_MIME, PAGE_ROW_ELEMENT_REORDER_TEXT_PREFIX))
  if (String(block?.type || '') === 'section' && (rowInsert || rowReorder)) {
    event.preventDefault()
    dt.dropEffect = rowInsert ? 'copy' : 'move'
    rowDragActive.value = true
    const rowCount = getSectionRows(block).length
    rowDragDropTarget.value = { sectionIndex: index, rowIndex: rowCount }
    return
  }
  if (String(block?.type || '') === 'section' && (rowElementInsert || rowElementReorder)) {
    event.preventDefault()
    dt.dropEffect = rowElementInsert ? 'copy' : 'move'
    rowElementDragActive.value = true
    const rowCount = getSectionRows(block).length
    rowElementDragDropTarget.value = {
      sectionIndex: index,
      rowIndex: rowCount,
      elementIndex: 0,
      columnIndex: 0,
      nestedRowElementIndex: -1,
    }
    return
  }
  const rect = event?.currentTarget?.getBoundingClientRect?.()
  if (!rect) return
  const halfway = rect.top + rect.height / 2
  dragDropIndex.value = event.clientY >= halfway ? index + 1 : index
}

function onSectionDrop(index, event) {
  const block = props.doc?.sections?.[index]
  const dt = event?.dataTransfer
  if (dt && String(block?.type || '') === 'section') {
    const rowInsertPayload = readPayloadFromTransfer(dt, PAGE_ROW_INSERT_MIME, PAGE_ROW_INSERT_TEXT_PREFIX)
    if (rowInsertPayload) {
      const columnCount = Math.max(1, Math.min(6, Number(rowInsertPayload?.columnCount || 1)))
      const targetRowIndex =
        Number(rowDragDropTarget.value?.sectionIndex) === Number(index)
          ? Number(rowDragDropTarget.value?.rowIndex)
          : getSectionRows(block).length
      emit('insert-row', { sectionIndex: index, rowIndex: targetRowIndex, columnCount })
      rowDragActive.value = false
      rowDragDropTarget.value = { sectionIndex: -1, rowIndex: -1 }
      return
    }
    const rowReorderPayload = readPayloadFromTransfer(dt, PAGE_ROW_REORDER_MIME, PAGE_ROW_REORDER_TEXT_PREFIX)
    if (rowReorderPayload) {
      const fromSectionIndex = Number(rowReorderPayload?.sectionIndex)
      const fromRowIndex = Number(rowReorderPayload?.rowIndex)
      if (Number.isFinite(fromSectionIndex) && Number.isFinite(fromRowIndex)) {
        const toSectionRows = getSectionRows(block)
        const targetRowIndex =
          Number(rowDragDropTarget.value?.sectionIndex) === Number(index)
            ? Number(rowDragDropTarget.value?.rowIndex)
            : toSectionRows.length
        emit('reorder-row', {
          fromSectionIndex,
          fromRowIndex,
          toSectionIndex: index,
          toRowIndex: targetRowIndex,
        })
      }
      rowDragActive.value = false
      rowDragDropTarget.value = { sectionIndex: -1, rowIndex: -1 }
      return
    }
    const rowElementInsertPayload = readPayloadFromTransfer(dt, PAGE_ROW_ELEMENT_INSERT_MIME, PAGE_ROW_ELEMENT_INSERT_TEXT_PREFIX)
    const rowElementReorderPayload = readPayloadFromTransfer(dt, PAGE_ROW_ELEMENT_REORDER_MIME, PAGE_ROW_ELEMENT_REORDER_TEXT_PREFIX)
    if (rowElementInsertPayload) {
      const elementType = String(rowElementInsertPayload?.type || '').trim().toLowerCase()
      if (elementType) {
        const sectionRows = getSectionRows(block)
        const targetRowIndex =
          Number(rowElementDragDropTarget.value?.sectionIndex) === Number(index)
            ? Number(rowElementDragDropTarget.value?.rowIndex)
            : sectionRows.length
        emit('insert-row-element', {
          sectionIndex: index,
          rowIndex: targetRowIndex,
          elementIndex: 0,
          elementType,
          columnIndex: 0,
          columnCount: Math.max(1, Math.min(6, Number(rowElementInsertPayload?.columnCount || 1))),
          nestedRowElementIndex: -1,
        })
      }
      rowElementDragActive.value = false
      rowElementDragDropTarget.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1, columnIndex: 0 }
      return
    }
    if (rowElementReorderPayload) {
      const fromSectionIndex = Number(rowElementReorderPayload?.sectionIndex)
      const fromRowIndex = Number(rowElementReorderPayload?.rowIndex)
      const fromElementIndex = Number(rowElementReorderPayload?.elementIndex)
      if (Number.isFinite(fromSectionIndex) && Number.isFinite(fromRowIndex) && Number.isFinite(fromElementIndex)) {
        const sectionRows = getSectionRows(block)
        const targetRowIndex =
          Number(rowElementDragDropTarget.value?.sectionIndex) === Number(index)
            ? Number(rowElementDragDropTarget.value?.rowIndex)
            : sectionRows.length
        const fromNestedRowElementIndexRaw = Number(rowElementReorderPayload?.nestedRowElementIndex)
        emit('reorder-row-element', {
          fromSectionIndex,
          fromRowIndex,
          fromElementIndex,
          fromNestedRowElementIndex: Number.isFinite(fromNestedRowElementIndexRaw) ? fromNestedRowElementIndexRaw : -1,
          toSectionIndex: index,
          toRowIndex: targetRowIndex,
          toElementIndex: 0,
          toColumnIndex: 0,
          toNestedRowElementIndex: -1,
        })
      }
      rowElementDragActive.value = false
      rowElementDragDropTarget.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1, columnIndex: 0 }
      return
    }
  }
  onCanvasDrop(event, index)
}

function onCanvasDrop(event, forcedIndex = null) {
  const dt = event?.dataTransfer
  if (!dt) return
  const rowInsertPayload = readPayloadFromTransfer(dt, PAGE_ROW_INSERT_MIME, PAGE_ROW_INSERT_TEXT_PREFIX)
  const rowReorderPayload = readPayloadFromTransfer(dt, PAGE_ROW_REORDER_MIME, PAGE_ROW_REORDER_TEXT_PREFIX)
  const rowElementInsertPayload = readPayloadFromTransfer(dt, PAGE_ROW_ELEMENT_INSERT_MIME, PAGE_ROW_ELEMENT_INSERT_TEXT_PREFIX)
  const rowElementReorderPayload = readPayloadFromTransfer(dt, PAGE_ROW_ELEMENT_REORDER_MIME, PAGE_ROW_ELEMENT_REORDER_TEXT_PREFIX)
  if (rowInsertPayload || rowReorderPayload || rowElementInsertPayload || rowElementReorderPayload) {
    dragActive.value = false
    dragDropIndex.value = null
    rowDragActive.value = false
    rowDragDropTarget.value = { sectionIndex: -1, rowIndex: -1 }
    rowElementDragActive.value = false
    rowElementDragDropTarget.value = { sectionIndex: -1, rowIndex: -1, elementIndex: -1, columnIndex: 0 }
    return
  }
  const insertType = String(dt.getData(PAGE_BLOCK_INSERT_MIME) || '').trim()
  const reorderFromRaw = String(dt.getData(PAGE_BLOCK_REORDER_MIME) || '').trim()
  const reorderFrom = Number.parseInt(reorderFromRaw, 10)
  const len = Array.isArray(props.doc?.sections) ? props.doc.sections.length : 0
  const resolvedIndex = Math.max(0, Math.min(
    Number(forcedIndex ?? dragDropIndex.value ?? len) || 0,
    len
  ))
  if (insertType) {
    emit('insert-from-drop', { type: insertType, index: resolvedIndex })
  } else if (Number.isFinite(reorderFrom)) {
    emit('reorder', { from: reorderFrom, to: resolvedIndex })
  }
  dragActive.value = false
  dragDropIndex.value = null
}

function shouldShowDropIndicator(index) {
  return dragActive.value && Number(dragDropIndex.value) === Number(index)
}

function getBlockSettingsButtonLabel(block) {
  const type = String(block?.type || '').trim().toLowerCase()
  if (type === 'section') return 'Section settings'
  if (type === 'text') return 'Text settings'
  if (type === 'header') return 'Header settings'
  if (type === 'image') return 'Image settings'
  if (type === 'button') return 'Button settings'
  if (type === 'video') return 'Video settings'
  if (type === 'mp3') return 'MP3 settings'
  if (type === 'embed') return 'Embed settings'
  return 'Block settings'
}

function getBlockBadgeLabel(block) {
  return getBlockSettingsButtonLabel(block).replace(/\s+settings$/i, '')
}

function getBlockTextSettings(block) {
  const settings = block?.props?.textSettings || null
  return settings && typeof settings === 'object' ? settings : {}
}

function getTextBlockStyle(block) {
  const settings = getBlockTextSettings(block)
  const size = Number(settings?.fontSize)
  const weight = Number(settings?.fontWeight)
  const lineHeightRaw = Number(settings?.lineHeight)
  const lineHeight = Number.isFinite(lineHeightRaw) ? Math.max(0.8, Math.min(3, lineHeightRaw)) : 1.6
  const explicitAlignRaw = String(block?.props?.textAlign || block?.props?.text_align || '').trim()
  const explicitAlign = explicitAlignRaw ? normalizeTextAlign(explicitAlignRaw, 'left') : ''
  const elementAlign = normalizeTextAlign(block?.props?.elementAlign, 'left')
  const normalizedSettingAlign = normalizeTextAlign(settings?.textAlign, elementAlign)
  const align = explicitAlign || (
    (!explicitAlignRaw && elementAlign === 'center' && normalizedSettingAlign === 'left')
      ? 'center'
      : normalizedSettingAlign
  )
  return {
    ...(Number.isFinite(size) ? { fontSize: `${Math.max(10, size)}px` } : {}),
    ...(Number.isFinite(weight) ? { fontWeight: String(Math.max(100, Math.min(900, Math.round(weight / 100) * 100))) } : {}),
    lineHeight: String(Number(lineHeight.toFixed(2))),
    color: normalizeHexColor(settings?.textColor || block?.props?.textColor, '#18181B'),
    textAlign: align,
    ...(Number.isFinite(Number(block?.props?.letterSpacing)) ? { letterSpacing: `${Number(block.props.letterSpacing)}px` } : {}),
  }
}

function getHeaderBlockTextStyle(block) {
  const settings = getBlockTextSettings(block)
  const weight = Number(settings?.fontWeight)
  const explicitAlignRaw = String(block?.props?.textAlign || block?.props?.text_align || '').trim()
  const explicitAlign = explicitAlignRaw ? normalizeTextAlign(explicitAlignRaw, 'left') : ''
  const elementAlign = normalizeTextAlign(block?.props?.elementAlign, 'left')
  const normalizedSettingAlign = normalizeTextAlign(settings?.textAlign, elementAlign)
  const align = explicitAlign || (
    (!explicitAlignRaw && elementAlign === 'center' && normalizedSettingAlign === 'left')
      ? 'center'
      : normalizedSettingAlign
  )
  const lineHeightRaw = Number(settings?.lineHeight)
  return {
    ...(Number.isFinite(weight) ? { fontWeight: String(Math.max(100, Math.min(900, Math.round(weight / 100) * 100))) } : {}),
    color: normalizeHexColor(settings?.textColor || block?.props?.textColor, '#18181B'),
    textAlign: align,
    lineHeight: String(Number.isFinite(lineHeightRaw) ? Math.max(0.8, Math.min(3, lineHeightRaw)) : 1.15),
  }
}

function resolveImageBlockUrl(block) {
  const fileId = sanitizeFileId(
    block?.props?.imageFileId ||
    block?.props?.image_file_id ||
    block?.props?.fileId ||
    block?.props?.file_id
  )
  if (fileId) return buildImageUrlFromFileId(fileId)
  const url = resolveLegacyTemplateAssetUrl(resolveStoredImageUrl(block?.props?.imageUrl || block?.props?.image_url))
  if (url) return url
  return pageImagePlaceholder
}

function resolveLegacyTemplateAssetUrl(value) {
  const raw = String(value || '').trim()
  if (!raw) return ''
  if (raw.startsWith('@/assets/media/images/platforma-logo-white.svg')) return loginLeftLogoDefault
  if (raw.startsWith('@/assets/media/images/platforma-logo-in-color.svg')) return loginRightLogoDefault
  if (raw.startsWith('@/assets/media/images/bg_hold_reg.gif')) return loginBgDefault
   if (raw.startsWith('@/assets/media/images/platforma-creator-icons-onboarding-page.png')) return onboardingLogoDefault
   if (raw.startsWith('@/assets/media/images/business-group.jpg')) return homeBusinessGroupDefault
   if (raw.startsWith('@/assets/media/images/platforma-businessman-rv.jpeg')) return homeBusinessmanDefault
  if (raw.startsWith('@/assets/media/images/online-learning-courses-video-thumbnail.jpg')) return pageTemplateCourseDefault
  if (raw.startsWith('@/assets/media/images/webinar.png')) return pageTemplateWebinarDefault
  if (raw.startsWith('@/assets/media/images/skin-care-2-default.png')) return pageTemplateSkincareDefault
  if (raw.startsWith('@/assets/media/images/business-group-hero.jpeg')) return pageTemplateBusinessHeroDefault
  if (raw.startsWith('@/assets/media/images/business-group-hero2.png')) return pageTemplateBusinessHeroAltDefault
  if (raw.startsWith('@/assets/media/images/businessman2.jpg')) return pageTemplateBusinessPortraitDefault
  if (raw.startsWith('@/assets/media/images/online-course.png')) return pageTemplateCourseDashboardDefault
  if (raw.startsWith('@/assets/media/images/skin-care-1-default.png')) return pageTemplateSkincareAltDefault
  if (raw.startsWith('@/assets/media/images/cart-templates/founder-studio.png')) return cartTemplateFounderStudio
  if (raw.startsWith('@/assets/media/images/cart-templates/fitness-focus.png')) return cartTemplateFitnessFocus
  if (raw.startsWith('@/assets/media/images/cart-templates/botanical-luxe.png')) return cartTemplateBotanicalLuxe
  return raw
}

function sanitizeFileId(value) {
  const normalized = String(value ?? '').trim()
  if (!normalized || normalized === 'null' || normalized === 'undefined') return ''
  return normalized
}

function resolveStoredImageUrl(value) {
  const normalized = String(value || '').trim()
  if (!normalized || normalized === 'null' || normalized === 'undefined') return ''
  return normalized
}

function toCssBackgroundImage(value) {
  const raw = String(value || '').trim()
  if (!raw) return 'none'
  if (raw.toLowerCase() === 'none') return 'none'
  if (/^(?:linear-gradient|radial-gradient|conic-gradient|repeating-linear-gradient|repeating-radial-gradient|repeating-conic-gradient|url\(|var\(|image-set\(|cross-fade\()/i.test(raw)) {
    return raw
  }
  return `url(${raw})`
}

function buildImageUrlFromFileId(fileId) {
  const normalized = sanitizeFileId(fileId)
  if (!normalized) return ''
  if (apiProxyUrl) return `${apiProxyUrl}/api/image/${encodeURIComponent(normalized)}`
  return `/api/image/${encodeURIComponent(normalized)}`
}

function getImageBlockRenderStyle(block) {
  const width = Number(block?.props?.imageWidth || 100)
  const clamped = Number.isFinite(width) ? Math.min(100, Math.max(20, Math.round(width))) : 100
  const radiusRaw = Number(block?.props?.imageBorderRadius ?? 0)
  const radius = Number.isFinite(radiusRaw) ? Math.max(0, Math.min(120, Math.round(radiusRaw))) : 0
  const shadowRaw = Number(block?.props?.imageShadowStrength ?? 0)
  const shadow = Number.isFinite(shadowRaw) ? Math.max(0, Math.min(100, Math.round(shadowRaw))) : 0
  const shadowYOffset = Math.max(0, Math.round(shadow * 0.22))
  const shadowBlur = Math.max(0, Math.round(shadow * 0.8))
  const shadowAlpha = Math.max(0, Math.min(0.35, (shadow / 100) * 0.35))
  return {
    width: `${clamped}%`,
    ...(block?.props?.imageAspectRatio ? { aspectRatio: String(block.props.imageAspectRatio) } : {}),
    objectFit: ['contain', 'fill'].includes(String(block?.props?.imageObjectFit || '').toLowerCase())
      ? String(block.props.imageObjectFit).toLowerCase()
      : 'cover',
    borderRadius: `${radius}px`,
    boxShadow: shadow > 0 ? `0 ${shadowYOffset}px ${shadowBlur}px rgba(0, 0, 0, ${Number(shadowAlpha.toFixed(2))})` : 'none',
  }
}

function getImageBlockLinkStyle(block) {
  const { width } = getImageBlockRenderStyle(block)
  return { width }
}

function getImageBlockLinkedImageStyle(block) {
  const style = getImageBlockRenderStyle(block)
  return {
    ...style,
    width: '100%',
  }
}

function getImageBlockHref(block) {
  return String(block?.props?.imageLinkAddress || block?.props?.image_link_address || '').trim()
}

function getImageBlockTarget(block) {
  if (!getImageBlockHref(block)) return undefined
  return (block?.props?.imageOpenInNewTab ?? block?.props?.image_open_in_new_tab) === true ? '_blank' : '_self'
}

function getImageBlockRel(block) {
  return getImageBlockTarget(block) === '_blank' ? 'noopener noreferrer' : undefined
}

function getImageBlockAlignClass(block) {
  const align = normalizeTextAlign(block?.props?.imageAlign, 'center')
  if (align === 'left') return 'flex items-center justify-start'
  if (align === 'right') return 'flex items-center justify-end'
  return 'flex items-center justify-center'
}

function getButtonBlockAlignClass(block) {
  const align = normalizeTextAlign(block?.props?.buttonAlign, 'center')
  if (align === 'left') return 'flex justify-start'
  if (align === 'right') return 'flex justify-end'
  return 'flex justify-center'
}

function getButtonBlockRenderStyle(block) {
  const buttonProps = block?.props || {}
  const textScale = normalizeButtonTextSize(buttonProps?.buttonTextSize ?? buttonProps?.button_text_size) / 100
  const baseFontSize = normalizeButtonFontSize(buttonProps?.buttonFontSize ?? buttonProps?.button_font_size)
  const computedFontSize = Math.max(10, Math.round(baseFontSize * textScale))
  const buttonRadiusRaw = Number(buttonProps?.buttonBorderRadius ?? buttonProps?.button_border_radius ?? 12)
  const buttonRadius = Number.isFinite(buttonRadiusRaw) ? Math.max(0, Math.min(120, Math.round(buttonRadiusRaw))) : 12
  const normalizedWidth = normalizeButtonWidth(buttonProps?.buttonWidth ?? buttonProps?.button_width)
  const storedElementBackground = normalizeBackgroundColor(buttonProps?.elementBackgroundColor, 'transparent')
  const buttonBackground = normalizeBackgroundColor(
    buttonProps?.buttonBackgroundColor || buttonProps?.button_background_color,
    DEFAULT_BUTTON_BACKGROUND_COLOR
  )
  const legacyTemplateButtonChrome = storedElementBackground === buttonBackground
  return {
    width: `${legacyTemplateButtonChrome ? Math.min(normalizedWidth, 32) : normalizedWidth}%`,
    backgroundColor: normalizeHexColor(
      buttonProps?.buttonBackgroundColor || buttonProps?.button_background_color,
      DEFAULT_BUTTON_BACKGROUND_COLOR
    ),
    color: normalizeHexColor(
      buttonProps?.buttonTextColor || buttonProps?.button_text_color,
      DEFAULT_BUTTON_TEXT_COLOR
    ),
    fontSize: `${computedFontSize}px`,
    padding: `${normalizeButtonPaddingY(buttonProps?.buttonPaddingY ?? buttonProps?.button_padding_y)}px ${normalizeButtonPaddingX(buttonProps?.buttonPaddingX ?? buttonProps?.button_padding_x)}px`,
    lineHeight: '1.3',
    textAlign: 'center',
    borderRadius: `${buttonRadius}px`,
  }
}

function getBlockToolbarClasses(block) {
  if (String(block?.type || '') === 'section') return 'top-0 right-0 z-50'
  return 'bottom-full right-3'
}

function isSectionChromeSuppressed(sectionIndex) {
  const target = Number(sectionIndex)
  if (hasAnyHoveredPageNode()) {
    return (
      Number(hoveredRowContext.value?.sectionIndex) === target ||
      Number(hoveredColumnContext.value?.sectionIndex) === target ||
      Number(hoveredElementContext.value?.sectionIndex) === target
    )
  }
  return (
    Number(props.selectedRowContext?.sectionIndex) === target ||
    Number(props.selectedColumnContext?.sectionIndex) === target ||
    Number(props.selectedElementContext?.sectionIndex) === target
  )
}

function hasAnyHoveredPageNode() {
  return (
    Number(hoveredSectionIndex.value) >= 0 ||
    Number(hoveredRowContext.value?.sectionIndex) >= 0 ||
    Number(hoveredColumnContext.value?.sectionIndex) >= 0 ||
    Number(hoveredElementContext.value?.sectionIndex) >= 0
  )
}

function getBlockChromeVisibilityClasses(index, block = null) {
  if (String(block?.type || '') === 'section') {
    if (isSectionChromeSuppressed(index)) return 'opacity-0 pointer-events-none'
    const active = hasAnyHoveredPageNode()
      ? Number(hoveredSectionIndex.value) === Number(index)
      : Number(props.selectedIndex) === Number(index)
    if (active) {
      return 'opacity-100 pointer-events-auto'
    }
    return 'opacity-0 pointer-events-none'
  }
  if (props.selectedIndex === index) return 'opacity-100 pointer-events-auto'
  return 'opacity-0 pointer-events-none group-hover/item:opacity-100 group-hover/item:pointer-events-auto'
}

function getBlockActionButtonClasses(block) {
  if (String(block?.type || '') === 'section') return 'bg-[#14b8a6] hover:bg-[#0f766e]'
  return 'bg-[#ff009dce] hover:bg-[#ff009db0]'
}

function getBlockAddButtonClasses(block) {
  if (String(block?.type || '') === 'section') return 'bg-[#14b8a6] hover:bg-[#0f766e] border-[#0f766e]'
  return 'bg-[#ff009dce] hover:bg-[#ff009db0] border-white'
}

function normalizeVideoType(value, fallback = 'upload') {
  const normalized = String(value || '').trim().toLowerCase()
  if (normalized === 'custom_embed') return 'custom_embed'
  if (normalized === 'youtube') return 'youtube'
  if (normalized === 'vimeo') return 'vimeo'
  if (normalized === 'wistia') return 'wistia'
  if (normalized === 'upload') return 'upload'
  if (normalized === 'platforma_media') return 'platforma_media'
  return fallback
}

function extractYouTubeId(value) {
  const raw = String(value || '').trim()
  if (!raw) return ''
  const direct = raw.match(/^[a-zA-Z0-9_-]{11}$/)
  if (direct) return direct[0]
  const patterns = [
    /(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/i,
    /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/i,
    /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/i,
    /(?:youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/i,
  ]
  for (const pattern of patterns) {
    const match = raw.match(pattern)
    if (match?.[1]) return match[1]
  }
  return ''
}

function extractVimeoId(value) {
  const raw = String(value || '').trim()
  if (!raw) return ''
  const direct = raw.match(/^\d+$/)
  if (direct) return direct[0]
  const match = raw.match(/vimeo\.com\/(?:video\/)?(\d+)/i)
  return match?.[1] || ''
}

function extractWistiaId(value) {
  const raw = String(value || '').trim()
  if (!raw) return ''
  const medias = raw.match(/wistia\.(?:com|net)\/(?:medias|embed\/medias)\/([a-zA-Z0-9]+)/i)
  if (medias?.[1]) return medias[1]
  const query = raw.match(/[?&]wvideo=([a-zA-Z0-9]+)/i)
  if (query?.[1]) return query[1]
  const direct = raw.match(/^[a-zA-Z0-9]+$/)
  return direct?.[0] || ''
}

function resolveVideoEmbedUrl(block) {
  const type = normalizeVideoType(block?.props?.videoType || block?.props?.video_type || '', '')
  const raw = String(block?.props?.videoExternalUrl || block?.props?.video_external_url || '').trim()
  if (!raw) return ''
  if (type === 'youtube') {
    const id = extractYouTubeId(raw)
    return id ? `https://www.youtube.com/embed/${id}` : ''
  }
  if (type === 'vimeo') {
    const id = extractVimeoId(raw)
    return id ? `https://player.vimeo.com/video/${id}` : ''
  }
  if (type === 'wistia') {
    const id = extractWistiaId(raw)
    return id ? `https://fast.wistia.net/embed/iframe/${id}` : ''
  }
  return ''
}

function resolveVideoEmbedHtml(block) {
  const type = normalizeVideoType(block?.props?.videoType || block?.props?.video_type || '', 'upload')
  if (type !== 'custom_embed') return ''
  return sanitizeVideoEmbedHtml(block?.props?.videoEmbedCode || block?.props?.video_embed_code || '')
}

function resolveVideoFileId(block) {
  const direct = sanitizeFileId(block?.props?.videoFileId || block?.props?.video_file_id || '')
  if (direct) return direct
  return extractVideoFileIdFromSourceUrl(
    block?.props?.videoPlaybackUrl ||
    block?.props?.video_playback_url ||
    block?.props?.videoUrl ||
    block?.props?.video_url ||
    block?.props?.videoPosterUrl ||
    block?.props?.video_poster_url ||
    ''
  )
}

function normalizeVideoSourceUrl(value) {
  const raw = String(value || '').trim()
  if (!raw || /^undefined(?:\/|$)/i.test(raw) || /^null(?:\/|$)/i.test(raw)) return ''
  const normalizeCreatorProxyPath = (input) => String(input || '')
    .replace(/\/api\/media\/stream-hls\/playlist\//i, '/api/stream-hls/playlist/')
    .replace(/\/api\/media\/video-thumbnail\//i, '/api/video-thumbnail/')
  if (/^https?:\/\//i.test(raw)) {
    try {
      const parsed = new URL(raw)
      parsed.pathname = normalizeCreatorProxyPath(parsed.pathname)
      return parsed.toString()
    } catch (_error) {
      return normalizeCreatorProxyPath(raw)
    }
  }
  return normalizeCreatorProxyPath(raw)
}

function normalizeVideoPlaylistProxyUrl(value) {
  const raw = normalizeVideoSourceUrl(value)
  if (!raw) return ''
  const match = raw.match(/\/(?:api\/)?(?:media\/)?stream-hls\/playlist\/([^/?#]+)/i)
  if (!match?.[1]) return raw
  const normalizedFileId = sanitizeFileId(decodeURIComponent(match[1]))
  if (!normalizedFileId) return raw
  if (apiProxyUrl) return `${apiProxyUrl}/api/stream-hls/playlist/${encodeURIComponent(normalizedFileId)}`
  return `/api/stream-hls/playlist/${encodeURIComponent(normalizedFileId)}`
}

function isLikelyHlsSource(value) {
  const raw = String(value || '').trim().toLowerCase()
  if (!raw) return false
  return raw.includes('/stream-hls/') || raw.includes('.m3u8')
}

function resolveVideoBorderRadius(block) {
  const radiusRaw = Number(block?.props?.videoBorderRadius ?? block?.props?.video_border_radius ?? 16)
  return Number.isFinite(radiusRaw) ? Math.max(0, Math.min(120, Math.round(radiusRaw))) : 16
}

function getVideoBlockContainerStyle(block) {
  return {
    aspectRatio: '16 / 9',
    borderRadius: `${resolveVideoBorderRadius(block)}px`,
  }
}

function buildVideoPosterUrlFromFileId(fileId) {
  const normalized = sanitizeFileId(fileId)
  if (!normalized) return ''
  if (apiProxyUrl) return `${apiProxyUrl}/api/video-thumbnail/${encodeURIComponent(normalized)}`
  return `/api/video-thumbnail/${encodeURIComponent(normalized)}`
}

function extractVideoFileIdFromSourceUrl(value) {
  const raw = normalizeVideoSourceUrl(value)
  if (!raw) return ''
  const directMatch = raw.match(/\/(?:stream-hls\/playlist|media\/stream-hls\/playlist|media\/video-thumbnail|video-thumbnail)\/([^/?#]+)/i)
  if (directMatch?.[1]) return sanitizeFileId(decodeURIComponent(directMatch[1]))
  const uuidMatch = raw.match(/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i)
  return sanitizeFileId(uuidMatch?.[1] || '')
}

function resolveVideoDirectUrl(block) {
  const type = normalizeVideoType(block?.props?.videoType || block?.props?.video_type || '', 'upload')
  if (type === 'custom_embed' || type === 'youtube' || type === 'vimeo' || type === 'wistia') return ''
  const explicitPlayback = normalizeVideoSourceUrl(block?.props?.videoPlaybackUrl || block?.props?.video_playback_url || '')
  if (explicitPlayback && !isLikelyHlsSource(explicitPlayback)) return explicitPlayback
  const explicitSource = normalizeVideoSourceUrl(block?.props?.videoUrl || block?.props?.video_url || '')
  if (!explicitSource) {
    return String(block?.props?.defaultVideoAsset || '').trim() === 'quiz-intro'
      ? normalizeVideoSourceUrl(props.defaultVideoUrl)
      : ''
  }
  if (isLikelyHlsSource(explicitSource)) return ''
  return explicitSource
}

function resolveVideoPlaylistUrl(block) {
  const type = normalizeVideoType(block?.props?.videoType || block?.props?.video_type || '', 'upload')
  if (type === 'custom_embed' || type === 'youtube' || type === 'vimeo' || type === 'wistia') return ''
  const explicitPlayback = normalizeVideoPlaylistProxyUrl(
    block?.props?.videoPlaybackUrl || block?.props?.video_playback_url || ''
  )
  if (explicitPlayback && isLikelyHlsSource(explicitPlayback)) return explicitPlayback
  const fileId = resolveVideoFileId(block)
  if (fileId) {
    if (apiProxyUrl) return `${apiProxyUrl}/api/stream-hls/playlist/${encodeURIComponent(fileId)}`
    return `/api/stream-hls/playlist/${encodeURIComponent(fileId)}`
  }
  const explicitSource = normalizeVideoPlaylistProxyUrl(
    block?.props?.videoUrl || block?.props?.video_url || ''
  )
  if (explicitSource && isLikelyHlsSource(explicitSource)) return explicitSource
  return ''
}

function shouldUsePageEditorVideoPlayer(block) {
  const fileId = resolveVideoFileId(block)
  if (!fileId) return false
  const playlist = String(resolveVideoPlaylistUrl(block) || '').trim().toLowerCase()
  if (!playlist) return true
  return playlist.includes('.m3u8') || playlist.includes('/stream-hls/')
}

function resolveVideoControlColor(block) {
  return normalizeHexColor(block?.props?.videoControlColor || block?.props?.video_control_color, '#2D8CFF')
}

function resolveVideoSourceKey(block) {
  return [
    resolveVideoFileId(block),
    resolveVideoPlaylistUrl(block),
    resolveVideoPosterUrl(block),
  ].join('|')
}

function resolveVideoPosterUrl(block) {
  const videoFileId = resolveVideoFileId(block)
  const thumbnailFileIdRaw = String(
    block?.props?.videoThumbnailFileId ||
    block?.props?.video_thumbnail_file_id ||
    block?.props?.thumbnailFileId ||
    block?.props?.thumbnail_file_id ||
    ''
  ).trim()
  const thumbnailFileId = thumbnailFileIdRaw && thumbnailFileIdRaw !== videoFileId ? thumbnailFileIdRaw : ''
  if (thumbnailFileId) return buildImageUrlFromFileId(thumbnailFileId)
  const customThumbnailUrl = normalizeVideoSourceUrl(
    block?.props?.videoThumbnailUrl ||
    block?.props?.video_thumbnail_url ||
    block?.props?.thumbnailUrl ||
    block?.props?.thumbnail_url ||
    ''
  )
  if (customThumbnailUrl) return customThumbnailUrl
  const payloadPosterUrl = normalizeVideoSourceUrl(block?.props?.videoPosterUrl || block?.props?.video_poster_url || '')
  if (payloadPosterUrl) return payloadPosterUrl
  const autoPreviewUrl = normalizeVideoSourceUrl(block?.props?.videoAutoPreviewUrl || block?.props?.video_auto_preview_url || '')
  if (autoPreviewUrl) return autoPreviewUrl
  if (videoFileId) return buildVideoPosterUrlFromFileId(videoFileId)
  const fileIdFromSource = extractVideoFileIdFromSourceUrl(resolveVideoPlaylistUrl(block) || resolveVideoDirectUrl(block))
  if (fileIdFromSource) return buildVideoPosterUrlFromFileId(fileIdFromSource)
  return ''
}

function getSectionBlockStyle(block) {
  const widthPercentRaw = Number(block?.props?.sectionWidthPercent ?? 100)
  const widthPercent = Number.isFinite(widthPercentRaw) ? Math.max(10, Math.min(100, Math.round(widthPercentRaw))) : 100
  const paddingXRaw = Number(block?.props?.sectionPaddingX ?? block?.props?.sectionPadding ?? 24)
  const paddingYRaw = Number(block?.props?.sectionPaddingY ?? block?.props?.sectionPadding ?? 24)
  const marginRaw = Number(block?.props?.sectionMargin ?? 0)
  const paddingX = Number.isFinite(paddingXRaw) ? Math.max(0, Math.min(240, Math.round(paddingXRaw))) : 0
  const paddingY = Number.isFinite(paddingYRaw) ? Math.max(0, Math.min(240, Math.round(paddingYRaw))) : 0
  const margin = Number.isFinite(marginRaw) ? Math.max(0, Math.min(240, Math.round(marginRaw))) : 0
  const alignRaw = String(block?.props?.sectionAlign || 'left').toLowerCase()
  const align = ['left', 'center', 'right'].includes(alignRaw) ? alignRaw : 'left'
  const isFullWidth = widthPercent >= 100

  let marginLeft = '0px'
  let marginRight = 'auto'
  if (isFullWidth) {
    marginLeft = '0px'
    marginRight = '0px'
  } else if (align === 'center') {
    marginLeft = 'auto'
    marginRight = 'auto'
  } else if (align === 'right') {
    marginLeft = 'auto'
    marginRight = '0px'
  }

  const fileId = sanitizeFileId(block?.props?.sectionBackgroundImageFileId || block?.props?.section_background_image_file_id)
  const rawUrl = resolveLegacyTemplateAssetUrl(resolveStoredImageUrl(block?.props?.sectionBackgroundImageUrl || block?.props?.section_background_image_url))
  const backgroundImageUrl = fileId ? buildImageUrlFromFileId(fileId) : rawUrl
  const backgroundSize = String(block?.props?.sectionBackgroundSize || 'cover').trim().toLowerCase() === 'contain' ? 'contain' : 'cover'
  const backgroundPosition = String(block?.props?.sectionBackgroundPosition || 'center').trim() || 'center'
  const backgroundRepeat = String(block?.props?.sectionBackgroundRepeat || 'no-repeat').trim().toLowerCase() === 'repeat' ? 'repeat' : 'no-repeat'

  return {
    width: `${widthPercent}%`,
    minHeight: toHeightCssValue(block?.props?.sectionHeight, block?.props?.sectionHeightUnit, 100),
    paddingLeft: `${paddingX}px`,
    paddingRight: `${paddingX}px`,
    paddingTop: `${paddingY}px`,
    paddingBottom: `${paddingY}px`,
    marginTop: `${margin}px`,
    marginBottom: `${margin}px`,
    marginLeft,
    marginRight,
    boxSizing: 'border-box',
    display: 'flow-root',
    backgroundColor: normalizeBackgroundColor(block?.props?.sectionBackgroundColor, '#FFFFFF'),
    backgroundImage: toCssBackgroundImage(backgroundImageUrl),
    backgroundSize,
    backgroundPosition,
    backgroundRepeat,
  }
}
</script>


<style scoped>
/* Show a placeholder in empty contenteditable areas */
.editable[contenteditable="true"]:empty::before {
  content: attr(data-placeholder);
  color: #a1a1aa; /* zinc-400 */
}

/* Keep focus visuals to the outer ring only */
.editable {
  outline: none;          /* kill default blue outline */
}

/* Neutralize inner highlight that was causing the second ring + padding shift */
.editable-active {
  box-shadow: none;       /* remove inner ring */
  padding-inline: 0;      /* remove layout shift on select */
  border-radius: .375rem; /* keep consistent rounding if you want */
}
</style>

